/**
 * Item Selection Page - FULLY INTEGRATED
 * 
 * Step between Dispute Wizard landing and actual letter generation.
 * Allows users to select specific accounts/inquiries to dispute.
 * Connected to real dispute generation engine and AI Decision Engine.
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { usePortal } from '../../context/PortalContext';
import { useReportData } from '../../context/ReportDataContext';
import ReportSelector from '../../components/disputes/ReportSelector';
import { metro2Service } from '../../services/metro2Service';
import adminOpsService from '../../services/adminOpsService';
import LetterGenerationProgress from '../../components/disputes/LetterGenerationProgress';
import {
  countGeneratedLetters,
  generationHasLetters,
  isFinalPackageReady,
  lettersByMethodFromResult,
  logGenerationResultAudit,
  logStep5ResultCounts,
  normalizeGenerationResult,
  packageReconciliationFailureMessage,
  pollUntilLettersAvailable,
} from '../../utils/generationResultUtils';
import {
  ACTIVE_PACKAGE_STORAGE_KEY,
  FINAL_PACKAGE_STORAGE_KEY,
} from '../../utils/finalPackageRecovery';
import { buildIdentityAnchorDataForCompletion } from '../../utils/consumerIdentityUtils';
import {
  buildCatalogDisplayedItemsKey,
  buildDeterministicFallbackItemId,
  buildDisputeWizardNormalizedState,
  buildExcludedDisplayedItems,
  buildExcludedMatcher,
  buildSelectAllEligibleSelection,
  formatExcludedDisplayLabel,
  isEligibleForDefaultSelection,
  normalizeExcludedItems,
  pruneSelectedItemsForExclusions,
  splitDisplayedCatalogItems,
} from '../../utils/itemSelectionUtils';
import { buildEffectiveDisputeSelection } from '../../utils/dataBreachSelectionUtils';
import {
  detectCollectionSignals,
  getCanonicalPackageLabel,
  getStableDisputeItemId,
  normalizeDisputeMethod,
  normalizeMethodKey,
} from '../../utils/normalizeDisputeMethod';
import {
  buildResolveMethodsRequestInput,
  createResolveMethodsRequestCoordinator,
} from '../../utils/buildResolveMethodsRequest';
import {
  assertNoCollectionsInStructural,
  groupPackagesByCanonicalMethod,
  mergeAssignmentsByStableId,
  toMethodResolutionGroups,
} from '../../utils/disputePackageGrouping';
import { buildDurableExpectedTotals } from '../../utils/buildDurableExpectedTotals';
import {
  buildFinalLetterPackagesFromPreview,
  getFinalLetterCount,
} from '../../utils/finalLetterPackages';
import {
  extractApiValidationMessage,
  logSanitizedValidationError,
} from '../../utils/extractApiValidationMessage';
import {
  filterNegativeTradelines,
  getNegativeDisplayReason,
} from '../../utils/negativeAccountClassifier';
import { trackWizardStep } from '../../utils/wizardProgress';
import {
  buildOrderedBureauComparisonEntries,
  bureauComparisonTableVisible,
  formatBureauComparisonFieldLabel,
  isDerogatoryEvidenceValue,
  resolveEvidencePaymentStatus,
} from '../../utils/packageEvidenceViewUtils';
import PIAnchorSelectionModal, { categorizeItem, ANCHOR_CATEGORIES, CATEGORY_INFO, MANUAL_ENTRY_ID, formatManualValue } from '../../components/disputes/PIAnchorSelectionModal';
import {
  ChevronRight,
  ChevronLeft,
  ChevronUp,
  ChevronDown,
  CheckCircle,
  FileText,
  Database,
  Building2,
  AlertCircle,
  AlertTriangle,
  Search,
  Check,
  Loader2,
  Scale,
  CreditCard,
  X,
  XCircle,
  Filter,
  Download,
  Eye,
  Sparkles,
  Shield,
  User,
  Package,
  Ban,
  ArrowRight,
  MapPin,
  Calendar,
  PenLine,
  Info,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { calculateUniqueLateMonths, hasChargeOffInPaymentHistory } from '../../utils/disputeEligibility';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');

// ============================================
// BUREAU NORMALIZATION UTILITY
// ============================================
// Canonical bureau names - must match backend
const CANONICAL_BUREAUS = {
  'equifax': 'Equifax',
  'experian': 'Experian',
  'transunion': 'TransUnion',
  'trans union': 'TransUnion',
};

/**
 * Normalize a bureau name to canonical format.
 * Prevents duplicates from casing differences (e.g., 'equifax' vs 'Equifax')
 */
const normalizeBureau = (bureau) => {
  if (!bureau) return bureau;
  const bureauLower = bureau.toLowerCase().trim();
  return CANONICAL_BUREAUS[bureauLower] || bureau.charAt(0).toUpperCase() + bureau.slice(1).toLowerCase();
};

/**
 * Normalize a list of bureaus, removing duplicates that differ only by casing.
 */
const normalizeBureausList = (bureaus) => {
  if (!bureaus || !Array.isArray(bureaus)) return bureaus;
  
  const seen = new Set();
  const normalized = [];
  
  for (const bureau of bureaus) {
    const canonical = normalizeBureau(bureau);
    const key = canonical.toLowerCase();
    
    if (!seen.has(key)) {
      seen.add(key);
      normalized.push(canonical);
    }
  }
  
  return normalized;
};

// Bureau colors with dark mode support
const BUREAU_COLORS = {
  TransUnion: 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-700',
  Experian: 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700',
  Equifax: 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700',
};

const DEBUG_DISPUTE_SELECTION = process.env.NODE_ENV !== 'production' && false;

// ============================================
// EMPLOYER RECOMMENDATION CONSTANTS
// ============================================
const EMPLOYER_EDUCATION_COPY = 
  "Employer information is often used as part of credit-file matching and verification. " +
  "If employer data is outdated, inconsistent, unnecessary, or not confirmed as current, " +
  "it can weaken the personal-information cleanup strategy and make negative-item disputes harder to isolate. " +
  "We recommend disputing employer information with the rest of your personal information unless you intentionally want it to remain.";

const EMPLOYER_WARNING_TITLE = "Keep Employer Information?";
const EMPLOYER_WARNING_BODY = 
  "Employer information is commonly used as a file-matching and verification element. " +
  "Keeping outdated or unnecessary employer data may weaken your personal-information cleanup strategy. " +
  "You can keep it, but we recommend disputing employer information along with your other personal information.";

/**
 * Check if a PI item is an employer item
 */
const isEmployerItem = (piItem) => {
  if (!piItem) return false;
  const fieldLabel = (piItem.fieldLabel || piItem.field_label || '').toLowerCase();
  const fieldKey = (piItem.fieldKey || piItem.field_key || '').toLowerCase();
  return fieldLabel.includes('employer') || fieldKey.includes('employer');
};

/**
 * Check if an employer item has data in any bureau
 */
const hasEmployerData = (piItem) => {
  if (!piItem || !piItem.values) return false;
  const values = piItem.values;
  
  const checkValue = (val) => {
    if (!val) return false;
    if (typeof val === 'string') return val.trim().length > 0 && val !== '—';
    if (Array.isArray(val)) return val.some(v => v && String(v).trim().length > 0 && v !== '—');
    return false;
  };
  
  return checkValue(values.TransUnion) || checkValue(values.Experian) || checkValue(values.Equifax);
};

// ============================================
// STEP INDICATOR
// ============================================

const StepIndicator = ({ currentStep, steps }) => (
  <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 mb-6 md:mb-8">
    <div className="flex items-center justify-start sm:justify-center min-w-max sm:min-w-0 mx-auto">
    {steps.map((step, index) => (
      <React.Fragment key={index}>
        <div className="flex flex-col items-center shrink-0">
          <div
            className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center font-semibold text-xs sm:text-sm transition-all ${
              index < currentStep
                ? 'bg-emerald-500 text-white'
                : index === currentStep
                ? 'bg-emerald-600 text-white ring-4 ring-emerald-100 dark:ring-emerald-900/50'
                : 'bg-stone-200 dark:bg-[rgba(142,218,182,0.15)] text-stone-500 dark:text-[#93AE9F]'
            }`}
          >
            {index < currentStep ? <Check size={16} className="sm:w-[18px] sm:h-[18px]" /> : index + 1}
          </div>
          <span className={`text-[10px] sm:text-xs mt-1.5 sm:mt-2 max-w-[64px] sm:max-w-[80px] text-center leading-tight ${
            index === currentStep ? 'text-emerald-700 dark:text-emerald-400 font-medium' : 'text-stone-500 dark:text-[#93AE9F]'
          }`}>
            {step}
          </span>
        </div>
        {index < steps.length - 1 && (
          <div className={`w-6 sm:w-16 h-1 mx-1 sm:mx-2 rounded shrink-0 ${
            index < currentStep ? 'bg-emerald-500' : 'bg-stone-200 dark:bg-[rgba(142,218,182,0.15)]'
          }`} />
        )}
      </React.Fragment>
    ))}
    </div>
  </div>
);

// ============================================
// STRATEGY RECOMMENDATION BANNER
// ============================================

// Mixed Method Banner - shown when selected items require different methods
const MixedMethodBanner = ({ methodResolution }) => {
  if (!methodResolution || !methodResolution.summary?.is_mixed) return null;
  
  const { summary, method_groups } = methodResolution;
  
  const METHOD_COLORS = {
    data_breach_method: 'bg-purple-50 border-purple-200 text-purple-800 dark:bg-purple-900/25 dark:border-purple-600/40 dark:text-purple-200',
    personal_info_accuracy: 'bg-teal-50 border-teal-200 text-teal-800 dark:bg-teal-900/25 dark:border-teal-600/40 dark:text-teal-200',
    isolated_method: 'bg-emerald-50 border-emerald-200 text-emerald-800 dark:bg-emerald-900/25 dark:border-emerald-600/40 dark:text-emerald-200',
    structural_method: 'bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-900/25 dark:border-amber-600/40 dark:text-amber-200',
    recovery_method: 'bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-900/25 dark:border-blue-600/40 dark:text-blue-200',
    factual_inaccuracy: 'bg-stone-50 border-stone-200 text-stone-800 dark:bg-stone-800/40 dark:border-stone-600/40 dark:text-stone-200',
  };

  const BADGE_COLORS = {
    purple: 'bg-purple-100 text-purple-700 border-purple-300 dark:bg-purple-900/40 dark:text-purple-200 dark:border-purple-600/50',
    teal: 'bg-teal-100 text-teal-700 border-teal-300 dark:bg-teal-900/40 dark:text-teal-200 dark:border-teal-600/50',
    blue: 'bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-900/40 dark:text-blue-200 dark:border-blue-600/50',
    amber: 'bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-900/40 dark:text-amber-200 dark:border-amber-600/50',
    green: 'bg-emerald-100 text-emerald-700 border-emerald-300 dark:bg-emerald-900/40 dark:text-emerald-200 dark:border-emerald-600/50',
    gray: 'bg-stone-100 text-stone-700 border-stone-300 dark:bg-stone-800/40 dark:text-stone-200 dark:border-stone-600/50',
  };

  return (
    <div
      className="rounded-xl border-2 border-amber-300 bg-amber-50 p-4 mb-6 dark:bg-[rgba(10,38,28,0.65)] dark:border-amber-600/45"
      data-testid="mixed-method-banner"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/35 flex items-center justify-center flex-shrink-0">
          <AlertCircle size={20} className="text-amber-700 dark:text-amber-300" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-bold text-lg text-amber-900 dark:text-[#EAF7F0]">
              Mixed Selection Detected
            </h3>
            <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-900/35 dark:text-amber-200 dark:border-amber-600/50 text-xs">
              {summary.unique_method_count} STRATEGY GROUPS
            </Badge>
          </div>
          <p className="text-sm mt-1 text-amber-800 dark:text-[#C7DDD0]">
            Your selected items use multiple dispute strategies. Final letter packages will be organized by bureau, and compatible Structural and Recovery items may be combined into one package.
          </p>

          {/* Method Groups Summary */}
          <div className="mt-4 grid gap-2 sm:grid-cols-2">
            {method_groups.map((group) => (
              <div
                key={group.method_key}
                className={`flex items-center gap-2 p-2 rounded-lg border ${METHOD_COLORS[group.method_key] || 'bg-stone-50 border-stone-200 dark:bg-stone-800/40 dark:border-stone-600/40 dark:text-stone-200'}`}
              >
                <Badge
                  variant="outline"
                  className={`text-xs ${BADGE_COLORS[group.method_color] || BADGE_COLORS.gray}`}
                >
                  {group.method_badge}
                </Badge>
                <span className="text-sm font-medium dark:text-[#EAF7F0]">{group.method_name}</span>
                <span className="text-xs opacity-70 ml-auto dark:text-[#93AE9F] dark:opacity-100">{group.count} item{group.count !== 1 ? 's' : ''}</span>
              </div>
            ))}
          </div>

          <p className="text-xs mt-3 text-amber-700 dark:text-[#93AE9F] flex items-center gap-1">
            <Scale size={12} />
            Each item keeps its assigned strategy. Final package counts are calculated during review.
          </p>
        </div>
      </div>
    </div>
  );
};

const StrategyRecommendationBanner = ({ 
  classification, 
  recommendedStrategy, 
  classificationReason,
  warnings = [],
  itemCount = 0,
  selectedMethod,
  onChangeStrategy,
  methodResolution 
}) => {
  // If we have a mixed method resolution, don't show this banner - MixedMethodBanner handles it
  if (methodResolution?.summary?.is_mixed) {
    return null;
  }
  
  // Use method resolution for uniform selections if available
  if (methodResolution?.summary?.is_uniform && methodResolution.summary.primary_method) {
    const { summary } = methodResolution;
    const methodColors = {
      data_breach_method: 'bg-purple-50 border-purple-200 text-purple-800',
      isolated_method: 'bg-emerald-50 border-emerald-200 text-emerald-800',
      structural_method: 'bg-amber-50 border-amber-200 text-amber-800',
      recovery_method: 'bg-blue-50 border-blue-200 text-blue-800',
      factual_inaccuracy: 'bg-stone-50 border-stone-200 text-stone-800',
    };
    
    const colorClass = methodColors[summary.primary_method] || 'bg-stone-50 border-stone-200 text-stone-800';
    const isDataBreach = summary.primary_method === 'data_breach_method';
    
    return (
      <div 
        className={`rounded-xl border-2 p-4 mb-6 ${colorClass} dark:bg-[rgba(10,38,28,0.65)] dark:border-[rgba(142,218,182,0.15)]`}
        data-testid="strategy-recommendation-banner"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-white/60 dark:bg-[rgba(142,218,182,0.15)] flex items-center justify-center flex-shrink-0">
              {isDataBreach ? (
                <Shield size={20} className="text-current" />
              ) : (
                <Sparkles size={20} className="text-current" />
              )}
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-bold text-lg dark:text-[#EAF7F0]">
                  {isDataBreach ? 'Method' : 'Recommended Strategy'}: {summary.primary_method_name}
                </h3>
                <Badge variant="outline" className="bg-white/50 dark:bg-[rgba(142,218,182,0.12)] dark:text-[#C7DDD0] dark:border-[rgba(142,218,182,0.20)] text-xs">
                  {summary.primary_method_badge}
                </Badge>
              </div>
              <p className="text-sm mt-1 opacity-90 dark:text-[#C7DDD0]">
                All selected items qualify for the same dispute method.
              </p>
              
              {itemCount > 0 && (
                <p className="text-xs mt-2 opacity-70 dark:text-[#93AE9F] flex items-center gap-1">
                  <CheckCircle size={12} />
                  {itemCount} selected item{itemCount !== 1 ? 's were' : ' was'} carried over from Credit Report
                </p>
              )}
              
              {isDataBreach && (
                <p className="text-xs mt-2 opacity-80 dark:text-[#93AE9F] flex items-center gap-1 font-medium">
                  <Shield size={12} />
                  FCRA Notice of Formal Dispute and Noncompliance • 15-day cure demand
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // Fallback to original logic if no method resolution
  if (!recommendedStrategy && !classification) return null;
  
  const classificationColors = {
    isolated: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    structural: 'bg-orange-50 border-orange-200 text-orange-800',
    recovery: 'bg-blue-50 border-blue-200 text-blue-800',
    structural_recovery: 'bg-purple-50 border-purple-200 text-purple-800',
    combo: 'bg-purple-50 border-purple-200 text-purple-800',
    data_breach: 'bg-red-50 border-red-200 text-red-800',
  };
  
  const classificationLabels = {
    isolated: 'Isolated Strategy',
    structural: 'Structural Strategy',
    recovery: 'Recovery Strategy',
    structural_recovery: 'Combo Strategy (Structural + Recovery)',
    combo: 'Combo Strategy',
    data_breach: 'Data Breach Method',
  };
  
  const colorClass = classificationColors[classification] || 'bg-stone-50 border-stone-200 text-stone-800';
  const label = recommendedStrategy?.title || classificationLabels[classification] || 'Recommended Strategy';
  
  // Display selected method if different from classification
  const methodDisplay = selectedMethod === 'data_breach' ? 'Data Breach Method' : null;
  
  return (
    <div 
      className={`rounded-xl border-2 p-4 mb-6 ${colorClass} dark:bg-[rgba(10,38,28,0.65)] dark:border-[rgba(142,218,182,0.15)]`}
      data-testid="strategy-recommendation-banner"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-white/60 dark:bg-[rgba(142,218,182,0.15)] flex items-center justify-center flex-shrink-0">
            {classification === 'data_breach' ? (
              <Shield size={20} className="text-current" />
            ) : (
              <Sparkles size={20} className="text-current" />
            )}
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-bold text-lg dark:text-[#EAF7F0]">
                {classification === 'data_breach' ? 'Method' : 'Recommended Strategy'}: {label}
              </h3>
              <Badge variant="outline" className="bg-white/50 dark:bg-[rgba(142,218,182,0.12)] dark:text-[#C7DDD0] dark:border-[rgba(142,218,182,0.20)] text-xs">
                {classification?.toUpperCase().replace('_', ' ') || 'AUTO'}
              </Badge>
              {methodDisplay && classification !== 'data_breach' && (
                <Badge variant="outline" className="bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700 text-xs">
                  Method: {methodDisplay}
                </Badge>
              )}
            </div>
            <p className="text-sm mt-1 opacity-90 dark:text-[#C7DDD0]">
              {recommendedStrategy?.reason || classificationReason || 
               'This recommendation is based on the selected accounts and current file classification.'}
            </p>
            
            {/* Handoff message - subtle indicator that items came from Credit Report */}
            {itemCount > 0 && (
              <p className="text-xs mt-2 opacity-70 dark:text-[#93AE9F] flex items-center gap-1">
                <CheckCircle size={12} />
                {itemCount} selected item{itemCount !== 1 ? 's were' : ' was'} carried over from Credit Report
              </p>
            )}
            
            {/* Data Breach Method specific notice */}
            {(classification === 'data_breach' || selectedMethod === 'data_breach') && (
              <p className="text-xs mt-2 opacity-80 dark:text-[#93AE9F] flex items-center gap-1 font-medium">
                <Shield size={12} />
                FCRA Notice of Formal Dispute and Noncompliance • 15-day cure demand
              </p>
            )}
            
            {/* Warnings */}
            {warnings.length > 0 && (
              <div className="mt-3 space-y-1">
                {warnings.slice(0, 2).map((warning, idx) => (
                  <div 
                    key={idx}
                    className="flex items-center gap-2 text-xs bg-white/40 dark:bg-[rgba(142,218,182,0.12)] rounded px-2 py-1"
                  >
                    <AlertCircle size={12} />
                    <span className="dark:text-[#C7DDD0]">{warning.message}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        
        {/* Optional: Manual override button */}
        {onChangeStrategy && (
          <Button
            variant="outline"
            size="sm"
            onClick={onChangeStrategy}
            className="bg-white/50 dark:bg-[rgba(142,218,182,0.12)] dark:border-[rgba(142,218,182,0.20)] dark:text-[#C7DDD0] text-xs"
          >
            Change Strategy
          </Button>
        )}
      </div>
    </div>
  );
};

// ============================================
// EXCLUDED ITEMS NOTICE
// ============================================

const ExcludedItemsSection = ({ excludedItems = [] }) => {
  if (!excludedItems.length) return null;

  return (
    <div className="bg-red-50 dark:bg-[rgba(239,68,68,0.12)] border border-red-200 dark:border-[rgba(239,68,68,0.20)] rounded-xl overflow-hidden" data-testid="excluded-items-notice">
      <div className="bg-red-100 dark:bg-[rgba(239,68,68,0.18)] px-4 py-3 border-b border-red-200 dark:border-[rgba(239,68,68,0.20)]">
        <div className="flex items-center gap-2">
          <Ban size={18} className="text-red-600 dark:text-red-400" />
          <h3 className="font-semibold text-red-900 dark:text-red-200">
            Excluded Items ({excludedItems.length})
          </h3>
        </div>
        <p className="text-sm text-red-700 dark:text-red-300 mt-1">
          These items have been removed from your dispute and will NOT be included in any letters.
        </p>
      </div>
      <div className="divide-y divide-red-100 dark:divide-[rgba(239,68,68,0.18)]">
        {excludedItems.map((item) => (
          <div key={item.item_id || item.fingerprint || item.display_label} className="p-4" data-testid={`excluded-item-${item.item_id || 'unknown'}`}>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-red-100 dark:bg-[rgba(239,68,68,0.18)] flex items-center justify-center flex-shrink-0">
                <XCircle size={16} className="text-red-600 dark:text-red-400" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-red-900 dark:text-red-200">
                    {item.display_label || formatExcludedDisplayLabel(item)}
                  </span>
                  <Badge variant="outline" className="bg-red-100 text-red-700 border-red-300 dark:bg-[rgba(239,68,68,0.18)] dark:text-red-200 dark:border-[rgba(239,68,68,0.28)] text-xs">
                    EXCLUDED
                  </Badge>
                </div>
                {item.reason && (
                  <p className="text-sm text-red-700 dark:text-red-300 mt-1">{item.reason}</p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================
// ACCOUNT ITEM CARD
// ============================================

const AccountItemCard = ({ item, type, isSelected, onToggle, methodAssignment }) => {
  const isAccount = type === 'account';
  const negativeReason = isAccount ? getNegativeDisplayReason(item) : null;
  
  // Method badge colors with dark mode support
  const METHOD_BADGE_COLORS = {
    data_breach_method: 'bg-purple-100 text-purple-700 border-purple-300 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-700',
    personal_info_accuracy: 'bg-teal-100 text-teal-700 border-teal-300 dark:bg-teal-900/30 dark:text-teal-300 dark:border-teal-700',
    isolated_method: 'bg-emerald-100 text-emerald-700 border-emerald-300 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-700',
    structural_method: 'bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700',
    recovery_method: 'bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700',
    factual_inaccuracy: 'bg-stone-100 text-stone-600 border-stone-300 dark:bg-stone-800/50 dark:text-stone-300 dark:border-stone-600',
  };
  
  return (
    <div 
      className={`border rounded-xl p-4 cursor-pointer transition-all ${
        isSelected 
          ? 'border-emerald-500 bg-emerald-50 ring-2 ring-emerald-200 dark:bg-emerald-900/30 dark:border-emerald-500 dark:ring-emerald-700' 
          : 'border-stone-200 hover:border-stone-300 bg-white dark:bg-[rgba(10,38,28,0.65)] dark:border-[rgba(142,218,182,0.15)] dark:hover:border-[rgba(142,218,182,0.25)]'
      }`}
      onClick={() => onToggle(item.id)}
      data-testid={`item-${item.id}`}
    >
      <div className="flex items-start gap-3">
        <Checkbox 
          checked={isSelected}
          onCheckedChange={() => onToggle(item.id)}
          className="mt-1"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-1">
            <h4 className="font-semibold text-stone-900 dark:text-[#EAF7F0] truncate">
              {isAccount ? item.creditor_name || item.account_name : item.company_name || item.inquiry_name}
            </h4>
            <div className="flex items-center gap-1 flex-shrink-0">
              {item.bureau && (
                <Badge variant="outline" className={`${BUREAU_COLORS[item.bureau]}`}>
                  {item.bureau}
                </Badge>
              )}
              {/* Show assigned method badge when selected */}
              {isSelected && methodAssignment && (
                <Badge 
                  variant="outline" 
                  className={`text-xs ${METHOD_BADGE_COLORS[methodAssignment.assigned_method] || 'bg-stone-100 text-stone-600 border-stone-300 dark:bg-stone-800/50 dark:text-stone-300 dark:border-stone-600'}`}
                >
                  {methodAssignment.method_badge}
                </Badge>
              )}
            </div>
          </div>
          
          {isAccount ? (
            <>
              <p className="text-sm text-stone-600 dark:text-[#C7DDD0]">
                {item.account_type || 'Account'} • Balance: ${(item.balance || 0).toLocaleString()}
              </p>
              {negativeReason && (
                <Badge variant="outline" className="mt-2 bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700">
                  {negativeReason}
                </Badge>
              )}
              {item.metro2_conflicts?.length > 0 && (
                <Badge className="mt-2 ml-1 bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-700">
                  Metro 2 Issues
                </Badge>
              )}
            </>
          ) : (
            <p className="text-sm text-stone-600 dark:text-[#C7DDD0]">
              {item.inquiry_date || item.date} • {item.inquiry_type || 'Hard Inquiry'}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================
// DELIVERY FORMAT SELECTOR
// ============================================

const DeliveryFormatSelector = ({ selectedFormat, onFormatChange, recommendedFormat }) => {
  const formats = [
    {
      id: 'combined',
      name: 'Combined Letter Package',
      description: 'One letter per bureau with all selected items. Faster and simpler.',
    },
    {
      id: 'separate',
      name: 'Separate Letters',
      description: 'Individual letters for each item. Better for complex cases.',
    },
  ];

  return (
    <div className="border border-amber-200 dark:border-amber-700/50 rounded-xl p-5 bg-amber-50/50 dark:bg-amber-900/20">
      <h4 className="font-bold text-stone-900 dark:text-[#EAF7F0] mb-1">Delivery Format</h4>
      <p className="text-sm text-stone-600 dark:text-[#C7DDD0] mb-4">
        Choose how your dispute letters will be organized.
      </p>

      <div className="grid md:grid-cols-2 gap-4">
        {formats.map((format) => (
          <button
            key={format.id}
            onClick={() => onFormatChange(format.id)}
            className={`text-left p-4 rounded-xl border-2 transition-all ${
              selectedFormat === format.id
                ? 'border-amber-400 bg-white dark:bg-[rgba(10,38,28,0.75)] dark:border-amber-500/50 shadow-sm'
                : 'border-stone-200 dark:border-[rgba(142,218,182,0.15)] bg-white dark:bg-[rgba(10,38,28,0.50)] hover:border-stone-300 dark:hover:border-[rgba(142,218,182,0.25)]'
            }`}
          >
            {recommendedFormat === format.id && (
              <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300 border-amber-200 dark:border-amber-700/50 mb-2">
                Recommended
              </Badge>
            )}
            <h5 className="font-semibold text-stone-900 dark:text-[#EAF7F0] mb-1">{format.name}</h5>
            <p className="text-sm text-stone-600 dark:text-[#C7DDD0]">{format.description}</p>
          </button>
        ))}
      </div>
    </div>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================

const ItemSelectionPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, token, getAuthHeaders } = useAuth();
  const { isStaff, portalData, isSelfServe } = usePortal();
  
  // Role-based visibility: Only staff/admin can see technical prompt details
  // Self-repair users see a simplified "Dispute Plan Summary"
  const canViewPromptDebug = useMemo(() => {
    // Check if user is staff/admin
    if (isStaff) return true;
    // Check user role directly as fallback
    const adminRoles = ['platform_admin', 'owner', 'specialist', 'staff', 'super_admin', 'platform_owner'];
    if (user?.role && adminRoles.includes(user.role)) return true;
    // Check portal capabilities
    if (portalData?.capabilities?.access_staff_queues) return true;
    return false;
  }, [isStaff, user?.role, portalData?.capabilities?.access_staff_queues]);
  const { 
    reportData, 
    selectedReportId, 
    isReportLocked,
    getLockedReportInfo,
  } = useReportData();

  // Get type from URL path
  const pathParts = window.location.pathname.split('/');
  const disputeType = pathParts.includes('metro2') ? 'metro2' : 'factual';
  const mode = searchParams.get('mode') || 'rounds';
  
  // Check for pre-selected item from navigation state (from Credit Report Dispute button)
  const preselectedItem = location.state?.preselectedItem;
  
  // SMART ROUTING: Check for preloaded data from Credit Report selection
  const smartRoutingData = location.state?.fromSmartRouting ? location.state : null;
  const preloadedItems = smartRoutingData?.preloadedItems;
  const recommendedStrategy = smartRoutingData?.recommendedStrategy;
  const classificationResult = smartRoutingData?.classification;
  const classificationReason = smartRoutingData?.classificationReason;
  const preloadedAiDecision = smartRoutingData?.aiDecision;
  const selectedMethod = smartRoutingData?.selectedMethod; // data_breach, factual, etc.
  
  // Steps for this flow
  const steps = ['Select Items', 'Add Context', 'Review', 'Generate', 'Complete'];
  
  // State
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedItems, setSelectedItems] = useState({ accounts: [], inquiries: [], personalInfo: [] });
  const [excludedItemIds, setExcludedItemIds] = useState(() => new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [deliveryFormat, setDeliveryFormat] = useState('combined');

  // Self-repair customers use backend-resolved package grouping; hide delivery format choice
  const showDeliveryFormatChoice = useMemo(() => {
    if (isStaff) return true;
    if (portalData?.capabilities?.access_staff_queues) return true;
    const agencyRoles = ['agency_owner', 'agency_admin', 'agency_staff', 'agency_member'];
    if (user?.role && agencyRoles.includes(user.role)) return true;
    if (isSelfServe || user?.role === 'self_repair_customer') return false;
    if (portalData?.portal_type === 'self_serve_portal') return false;
    return true;
  }, [isStaff, isSelfServe, user?.role, portalData?.capabilities?.access_staff_queues, portalData?.portal_type]);

  const resolvedDeliveryFormat = useMemo(() => {
    if (!showDeliveryFormatChoice) return 'combined';
    return searchParams.get('format') || deliveryFormat;
  }, [showDeliveryFormatChoice, searchParams, deliveryFormat]);

  const [disputeContext, setDisputeContext] = useState('');
  const [loading, setLoading] = useState(false);
  const [roundData, setRoundData] = useState(null);
  const [generatedLetters, setGeneratedLetters] = useState([]);
  const [generationError, setGenerationError] = useState(null);
  const [letterContent, setLetterContent] = useState(null);
  const [hasAppliedPreselection, setHasAppliedPreselection] = useState(false);
  const [hasAppliedSmartRouting, setHasAppliedSmartRouting] = useState(false);
  
  // Track if user manually overrode the strategy
  const [userOverrodeStrategy, setUserOverrodeStrategy] = useState(false);
  
  // AI Decision Engine state
  const [aiDecision, setAiDecision] = useState(preloadedAiDecision || null);
  const [decisionLoading, setDecisionLoading] = useState(false);
  
  // Method Resolution state - from Admin Rules
  const [methodResolution, setMethodResolution] = useState(null);
  const [methodResolutionLoading, setMethodResolutionLoading] = useState(false);
  
  // PI Anchor Protection state
  const [showPIAnchorModal, setShowPIAnchorModal] = useState(false);
  const [piAnchorConfirmations, setPIAnchorConfirmations] = useState([]);
  const [piValidationPending, setPIValidationPending] = useState(false);
  const [userProfileAddress, setUserProfileAddress] = useState(null);
  
  // PHASE A: Generation Plan Preview state - shows matched prompts BEFORE generation
  const [generationPlanPreview, setGenerationPlanPreview] = useState(null);
  const [generationPlanLoading, setGenerationPlanLoading] = useState(false);
  const [disputePlanSummaryStatus, setDisputePlanSummaryStatus] = useState('idle'); // idle|loading|success|empty|error
  const [disputePlanSummaryError, setDisputePlanSummaryError] = useState(null);
  const [disputePlanSummaryCollapsed, setDisputePlanSummaryCollapsed] = useState(false);
  const lastSuccessfulPlanPreviewRef = useRef(null);
  const [summaryRetryNonce, setSummaryRetryNonce] = useState(0);
  
  // View Prompt Modal state
  const [viewPromptModalOpen, setViewPromptModalOpen] = useState(false);
  const [selectedPromptForView, setSelectedPromptForView] = useState(null);
  
  // View Evidence Modal state
  const [viewEvidenceModalOpen, setViewEvidenceModalOpen] = useState(false);
  const [selectedEvidenceForView, setSelectedEvidenceForView] = useState(null);
  
  // ============================================
  // ROUND NUMBER RESOLUTION STATE (NEW)
  // ============================================
  // Round number is resolved from SENT history, not just creation order
  const [resolvedRoundNumber, setResolvedRoundNumber] = useState(1);
  const [roundResolutionMeta, setRoundResolutionMeta] = useState(null);
  
  // STEP 3 -> STEP 4 SHARED SUMMARY (source of truth)
  // ============================================
  // These are set in Review step and used in Generate step
  const [resolvedGenerationSummary, setResolvedGenerationSummary] = useState({
    totalItems: 0,
    totalPackages: 0,
    totalLetters: 0,
  });
  
  // ============================================
  // EMPLOYER RECOMMENDATION STATE
  // ============================================
  const [showEmployerWarning, setShowEmployerWarning] = useState(false);
  const [employerItemPendingRemoval, setEmployerItemPendingRemoval] = useState(null);
  const [employerRemovedItems, setEmployerRemovedItems] = useState([]); // Track removed employer items for Step 3
  
  // ============================================
  // PROCEEDING ITEMS EXPANDED STATE (Step 3)
  // ============================================
  const [showProceedingItemsExpanded, setShowProceedingItemsExpanded] = useState(false);
  
  // Resolve round number from sent history
  const resolveRoundNumber = async (bureau, family, itemIds) => {
    try {
      const userId = user?.user_id || user?.id;
      if (!userId || !itemIds?.length) return 1;
      
      const response = await fetch(`${BACKEND_URL}/api/letter-preview/resolve-round-number`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          user_id: userId,
          bureau: bureau || 'unknown',
          family: family || aiDecision?.generation_config?.classification || 'isolated',
          item_ids: itemIds,
          classification: aiDecision?.generation_config?.classification,
        }),
      });
      
      if (!response.ok) {
        if (process.env.NODE_ENV !== 'production') {
          console.debug('[ROUND RESOLUTION] Failed to resolve round number, defaulting to 1');
        }
        return 1;
      }
      
      const data = await response.json();
      if (data.success) {
        if (process.env.NODE_ENV !== 'production') {
          console.debug('[ROUND RESOLUTION]', {
            round: data.round_number,
            reason: data.resolution_meta?.reason,
          });
        }
        setResolvedRoundNumber(data.round_number);
        setRoundResolutionMeta(data.resolution_meta);
        return data.round_number;
      }
      
      return 1;
    } catch (error) {
      console.error('[ROUND RESOLUTION] Error:', error);
      return 1;
    }
  };
  
  // Personal Info items from preloaded data or report
  const personalInfoItems = useMemo(() => {
    // If we have preloaded personal info items, use those
    if (preloadedItems?.personalInfo?.length > 0) {
      return preloadedItems.personalInfo;
    }
    // Otherwise, extract from report's personal_info.bureau_data
    const bureauData = reportData?.personal_info?.bureau_data;
    if (!bureauData) return [];
    
    const items = [];
    
    // Field order for non-address fields
    const standardFields = ['name', 'date_of_birth', 'also_known_as', 'former', 'employers'];
    
    const fieldLabels = {
      name: 'Name',
      also_known_as: 'Also Known As',
      former: 'Former',
      date_of_birth: 'Date of Birth',
      employers: 'Employers'
    };
    
    // Helper to check if field has data
    const hasFieldData = (fieldData) => {
      return ['TransUnion', 'Experian', 'Equifax'].some(bureau => {
        const val = fieldData?.[bureau] || fieldData?.[bureau.toLowerCase()];
        if (!val) return false;
        if (Array.isArray(val)) return val.length > 0;
        if (typeof val === 'string') return val.trim().length > 0;
        return true;
      });
    };
    
    // Helper to get first address from array or string
    const getFirstAddress = (val) => {
      if (!val) return null;
      if (Array.isArray(val)) return val.length > 0 ? val[0] : null;
      return typeof val === 'string' && val.trim() ? val : null;
    };
    
    // 1. Add Current Address row (single row, one address per bureau - first/primary only)
    const currentAddresses = bureauData.current_addresses;
    if (currentAddresses && hasFieldData(currentAddresses)) {
      const tuCurrent = getFirstAddress(currentAddresses.TransUnion || currentAddresses.transunion);
      const exCurrent = getFirstAddress(currentAddresses.Experian || currentAddresses.experian);
      const eqCurrent = getFirstAddress(currentAddresses.Equifax || currentAddresses.equifax);
      
      if (tuCurrent || exCurrent || eqCurrent) {
        items.push({
          id: 'pi-current_address',
          field: 'current_address',
          fieldLabel: 'Current Address',
          type: 'personal_info',
          values: {
            TransUnion: tuCurrent,
            Experian: exCurrent,
            Equifax: eqCurrent
          },
          bureaus: ['TransUnion', 'Experian', 'Equifax'].filter(bureau => {
            if (bureau === 'TransUnion') return !!tuCurrent;
            if (bureau === 'Experian') return !!exCurrent;
            if (bureau === 'Equifax') return !!eqCurrent;
            return false;
          }),
        });
      }
    }
    
    // 2. Add Previous Address rows - EACH as its own separate row
    const previousAddressItems = bureauData.previous_address_items;
    if (previousAddressItems && Array.isArray(previousAddressItems)) {
      previousAddressItems.forEach((addrItem, idx) => {
        const hasAnyValue = addrItem.TransUnion || addrItem.Experian || addrItem.Equifax;
        if (hasAnyValue) {
          items.push({
            id: `pi-previous_address_${idx + 1}`,
            field: `previous_address_${idx + 1}`,
            fieldLabel: `Previous Address ${idx + 1}`,
            type: 'personal_info',
            values: {
              TransUnion: addrItem.TransUnion || null,
              Experian: addrItem.Experian || null,
              Equifax: addrItem.Equifax || null
            },
            bureaus: ['TransUnion', 'Experian', 'Equifax'].filter(bureau => addrItem[bureau]),
            // Include reported date metadata per bureau (for Anchor modal display)
            reportedDates: {
              TransUnion: addrItem.TransUnion_reported_date || null,
              Experian: addrItem.Experian_reported_date || null,
              Equifax: addrItem.Equifax_reported_date || null
            }
          });
        }
      });
    }
    
    // 3. Add standard fields (name, dob, aliases, etc.)
    standardFields.forEach((fieldKey) => {
      const fieldData = bureauData[fieldKey];
      if (!fieldData || !hasFieldData(fieldData)) return;
      
      items.push({
        id: `pi-${fieldKey}`,
        field: fieldKey,
        fieldLabel: fieldLabels[fieldKey] || fieldKey,
        type: 'personal_info',
        values: {
          TransUnion: fieldData.TransUnion || fieldData.transunion || fieldData.tu,
          Experian: fieldData.Experian || fieldData.experian || fieldData.ex,
          Equifax: fieldData.Equifax || fieldData.equifax || fieldData.eq
        },
        bureaus: ['TransUnion', 'Experian', 'Equifax'].filter(bureau => {
          const val = fieldData[bureau] || fieldData[bureau.toLowerCase()];
          return val && (Array.isArray(val) ? val.length > 0 : true);
        }),
      });
    });
    
    return items;
  }, [preloadedItems, reportData]);

  // Extract items from report
  const negativeAccounts = useMemo(() => {
    return filterNegativeTradelines(reportData?.tradelines || []).map((t) => ({
      ...t,
      id: t.id || t.account_id || t.tradeline_id || buildDeterministicFallbackItemId(t, 'account'),
    }));
  }, [reportData]);

  const hardInquiries = useMemo(() => {
    return (reportData?.inquiries || []).filter(i => 
      i.inquiry_type === 'hard' || !i.inquiry_type
    ).map((i) => ({
      ...i,
      // Normalize ID: prefer inquiry_id, fallback to id, then generate
      id: i.inquiry_id || i.id || buildDeterministicFallbackItemId(i, 'inquiry'),
      // Keep inquiry_id for backward compatibility
      inquiry_id: i.inquiry_id || i.id || buildDeterministicFallbackItemId(i, 'inquiry'),
    }));
  }, [reportData]);

  const protectedPIIds = useMemo(
    () => piAnchorConfirmations.map((conf) => conf.correct_item_id).filter(Boolean),
    [piAnchorConfirmations]
  );
  const protectedPIIdSet = useMemo(
    () => new Set((protectedPIIds || []).map((id) => String(id))),
    [protectedPIIds]
  );

  const normalizedExcludedItems = useMemo(
    () => normalizeExcludedItems(methodResolution?.excluded_items || []),
    [methodResolution?.excluded_items]
  );

  const excludedMatcher = useMemo(
    () => buildExcludedMatcher(normalizedExcludedItems),
    [normalizedExcludedItems]
  );

  useEffect(() => {
    if (!normalizedExcludedItems.length && !(excludedMatcher?.ids?.size || 0)) return;
    setExcludedItemIds((prev) => {
      const next = new Set(prev);
      normalizedExcludedItems.forEach((item) => {
        if (item?.item_id) next.add(String(item.item_id));
      });
      [...(excludedMatcher?.ids || [])].forEach((id) => next.add(String(id)));
      return next;
    });
  }, [normalizedExcludedItems, excludedMatcher]);

  const catalogDisplayedItemsKey = useMemo(
    () => buildCatalogDisplayedItemsKey({
      selectedReportId,
      accounts: negativeAccounts,
      inquiries: hardInquiries,
      personalInfo: personalInfoItems,
    }),
    [negativeAccounts, hardInquiries, personalInfoItems, selectedReportId]
  );

  const catalogSplit = useMemo(
    () => splitDisplayedCatalogItems({
      accounts: negativeAccounts,
      inquiries: hardInquiries,
      personalInfo: personalInfoItems,
      excludedMatcher,
      protectedPIIds,
    }),
    [negativeAccounts, hardInquiries, personalInfoItems, excludedMatcher, protectedPIIds]
  );

  const {
    eligibleAccounts,
    eligibleInquiries,
    eligiblePersonalInfo,
  } = catalogSplit;

  const filteredEligibleAccounts = useMemo(() => {
    if (!searchTerm) return eligibleAccounts;
    const term = searchTerm.toLowerCase();
    return eligibleAccounts.filter(a =>
      (a.creditor_name || a.account_name || '').toLowerCase().includes(term) ||
      (a.account_type || '').toLowerCase().includes(term)
    );
  }, [eligibleAccounts, searchTerm]);

  const filteredEligibleInquiries = useMemo(() => {
    if (!searchTerm) return eligibleInquiries;
    const term = searchTerm.toLowerCase();
    return eligibleInquiries.filter(i =>
      (i.company_name || i.inquiry_name || '').toLowerCase().includes(term)
    );
  }, [eligibleInquiries, searchTerm]);

  const excludedItemIdsSet = useMemo(() => {
    const ids = new Set([...(excludedItemIds || [])].map((id) => String(id)));
    return ids;
  }, [excludedItemIds]);

  const excludedDisplayedItems = useMemo(
    () => {
      const displayed = buildExcludedDisplayedItems({
        normalizedExcludedItems,
        accounts: negativeAccounts,
        inquiries: hardInquiries,
        personalInfo: personalInfoItems,
        excludedMatcher,
      });
      const existingIds = new Set(displayed.map((item) => String(item.item_id || '')));
      [...excludedItemIdsSet].forEach((id) => {
        if (existingIds.has(String(id))) return;
        const account = negativeAccounts.find((item) => String(item.id) === String(id));
        const inquiry = hardInquiries.find((item) => String(item.id) === String(id));
        const personalInfo = personalInfoItems.find((item) => String(item.id) === String(id));
        const source = account || inquiry || personalInfo;
        if (!source) return;
        displayed.push({
          item_id: String(id),
          item_type: source.type || source.item_type || (account ? 'account' : inquiry ? 'inquiry' : 'personal_info'),
          reason: 'Item excluded by admin rules',
          display_label: formatExcludedDisplayLabel(source),
          status: 'excluded',
        });
      });
      return displayed;
    },
    [
      normalizedExcludedItems,
      negativeAccounts,
      hardInquiries,
      personalInfoItems,
      excludedMatcher,
      excludedItemIdsSet,
    ]
  );

  const catalogAccountsForInit = useMemo(
    () => negativeAccounts.filter((item) => isEligibleForDefaultSelection(item, 'account')),
    [negativeAccounts]
  );

  const catalogInquiriesForInit = useMemo(
    () => hardInquiries.filter((item) => isEligibleForDefaultSelection(item, 'inquiry')),
    [hardInquiries]
  );

  const catalogPersonalInfoForInit = useMemo(
    () => personalInfoItems.filter((item) => isEligibleForDefaultSelection(item, 'personal_info', { protectedPIIds })),
    [personalInfoItems, protectedPIIds]
  );

  const exclusionPruneKey = useMemo(
    () => normalizedExcludedItems
      .map((item) => `${item.item_id}|${item.fingerprint}`)
      .sort()
      .join('|'),
    [normalizedExcludedItems]
  );

  const effectiveSelectedItems = useMemo(() => {
    // Do not feed methodResolution into companion expansion — that creates a
    // companions ↔ resolve-methods render/request loop.
    const effective = buildEffectiveDisputeSelection(
      selectedItems,
      hardInquiries,
      personalInfoItems,
      {
        protectedPIIds,
        excludedMatcher,
        selectedMethod,
        classification: classificationResult,
        allowMethodResolutionCompanions: false,
      }
    );
    const selectedCount =
      (selectedItems.accounts?.length || 0)
      + (selectedItems.inquiries?.length || 0)
      + (selectedItems.personalInfo?.length || 0);
    const effectiveCount =
      (effective.accounts?.length || 0)
      + (effective.inquiries?.length || 0)
      + (effective.personalInfo?.length || 0);
    if (
      process.env.NODE_ENV !== 'production'
      && effectiveCount > selectedCount
    ) {
      console.debug('[DATA BREACH COMPANIONS] merged report inquiries/PI into effective selection', {
        selectedCount,
        effectiveCount,
      });
    }
    return effective;
  }, [
    selectedItems,
    hardInquiries,
    personalInfoItems,
    protectedPIIds,
    excludedMatcher,
    selectedMethod,
    classificationResult,
  ]);

  const accountById = useMemo(
    () => new Map(negativeAccounts.map((item) => [String(item.id), item])),
    [negativeAccounts]
  );
  const inquiryById = useMemo(
    () => new Map(hardInquiries.map((item) => [String(item.id), item])),
    [hardInquiries]
  );
  const personalInfoById = useMemo(
    () => new Map(personalInfoItems.map((item) => [String(item.id), item])),
    [personalInfoItems]
  );

  // All hooks below this point may depend on normalized selection state.
  // Do not move preview effects above this block.
  const normalizedSelectionState = useMemo(() => {
    const buildTypeState = (allItems, selectedIds) => buildDisputeWizardNormalizedState({
      allItems,
      selectedItemIds: selectedIds || [],
      excludedItemIds: [...excludedItemIdsSet],
    });

    const accountState = buildTypeState(negativeAccounts, effectiveSelectedItems.accounts || []);
    const inquiryState = buildTypeState(hardInquiries, effectiveSelectedItems.inquiries || []);
    const personalInfoProceedingIds = (effectiveSelectedItems.personalInfo || [])
      .map((id) => String(id))
      .filter((id) => !protectedPIIdSet.has(id));
    const personalInfoState = buildTypeState(personalInfoItems, personalInfoProceedingIds);

    return {
      accounts: accountState,
      inquiries: inquiryState,
      personalInfo: personalInfoState,
      proceedingSelectedItems: {
        accounts: accountState.proceedingItemIds,
        inquiries: inquiryState.proceedingItemIds,
        personalInfo: personalInfoState.proceedingItemIds,
      },
    };
  }, [
    excludedItemIdsSet,
    negativeAccounts,
    hardInquiries,
    personalInfoItems,
    effectiveSelectedItems,
    protectedPIIdSet,
  ]);

  const proceedingSelectedItems = normalizedSelectionState.proceedingSelectedItems;
  const selectionSignature = useMemo(() => JSON.stringify({
    selected: [
      ...(effectiveSelectedItems.accounts || []),
      ...(effectiveSelectedItems.inquiries || []),
      ...(effectiveSelectedItems.personalInfo || []),
    ].map(String).sort(),
    excluded: [...excludedItemIdsSet].map(String).sort(),
    proceeding: [
      ...(proceedingSelectedItems.accounts || []),
      ...(proceedingSelectedItems.inquiries || []),
      ...(proceedingSelectedItems.personalInfo || []),
    ].map(String).sort(),
  }), [effectiveSelectedItems, excludedItemIdsSet, proceedingSelectedItems]);

  useEffect(() => {
    if (!DEBUG_DISPUTE_SELECTION) return;
    console.log('[DISPUTE WIZARD NORMALIZED STATE]', {
      allCount: negativeAccounts.length + hardInquiries.length + personalInfoItems.length,
      selectedCount:
        (effectiveSelectedItems.accounts?.length || 0)
        + (effectiveSelectedItems.inquiries?.length || 0)
        + (effectiveSelectedItems.personalInfo?.length || 0),
      excludedCount: excludedItemIdsSet.size,
      proceedingCount:
        proceedingSelectedItems.accounts.length
        + proceedingSelectedItems.inquiries.length
        + proceedingSelectedItems.personalInfo.length,
      visibleCountsByCategory: {
        accounts: normalizedSelectionState.accounts.visibleItems.length,
        inquiries: normalizedSelectionState.inquiries.visibleItems.length,
        personalInfo: normalizedSelectionState.personalInfo.visibleItems.length,
      },
      selectedIds: [
        ...(effectiveSelectedItems.accounts || []),
        ...(effectiveSelectedItems.inquiries || []),
        ...(effectiveSelectedItems.personalInfo || []),
      ],
      excludedIds: [...excludedItemIdsSet],
      proceedingIds: [
        ...proceedingSelectedItems.accounts,
        ...proceedingSelectedItems.inquiries,
        ...proceedingSelectedItems.personalInfo,
      ],
      selectionSignature,
      source: 'item_selection_page',
    });
  }, [
    negativeAccounts.length,
    hardInquiries.length,
    personalInfoItems.length,
    effectiveSelectedItems,
    excludedItemIdsSet,
    proceedingSelectedItems,
    normalizedSelectionState,
    selectionSignature,
  ]);

  const DEBUG_ITEM_DEFAULT_SELECT = process.env.NODE_ENV !== 'production';

  const hasInitializedSelectionRef = useRef(false);
  const lastCatalogDatasetKeyRef = useRef('');
  const lastExclusionPruneKeyRef = useRef('');
  const step3GenerationPayloadRef = useRef(null);
  const buildGenerationRequestFnRef = useRef(null);
  const reviewPlanSnapshotRef = useRef(null);
  const generatePayloadLoggedForRoundRef = useRef(null);
  const [regenerationNonce, setRegenerationNonce] = useState(0);
  const lastPreviewSignatureRef = useRef('');
  const inFlightPreviewSignatureRef = useRef('');
  const previewAbortControllerRef = useRef(null);
  const summaryWaitTimeoutRef = useRef(null);
  const summaryFailSafeTimeoutRef = useRef(null);
  const lastResolveMethodsKeyRef = useRef('');
  const resolveMethodsCoordinatorRef = useRef(null);
  const lastSuccessfulMethodResolutionRef = useRef(null);
  const methodResolutionRef = useRef(null);
  const tokenRef = useRef(token);
  tokenRef.current = token;
  const tradelinesSignature = useMemo(() => {
    const tradelines = reportData?.tradelines || [];
    return tradelines
      .map((acc) => String(acc.id || acc.account_id || acc.tradeline_id || ''))
      .filter(Boolean)
      .sort()
      .join('|');
  }, [reportData?.tradelines]);

  const classificationSignature = useMemo(
    () => JSON.stringify(classificationResult ?? null),
    [classificationResult]
  );

  const accountsCatalogSignature = useMemo(
    () => JSON.stringify(
      (negativeAccounts || [])
        .map((account) => ({
          id: String(account.id || ''),
          accountType: account.account_type || account.type || null,
          accountTypeDetail: account.account_type_detail || account.accountTypeDetail || null,
          status: account.status || account.account_status || null,
          paymentStatus: account.payment_status || null,
          isCollection: Boolean(account.is_collection ?? account.isCollection),
          isChargeoff: Boolean(account.is_chargeoff ?? account.isChargeoff),
          originalCreditor: account.original_creditor || account.originalCreditor || null,
          creditorName: account.creditor_name || account.account_name || account.creditorName || null,
          latePaymentCount: account.late_payment_count || account.latePaymentCount || 0,
        }))
        .sort((a, b) => String(a.id).localeCompare(String(b.id)))
    ),
    [negativeAccounts]
  );

  const inquiriesCatalogSignature = useMemo(
    () => JSON.stringify(
      (hardInquiries || [])
        .map((inquiry) => ({
          id: String(inquiry.id || ''),
          creditorName: inquiry.creditor_name || inquiry.company_name || inquiry.inquiry_name || null,
          inquiryDate: inquiry.inquiry_date || inquiry.date || null,
          bureau: inquiry.bureau || null,
        }))
        .sort((a, b) => String(a.id).localeCompare(String(b.id)))
    ),
    [hardInquiries]
  );

  const personalInfoCatalogSignature = useMemo(
    () => JSON.stringify(
      (personalInfoItems || [])
        .map((pi) => ({
          id: String(pi.id || ''),
          field: pi.field || pi.fieldLabel || null,
          bureaus: pi.bureaus || [],
        }))
        .sort((a, b) => String(a.id).localeCompare(String(b.id)))
    ),
    [personalInfoItems]
  );

  const resolveMethodsMode = selectedMethod || classificationResult || disputeType || 'factual';

  const resolveMethodsRequestInput = useMemo(() => {
    return buildResolveMethodsRequestInput({
      proceedingSelectedItems,
      negativeAccounts,
      hardInquiries,
      personalInfoItems,
      tradelines: reportData?.tradelines || [],
      resolvedRoundNumber,
      mode: resolveMethodsMode,
    });
    // Intentionally keyed by primitive signatures so equivalent array/map identity
    // churn does not rebuild the request or retrigger the network effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectionSignature,
    tradelinesSignature,
    accountsCatalogSignature,
    inquiriesCatalogSignature,
    personalInfoCatalogSignature,
    resolvedRoundNumber,
    selectedMethod,
    classificationSignature,
    resolveMethodsMode,
  ]);

  const resolveMethodsKey = resolveMethodsRequestInput?.resolveMethodsKey || '';

  useEffect(() => {
    methodResolutionRef.current = methodResolution;
  }, [methodResolution]);

  if (!resolveMethodsCoordinatorRef.current) {
    resolveMethodsCoordinatorRef.current = createResolveMethodsRequestCoordinator({
      debounceMs: 300,
      endpoint: `${BACKEND_URL}/api/admin-rules/resolve-methods`,
      getAuthToken: () => tokenRef.current,
      onLoading: (loading) => setMethodResolutionLoading(loading),
      onEmpty: () => {
        setMethodResolution(null);
        lastResolveMethodsKeyRef.current = '';
      },
      onSuccess: (data, meta) => {
        lastResolveMethodsKeyRef.current = meta.key;
        lastSuccessfulMethodResolutionRef.current = data;
        setMethodResolution(data);
        if (DEBUG_DISPUTE_SELECTION) {
          console.debug('[METHOD RESOLUTION]', data.summary?.is_mixed ? 'MIXED' : 'UNIFORM', data.summary);
        }
      },
      onError: (error) => {
        console.error('[METHOD RESOLUTION] Error:', error);
        if (lastSuccessfulMethodResolutionRef.current) {
          setMethodResolution(lastSuccessfulMethodResolutionRef.current);
        }
      },
    });
  }

  useEffect(() => {
    if (currentStep !== 3) {
      step3GenerationPayloadRef.current = null;
      generatePayloadLoggedForRoundRef.current = null;
    }
  }, [currentStep]);

  // Selection helpers
  const toggleItem = (type, id) => {
    hasInitializedSelectionRef.current = true;
    setSelectedItems(prev => {
      const key = type === 'account' ? 'accounts' : 'inquiries';
      const current = prev[key];
      const newList = current.includes(id)
        ? current.filter(i => i !== id)
        : [...current, id];
      return { ...prev, [key]: newList };
    });
  };

  // Apply pre-selection from navigation state (e.g., from Credit Report Dispute button)
  useEffect(() => {
    if (preselectedItem && !hasAppliedPreselection && negativeAccounts.length > 0) {
      const { type, tradeline, inquiry } = preselectedItem;
      
      if (type === 'account' && tradeline) {
        // Find matching account in the list
        const matchingAccount = negativeAccounts.find(a => 
          a.tradeline_id === tradeline.tradeline_id ||
          a.id === tradeline.id ||
          (a.creditor_name === tradeline.creditor_name && a.account_number === tradeline.account_number)
        );
        
        if (matchingAccount) {
          setSelectedItems(buildEffectiveDisputeSelection({
            accounts: [matchingAccount.id],
            inquiries: [],
            personalInfo: [],
          }, hardInquiries, personalInfoItems, {
            selectedMethod,
            classification: classificationResult,
            forceDataBreachCompanions: true,
          }));
          toast.success(`${tradeline.creditor_name || tradeline.account_name || 'Account'} pre-selected for dispute`);
        }
      } else if (type === 'inquiry' && inquiry) {
        // Find matching inquiry
        const matchingInquiry = hardInquiries.find(i => 
          i.id === inquiry.id ||
          (i.company_name === inquiry.company_name && i.inquiry_date === inquiry.inquiry_date)
        );
        
        if (matchingInquiry) {
          setSelectedItems(prev => ({
            ...prev,
            inquiries: [matchingInquiry.id]
          }));
          toast.success(`${inquiry.company_name || 'Inquiry'} pre-selected for dispute`);
        }
      }
      
      setHasAppliedPreselection(true);
    }
  }, [preselectedItem, negativeAccounts, hardInquiries, hasAppliedPreselection]);

  // SMART ROUTING: Apply preloaded items from Credit Report smart selection
  useEffect(() => {
    if (smartRoutingData && preloadedItems && !hasAppliedSmartRouting) {
      // Extract account IDs from preloaded items
      const accountIds = [];
      const inquiryIds = [];
      
      console.log('[SMART ROUTING] Applying preloaded items:', {
        preloadedAccounts: preloadedItems.accounts?.length || 0,
        preloadedInquiries: preloadedItems.inquiries?.length || 0,
        availableAccounts: negativeAccounts.length,
        availableInquiries: hardInquiries.length,
      });
      
      // Match preloaded accounts with negative accounts
      (preloadedItems.accounts || []).forEach(preloadedAccount => {
        const matchingAccount = negativeAccounts.find(a => 
          a.tradeline_id === preloadedAccount.tradeline_id ||
          a.id === preloadedAccount.id ||
          a.account_id === preloadedAccount.account_id ||
          (a.creditor_name === preloadedAccount.creditor_name && 
           a.account_number === preloadedAccount.account_number)
        );
        if (matchingAccount && !accountIds.includes(matchingAccount.id)) {
          console.log('[SMART ROUTING] Matched account:', matchingAccount.creditor_name, '->', matchingAccount.id);
          accountIds.push(matchingAccount.id);
        }
      });
      
      // Match preloaded inquiries with hard inquiries
      // Use multiple matching strategies to handle different ID formats
      (preloadedItems.inquiries || []).forEach(preloadedInquiry => {
        // Build a canonical identifier for the preloaded inquiry
        const preloadedId = preloadedInquiry.id || preloadedInquiry.inquiry_id;
        const preloadedCompany = (preloadedInquiry.company_name || '').toLowerCase().trim();
        const preloadedDate = preloadedInquiry.inquiry_date || preloadedInquiry.date;
        const preloadedBureau = (preloadedInquiry.bureau || '').toLowerCase().trim();
        
        const matchingInquiry = hardInquiries.find(i => {
          // Try exact ID match first
          if (preloadedId && (i.id === preloadedId || i.inquiry_id === preloadedId)) {
            return true;
          }
          
          // Try matching by company + date + bureau
          const iCompany = (i.company_name || i.inquiry_name || '').toLowerCase().trim();
          const iDate = i.inquiry_date || i.date;
          const iBureau = (i.bureau || '').toLowerCase().trim();
          
          if (preloadedCompany && preloadedDate && 
              iCompany.includes(preloadedCompany.substring(0, 5)) && 
              iDate === preloadedDate) {
            // Additional bureau check if available
            if (preloadedBureau && iBureau && preloadedBureau !== iBureau) {
              return false;
            }
            return true;
          }
          
          // Try matching by company name + bureau only
          if (preloadedCompany && preloadedBureau &&
              iCompany.includes(preloadedCompany.substring(0, 5)) && 
              iBureau === preloadedBureau) {
            return true;
          }
          
          return false;
        });
        
        if (matchingInquiry && !inquiryIds.includes(matchingInquiry.id)) {
          console.log('[SMART ROUTING] Matched inquiry:', matchingInquiry.company_name, matchingInquiry.bureau, '->', matchingInquiry.id);
          inquiryIds.push(matchingInquiry.id);
        } else if (!matchingInquiry) {
          console.log('[SMART ROUTING] Could not match inquiry:', preloadedInquiry.company_name, preloadedInquiry.bureau);
        }
      });
      
      // Apply selections if we found matches
      // Also include personal info items directly (they don't need matching)
      const personalInfoIds = (preloadedItems.personalInfo || []).map(pi => pi.id);
      
      const totalMatched = accountIds.length + inquiryIds.length + personalInfoIds.length;
      const totalPreloaded = (preloadedItems.accounts?.length || 0) + 
                             (preloadedItems.inquiries?.length || 0) + 
                             (preloadedItems.personalInfo?.length || 0);
      
      console.log('[SMART ROUTING] Match results:', {
        accountsMatched: accountIds.length,
        inquiriesMatched: inquiryIds.length,
        personalInfoCount: personalInfoIds.length,
        totalMatched,
        totalPreloaded,
      });
      
      if (totalMatched > 0) {
        setSelectedItems(buildEffectiveDisputeSelection({
          accounts: accountIds,
          inquiries: inquiryIds,
          personalInfo: personalInfoIds,
        }, hardInquiries, personalInfoItems, {
          selectedMethod,
          classification: classificationResult,
          forceDataBreachCompanions: selectedMethod === 'data_breach' || classificationResult === 'data_breach',
        }));
        
        if (totalMatched < totalPreloaded) {
          toast.warning(`${totalMatched} of ${totalPreloaded} items matched`, {
            description: 'Some items could not be matched. You may need to select them manually.',
            duration: 4000,
          });
        } else {
          toast.success(`${totalMatched} item(s) loaded from Credit Report`, {
            description: `Strategy: ${recommendedStrategy?.title || classificationResult}`,
            duration: 3000,
          });
        }
      } else if (totalPreloaded > 0) {
        toast.error('Could not match preloaded items', {
          description: 'Please select items manually from the list below.',
          duration: 4000,
        });
      }
      
      setHasAppliedSmartRouting(true);
    }
  }, [smartRoutingData, preloadedItems, negativeAccounts, hardInquiries, hasAppliedSmartRouting, recommendedStrategy, classificationResult]);

  // Default-select all catalog items once per report dataset (unless preselection/smart routing owns it).
  useEffect(() => {
    if (!catalogDisplayedItemsKey) return;

    if (lastCatalogDatasetKeyRef.current !== catalogDisplayedItemsKey) {
      hasInitializedSelectionRef.current = false;
      lastCatalogDatasetKeyRef.current = catalogDisplayedItemsKey;
      lastExclusionPruneKeyRef.current = '';
    }

    if (smartRoutingData && preloadedItems && !hasAppliedSmartRouting) return;
    if (preselectedItem && !hasAppliedPreselection) return;

    if (smartRoutingData && preloadedItems && hasAppliedSmartRouting) {
      hasInitializedSelectionRef.current = true;
      return;
    }

    if (preselectedItem && hasAppliedPreselection) {
      hasInitializedSelectionRef.current = true;
      return;
    }

    if (hasInitializedSelectionRef.current) return;

    const nextSelection = buildSelectAllEligibleSelection({
      eligibleAccounts: catalogAccountsForInit,
      eligibleInquiries: catalogInquiriesForInit,
      eligiblePersonalInfo: catalogPersonalInfoForInit,
    });
    const eligibleCount =
      nextSelection.accounts.length
      + nextSelection.inquiries.length
      + nextSelection.personalInfo.length;

    if (eligibleCount === 0) return;

    setSelectedItems(nextSelection);
    hasInitializedSelectionRef.current = true;

    if (DEBUG_ITEM_DEFAULT_SELECT) {
      console.log('[ITEM DEFAULT SELECT] eligibleDisplayedItems count', eligibleCount);
      console.log('[ITEM DEFAULT SELECT] selectedItemIds count', eligibleCount);
      console.log('[ITEM DEFAULT SELECT] excluded count', normalizedExcludedItems.length);
      console.log('[ITEM DEFAULT SELECT] dataset key', catalogDisplayedItemsKey);
    }
  }, [
    catalogDisplayedItemsKey,
    catalogAccountsForInit,
    catalogInquiriesForInit,
    catalogPersonalInfoForInit,
    normalizedExcludedItems.length,
    smartRoutingData,
    preloadedItems,
    hasAppliedSmartRouting,
    preselectedItem,
    hasAppliedPreselection,
  ]);

  // Remove admin-excluded items from selection once per exclusion snapshot.
  useEffect(() => {
    if (!exclusionPruneKey) return;
    if (lastExclusionPruneKeyRef.current === exclusionPruneKey) return;
    lastExclusionPruneKeyRef.current = exclusionPruneKey;

    setSelectedItems((prev) => {
      const next = pruneSelectedItemsForExclusions(prev, {
        accounts: negativeAccounts,
        inquiries: hardInquiries,
        personalInfo: personalInfoItems,
        excludedMatcher,
      });

      if (
        next.accounts.length === prev.accounts.length
        && next.inquiries.length === prev.inquiries.length
        && next.personalInfo.length === prev.personalInfo.length
      ) {
        return prev;
      }

      return next;
    });
  }, [
    exclusionPruneKey,
    excludedMatcher,
    negativeAccounts,
    hardInquiries,
    personalInfoItems,
  ]);

  // ============================================
  // FETCH USER PROFILE ADDRESS FOR SAVED ADDRESSES
  // ============================================
  useEffect(() => {
    const fetchUserProfileAddress = async () => {
      if (!token) return;
      
      try {
        const response = await fetch(`${BACKEND_URL}/api/users/profile`, {
          headers: getAuthHeaders(),
        });
        
        if (response.ok) {
          const profile = await response.json();
          // Check if profile has address data
          const mailingAddr = profile.mailing_address || {};
          if (mailingAddr.line1 || profile.address_line1) {
            setUserProfileAddress({
              street_address: mailingAddr.line1 || profile.address_line1 || '',
              unit: mailingAddr.line2 || profile.address_line2 || '',
              city: mailingAddr.city || profile.city || '',
              state: mailingAddr.state || profile.state || '',
              zip: mailingAddr.postal_code || profile.postal_code || '',
              source: 'client_profile',
            });
            console.log('[PI ANCHOR] Loaded user profile address:', mailingAddr.line1 || profile.address_line1);
          }
        }
      } catch (err) {
        console.warn('[PI ANCHOR] Failed to fetch user profile address:', err);
      }
    };
    
    fetchUserProfileAddress();
  }, [token, getAuthHeaders]);

  const selectAll = () => {
    hasInitializedSelectionRef.current = true;
    setSelectedItems(buildSelectAllEligibleSelection({
      eligibleAccounts,
      eligibleInquiries,
      eligiblePersonalInfo,
    }));
  };

  const clearAll = () => {
    hasInitializedSelectionRef.current = true;
    setSelectedItems({ accounts: [], inquiries: [], personalInfo: [] });
  };

  const allEligibleSelected = useMemo(() => {
    const totalEligible =
      eligibleAccounts.length + eligibleInquiries.length + eligiblePersonalInfo.length;
    if (totalEligible === 0) return false;

    const accountsSelected = eligibleAccounts.every((item) => selectedItems.accounts.includes(item.id));
    const inquiriesSelected = eligibleInquiries.every((item) => selectedItems.inquiries.includes(item.id));
    const personalInfoSelected = eligiblePersonalInfo.every((item) => selectedItems.personalInfo.includes(item.id));

    return accountsSelected && inquiriesSelected && personalInfoSelected;
  }, [eligibleAccounts, eligibleInquiries, eligiblePersonalInfo, selectedItems]);

  const totalSelected =
    proceedingSelectedItems.accounts.length
    + proceedingSelectedItems.inquiries.length
    + proceedingSelectedItems.personalInfo.length;

  const proceedingItemIdsSet = useMemo(
    () => new Set([
      ...proceedingSelectedItems.accounts.map(String),
      ...proceedingSelectedItems.inquiries.map(String),
      ...proceedingSelectedItems.personalInfo.map(String),
    ]),
    [proceedingSelectedItems]
  );
  const proceedingItemsCount = proceedingItemIdsSet.size;

  // Bureau counts - count only eligible selected items
  const bureauCounts = useMemo(() => {
    const counts = {};
    const missingProceedingIds = new Set(proceedingItemIdsSet);
    
    proceedingSelectedItems.accounts.forEach(id => {
      const account = accountById.get(String(id)) || eligibleAccounts.find(a => String(a.id) === String(id));
      if (account?.bureau) {
        counts[account.bureau] = (counts[account.bureau] || 0) + 1;
        missingProceedingIds.delete(String(id));
      }
    });
    
    proceedingSelectedItems.inquiries.forEach(id => {
      const inquiry = inquiryById.get(String(id)) || eligibleInquiries.find(i => String(i.id) === String(id));
      if (inquiry?.bureau) {
        counts[inquiry.bureau] = (counts[inquiry.bureau] || 0) + 1;
        missingProceedingIds.delete(String(id));
      }
    });
    
    proceedingSelectedItems.personalInfo.forEach(id => {
      const piItem = personalInfoById.get(String(id)) || eligiblePersonalInfo.find(pi => String(pi.id) === String(id));
      if (piItem?.bureaus) {
        piItem.bureaus.forEach(bureau => {
          counts[bureau] = (counts[bureau] || 0) + 1;
        });
        missingProceedingIds.delete(String(id));
      }
    });
    
    if (DEBUG_DISPUTE_SELECTION && missingProceedingIds.size > 0) {
      console.log('[BUREAU COUNTS] Mismatch:', {
        actualProceedingCount: proceedingItemsCount,
        missingProceedingIds: [...missingProceedingIds],
        selectedAccounts: proceedingSelectedItems.accounts,
        selectedInquiries: proceedingSelectedItems.inquiries,
        selectedPersonalInfo: proceedingSelectedItems.personalInfo,
        bureauCounts: counts,
      });
    }
    
    return counts;
  }, [
    proceedingSelectedItems,
    eligibleAccounts,
    eligibleInquiries,
    eligiblePersonalInfo,
    proceedingItemsCount,
    proceedingItemIdsSet,
    accountById,
    inquiryById,
    personalInfoById,
  ]);

  const generationSummaryFromProceeding = useMemo(() => {
    const itemAssignments = methodResolution?.item_assignments || {};
    const methodGroups = {};
    proceedingSelectedItems.accounts.forEach((id) => {
      const methodKey = itemAssignments[id]?.assigned_method || 'unassigned';
      methodGroups[methodKey] = (methodGroups[methodKey] || 0) + 1;
    });
    proceedingSelectedItems.inquiries.forEach((id) => {
      const methodKey = itemAssignments[id]?.assigned_method || 'unassigned';
      methodGroups[methodKey] = (methodGroups[methodKey] || 0) + 1;
    });
    proceedingSelectedItems.personalInfo.forEach((id) => {
      const methodKey = itemAssignments[id]?.assigned_method || 'unassigned';
      methodGroups[methodKey] = (methodGroups[methodKey] || 0) + 1;
    });

    const totalPackages = Object.keys(methodGroups).length || (proceedingItemsCount > 0 ? 1 : 0);
    const totalLetters =
      generationPlanPreview?.expected_package_count
      || generationPlanPreview?.packages?.length
      || (proceedingItemsCount > 0
        ? Math.max(totalPackages, Object.keys(bureauCounts || {}).length || 1)
        : 0);

    return {
      totalItems: proceedingItemsCount,
      totalPackages,
      totalLetters: proceedingItemsCount > 0 ? totalLetters : 0,
      packageTotals: methodGroups,
    };
  }, [methodResolution, proceedingSelectedItems, proceedingItemsCount, bureauCounts, generationPlanPreview]);

  useEffect(() => {
    setResolvedGenerationSummary((prev) => {
      if (
        prev.totalItems === generationSummaryFromProceeding.totalItems
        && prev.totalPackages === generationSummaryFromProceeding.totalPackages
        && prev.totalLetters === generationSummaryFromProceeding.totalLetters
      ) {
        return prev;
      }
      return generationSummaryFromProceeding;
    });
  }, [generationSummaryFromProceeding]);
  
  // Resolve methods from Admin Rules — deps are key + memoized request only.
  useEffect(() => {
    const coordinator = resolveMethodsCoordinatorRef.current;
    const requestInput = resolveMethodsRequestInput;
    const key = resolveMethodsKey;

    if (!key || !requestInput?.itemsForResolution?.length) {
      coordinator.start({ key: '', body: null });
      return undefined;
    }

    if (
      key === lastResolveMethodsKeyRef.current
      && lastSuccessfulMethodResolutionRef.current
      && !methodResolutionRef.current
    ) {
      setMethodResolution(lastSuccessfulMethodResolutionRef.current);
    }

    const handle = coordinator.start({
      key,
      body: requestInput,
    });

    return () => {
      if (handle?.cleanup) handle.cleanup();
    };
  }, [resolveMethodsKey, token, resolveMethodsRequestInput]);

  // PHASE A: Fetch generation plan preview when entering Review step (step 2)
  useEffect(() => {
    const fetchGenerationPlanPreview = async () => {
      // Only fetch when on step 2 (Review) and we have items selected
      if (currentStep !== 2 || !aiDecision) {
        if (currentStep !== 2) {
          setGenerationPlanLoading(false);
          if (disputePlanSummaryStatus === 'loading') {
            setDisputePlanSummaryStatus(generationPlanPreview || lastSuccessfulPlanPreviewRef.current ? 'success' : 'idle');
          }
        }
        return;
      }
      
      const totalSelected =
        proceedingSelectedItems.accounts.length
        + proceedingSelectedItems.inquiries.length
        + (proceedingSelectedItems.personalInfo?.length || 0);
      if (totalSelected === 0) {
        setGenerationPlanLoading(false);
        setDisputePlanSummaryStatus('empty');
        return;
      }

      if (methodResolutionLoading) {
        setDisputePlanSummaryStatus((prev) => (prev === 'success' ? prev : 'loading'));
        setGenerationPlanLoading(true);
        if (summaryWaitTimeoutRef.current) {
          clearTimeout(summaryWaitTimeoutRef.current);
        }
        // Fail-safe while waiting on method resolution so the spinner cannot remain forever.
        summaryWaitTimeoutRef.current = setTimeout(() => {
          setGenerationPlanLoading(false);
          setDisputePlanSummaryStatus((prev) => (prev === 'success' ? prev : 'error'));
          setDisputePlanSummaryError(
            'Unable to prepare the dispute plan summary. Your selected items are still preserved.'
          );
          summaryWaitTimeoutRef.current = null;
        }, 20000);
        return;
      }

      if (summaryWaitTimeoutRef.current) {
        clearTimeout(summaryWaitTimeoutRef.current);
        summaryWaitTimeoutRef.current = null;
      }

      if (inFlightPreviewSignatureRef.current === selectionSignature) return;
      if (lastPreviewSignatureRef.current === selectionSignature && generationPlanPreview) {
        setDisputePlanSummaryStatus('success');
        setGenerationPlanLoading(false);
        return;
      }

      if (previewAbortControllerRef.current) {
        previewAbortControllerRef.current.abort();
      }
      const abortController = new AbortController();
      previewAbortControllerRef.current = abortController;
      inFlightPreviewSignatureRef.current = selectionSignature;
      
      setGenerationPlanLoading(true);
      setDisputePlanSummaryStatus('loading');
      setDisputePlanSummaryError(null);

      if (summaryFailSafeTimeoutRef.current) {
        clearTimeout(summaryFailSafeTimeoutRef.current);
      }
      summaryFailSafeTimeoutRef.current = setTimeout(() => {
        if (inFlightPreviewSignatureRef.current === selectionSignature) {
          setGenerationPlanLoading(false);
          setDisputePlanSummaryStatus((prev) => (prev === 'success' ? prev : 'error'));
          setDisputePlanSummaryError(
            'Unable to prepare the dispute plan summary. Your selected items are still preserved.'
          );
          inFlightPreviewSignatureRef.current = '';
        }
        summaryFailSafeTimeoutRef.current = null;
      }, 20000);
      
      try {
        // ============================================
        // RESOLVE ROUND NUMBER FROM SENT HISTORY
        // ============================================
        // Get all item IDs for thread resolution
        const allItemIds = [
          ...proceedingSelectedItems.accounts,
          ...proceedingSelectedItems.inquiries,
          ...(proceedingSelectedItems.personalInfo || [])
        ];
        
        // Resolve round number before building the generation plan
        const resolvedRound = await resolveRoundNumber(
          'combined', // bureau will be resolved per-package
          aiDecision?.generation_config?.classification || 'isolated',
          allItemIds
        );
        
        if (DEBUG_DISPUTE_SELECTION) {
          console.debug('[GENERATION PLAN PREVIEW] Using resolved round number:', resolvedRound);
        }
        
        // Build dispute items for the preview request
        const disputeItems = [];
        
        proceedingSelectedItems.accounts.forEach(id => {
          const account = accountById.get(String(id));
          if (account) {
            // Extract account_type_detail from bureau_comparison if available
            const bureauComparisonTypeDetail = account.bureau_comparison?.account_type_detail || {};
            const collectionSignals = detectCollectionSignals(account);
            const assignment = methodResolution?.item_assignments?.[id];
            const normalized = normalizeDisputeMethod(
              {
                ...account,
                is_collection: collectionSignals.is_collection,
                original_creditor: collectionSignals.original_creditor,
              },
              assignment
            );
            const extractedTypeDetail = collectionSignals.account_type_detail
              || account.account_type_detail
              || Object.values(bureauComparisonTypeDetail).find(v => v)
              || '';
            
            // Detect chargeoff status
            const isChargeoffAccount = (
              account.is_chargeoff ||
              account.account_type_detail?.toLowerCase()?.includes('chargeoff') ||
              account.account_type_detail?.toLowerCase()?.includes('charge-off') ||
              extractedTypeDetail?.toLowerCase()?.includes('chargeoff') ||
              extractedTypeDetail?.toLowerCase()?.includes('charge-off') ||
              account.status?.toLowerCase()?.includes('chargeoff') ||
              account.status?.toLowerCase()?.includes('charge-off') ||
              account.payment_status?.toLowerCase()?.includes('chargeoff') ||
              account.payment_status?.toLowerCase()?.includes('charge-off')
            );
            
            disputeItems.push({
              item_id: id,
              item_type: 'account',
              account_type: account.account_type || account.type || 'standard',
              account_type_detail: extractedTypeDetail,
              account_status: account.status || account.account_status || '',
              payment_status: account.payment_status || account.paymentStatus || account.status || '',
              original_creditor: collectionSignals.original_creditor || account.original_creditor || account.originalCreditor || '',
              late_payment_count: account.late_payment_count || account.latePaymentCount || 0,
              // FIX: Only use actual bureaus from account data - do NOT fall back to all 3
              bureaus: account.bureaus_reported || account.bureaus || (account.bureau ? [account.bureau] : []),
              bureaus_reported: account.bureaus_reported || account.bureaus || (account.bureau ? [account.bureau] : []),
              creditor_name: account.creditor_name || account.account_name || '',
              is_chargeoff: isChargeoffAccount,
              is_collection: normalized.isCollection,
              dispute_family: normalized.disputeFamily,
              method: normalized.method,
              method_assignment: methodResolution?.item_assignments?.[id] || null,
            });
          }
        });
        
        proceedingSelectedItems.inquiries.forEach(id => {
          const inquiry = inquiryById.get(String(id));
          if (inquiry) {
            disputeItems.push({
              item_id: id,
              item_type: 'inquiry',
              bureaus: inquiry.bureau ? [inquiry.bureau] : [],
              bureaus_reported: inquiry.bureau ? [inquiry.bureau] : [],
              creditor_name: inquiry.creditor_name || inquiry.company_name || inquiry.inquiry_name || '',
              method_assignment: methodResolution?.item_assignments?.[id] || null,
            });
          }
        });
        
        proceedingSelectedItems.personalInfo?.forEach(id => {
          const piItem = personalInfoById.get(String(id));
          if (piItem) {
            disputeItems.push({
              item_id: id,
              item_type: 'personal_info',
              bureaus: piItem.bureaus || [],
              bureaus_reported: piItem.bureaus || [],
              field_type: piItem.field || piItem.fieldLabel || '',
              method_assignment: methodResolution?.item_assignments?.[id] || null,
            });
          }
        });
        
        // Call the preview-generation-plan endpoint
        const response = await fetch(`${BACKEND_URL}/api/letter-preview/preview-generation-plan`, {
          method: 'POST',
          signal: abortController.signal,
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            bureaus: normalizeBureausList(['Equifax', 'Experian', 'TransUnion']),
            dispute_items: disputeItems,
            decision_output: {
              recommended_method: aiDecision.recommended_method,
              strategy_recommendation: aiDecision.strategy_recommendation,
              scenario_tags: aiDecision.scenario_tags || [],
              generation_config: aiDecision.generation_config || {},
            },
            // CRITICAL: Pass method_resolution for strict package scoping
            method_resolution: methodResolution ? {
              is_mixed: methodResolution.summary?.is_mixed || false,
              primary_method: methodResolution.summary?.primary_method,
              unique_method_count: methodResolution.summary?.unique_method_count || 1,
              method_groups: methodResolution.method_groups || [],
              item_assignments: methodResolution.item_assignments || {},
            } : null,
            round_number: resolvedRoundNumber, // Use resolved round number from sent history
            tenant_id: user?.tenant_id || null,
            // Full item data for evidence views
            full_account_data: negativeAccounts.filter(a => proceedingSelectedItems.accounts.includes(a.id)),
            full_inquiry_data: hardInquiries.filter(i => proceedingSelectedItems.inquiries.includes(i.id)),
            full_pi_data: personalInfoItems.filter(pi => proceedingSelectedItems.personalInfo?.includes(pi.id)),
            pi_anchor_confirmations: piAnchorConfirmations,
            // SOURCE REPORT LOCKING: Pass the selected report ID
            selected_report_id: selectedReportId,
            is_report_locked: isReportLocked,
            selection_signature: selectionSignature,
          }),
        });
        
        if (!response.ok) {
          throw new Error(`Failed to fetch generation plan: ${response.status}`);
        }
        
        const planData = await response.json();
        if (selectionSignature !== inFlightPreviewSignatureRef.current) {
          if (DEBUG_DISPUTE_SELECTION) {
            console.log('[GENERATION PLAN PREVIEW] Ignored stale response', {
              responseSignature: selectionSignature,
              currentSignature: inFlightPreviewSignatureRef.current,
            });
          }
          return;
        }
        setGenerationPlanPreview(planData);
        lastSuccessfulPlanPreviewRef.current = planData;
        lastPreviewSignatureRef.current = selectionSignature;
        setDisputePlanSummaryStatus(
          (planData.packages?.length || 0) > 0 ? 'success' : 'empty'
        );
        if ((planData.packages?.length || 0) > 4) {
          setDisputePlanSummaryCollapsed(true);
        }
        
        if (DEBUG_DISPUTE_SELECTION) {
          console.debug('[GENERATION PLAN PREVIEW]', {
            packageCount: planData.packages?.length || 0,
            promptManaged: planData.generation_summary?.prompt_managed_count,
          });
        }

        if (planData.generation_summary?.missing_prompts?.length) {
          toast.error(
            `${planData.generation_summary.missing_prompts.length} package(s) missing prompts in Prompt Management`,
            { duration: 5000 }
          );
        } else if (planData.generation_summary?.errors?.length) {
          toast.error(planData.generation_summary.errors[0], { duration: 5000 });
        }
        
      } catch (error) {
        if (error?.name === 'AbortError') return;
        console.error('[GENERATION PLAN PREVIEW] Error:', error);
        setDisputePlanSummaryError(
          'Unable to prepare the dispute plan summary. Your selected items are still preserved.'
        );
        setDisputePlanSummaryStatus(
          lastSuccessfulPlanPreviewRef.current ? 'success' : 'error'
        );
        if (lastSuccessfulPlanPreviewRef.current && !generationPlanPreview) {
          setGenerationPlanPreview(lastSuccessfulPlanPreviewRef.current);
        }
      } finally {
        if (summaryFailSafeTimeoutRef.current) {
          clearTimeout(summaryFailSafeTimeoutRef.current);
          summaryFailSafeTimeoutRef.current = null;
        }
        if (inFlightPreviewSignatureRef.current === selectionSignature) {
          inFlightPreviewSignatureRef.current = '';
          setGenerationPlanLoading(false);
        }
      }
    };
    
    fetchGenerationPlanPreview();
    return () => {
      if (previewAbortControllerRef.current) {
        previewAbortControllerRef.current.abort();
        previewAbortControllerRef.current = null;
      }
      if (summaryWaitTimeoutRef.current) {
        clearTimeout(summaryWaitTimeoutRef.current);
        summaryWaitTimeoutRef.current = null;
      }
      if (summaryFailSafeTimeoutRef.current) {
        clearTimeout(summaryFailSafeTimeoutRef.current);
        summaryFailSafeTimeoutRef.current = null;
      }
    };
  }, [
    currentStep,
    aiDecision,
    selectionSignature,
    proceedingSelectedItems,
    negativeAccounts,
    hardInquiries,
    personalInfoItems,
    token,
    user,
    methodResolution,
    methodResolutionLoading,
    piAnchorConfirmations,
    selectedReportId,
    isReportLocked,
    accountById,
    inquiryById,
    personalInfoById,
    summaryRetryNonce,
  ]);

  // Handle round creation
  const handleCreateRound = async () => {
    const reviewSnapshot = {
      totalItems: resolvedGenerationSummary.totalItems,
      totalPackages: resolvedGenerationSummary.totalPackages,
      totalLetters: resolvedGenerationSummary.totalLetters,
      packageMethods: (methodResolution?.method_groups || []).map((group) => group.method_key),
      packageCounts: resolvedGenerationSummary.packageTotals || {},
      effectiveSelectedItems: proceedingSelectedItems,
      methodResolution: methodResolution
        ? {
            is_mixed: methodResolution.summary?.is_mixed || false,
            primary_method: methodResolution.summary?.primary_method,
            unique_method_count: methodResolution.summary?.unique_method_count || 1,
            method_groups: (methodResolution.method_groups || []).map((g) => g.method_key),
          }
        : null,
      itemAssignments: methodResolution?.item_assignments || {},
      generationPlanPreviewPackages: generationPlanPreview?.packages?.length || 0,
    };
    reviewPlanSnapshotRef.current = reviewSnapshot;
    console.log('[REVIEW PLAN FINAL]', reviewSnapshot);

    setLoading(true);
    try {
      // Build consumer data
      const consumerData = {
        full_name: reportData?.personal_info?.name || user?.name || '',
        ssn_last4: reportData?.personal_info?.ssn_last4 || '',
        date_of_birth: reportData?.personal_info?.dob || '',
        current_address: reportData?.personal_info?.current_address || 
          (reportData?.addresses?.[0]?.full_address) || '',
      };
      
      // Build AI Decision payload if available
      const aiDecisionPayload = aiDecision ? {
        recommended_method: aiDecision.recommended_method,
        strategy_recommendation: aiDecision.strategy_recommendation,
        allowed_methods: aiDecision.allowed_methods,
        blocked_methods: aiDecision.blocked_methods,
        signals: aiDecision.signals,
        warnings: aiDecision.warnings,
        target_bureaus: aiDecision.target_bureaus,
        generation_config: aiDecision.generation_config,
      } : null;
      
      // Filter out protected PI anchors from the dispute list
      const protectedPIIds = piAnchorConfirmations.map(conf => conf.correct_item_id);
      const disputedPIIds = proceedingSelectedItems.personalInfo.filter(id => !protectedPIIds.includes(id));
      console.log('[handleCreateRound] Protected PI IDs:', protectedPIIds);
      console.log('[handleCreateRound] Disputed PI IDs:', disputedPIIds);
      
      // Create round
      const createResult = await metro2Service.createRound({
        accountIds: proceedingSelectedItems.accounts,
        inquiryIds: proceedingSelectedItems.inquiries,
        personalInfoIds: disputedPIIds,  // NEW: Pass PI IDs (excluding protected anchors)
        userId: user?.user_id || user?.id,
        tier: user?.tier || 'auto_starter',
        strategyMode: resolvedDeliveryFormat === 'combined' ? 'bureau_package' : 'single_item',
        provider: reportData?.provider || 'identityiq',
        reportData: {
          tradelines: negativeAccounts.filter(a => proceedingSelectedItems.accounts.includes(a.id)),
          inquiries: hardInquiries.filter(i => proceedingSelectedItems.inquiries.includes(i.id)),
          personal_info: personalInfoItems.filter(pi => disputedPIIds.includes(pi.id)),  // NEW: Include PI data
        },
        consumerData,
        disputeContext,
        disputeType,
        // NEW: Pass AI Decision to backend for strategy-specific letter generation
        aiDecision: aiDecisionPayload,
        // NEW: Pass PI anchor protection data
        piAnchorConfirmations: piAnchorConfirmations,
      }, token);
      
      if (!createResult.success) {
        throw new Error(createResult.message || 'Failed to create round');
      }
      
      // Process round
      const processResult = await metro2Service.processRound(
        createResult.round_id,
        user?.user_id || user?.id,
        token
      );
      
      if (!processResult.success) {
        throw new Error(processResult.message || 'Failed to process round');
      }
      
      setRoundData({
        ...createResult,
        ...processResult,
      });

      trackWizardStep({
        metro2Service,
        roundId: createResult.round_id,
        token,
        currentStep: 'select_items',
        wizardStatus: 'draft',
      });

      step3GenerationPayloadRef.current = null;
      setRegenerationNonce((n) => n + 1);
      try {
        sessionStorage.removeItem(FINAL_PACKAGE_STORAGE_KEY);
        localStorage.removeItem(ACTIVE_PACKAGE_STORAGE_KEY);
        Object.keys(sessionStorage)
          .filter((key) => key.startsWith('generation_') || key.startsWith('dispute_wizard_'))
          .forEach((key) => sessionStorage.removeItem(key));
      } catch (_) {
        // ignore storage errors
      }
      
      setCurrentStep(3);
      
    } catch (error) {
      console.error('Create round error:', error);
      toast.error(error.message || 'Failed to create dispute round');
    } finally {
      setLoading(false);
    }
  };

  // Handle letter generation
  const handleGenerateLetters = async () => {
    // Instead of generating inline, navigate to the preview page with selected items
    const selectedAccountsList = negativeAccounts.filter(acc => proceedingSelectedItems.accounts.includes(acc.id));
    const selectedInquiriesList = hardInquiries.filter(inq => proceedingSelectedItems.inquiries.includes(inq.id));
    const selectedPIList = personalInfoItems.filter(pi => proceedingSelectedItems.personalInfo.includes(pi.id));
    
    // Use AI Decision Engine result if available, otherwise fall back to URL path
    const pathType = searchParams.get('type') || (location.pathname.includes('metro2') ? 'metro2' : 'factual');
    
    // Determine actual letter type based on AI decision or URL
    let effectiveLetterType = pathType;
    
    // PRIORITY 1: Use selectedMethod from smart routing (inquiry/personal info flows)
    if (selectedMethod === 'data_breach') {
      effectiveLetterType = 'data_breach';
    } else if (aiDecision?.recommended_method) {
      // Map backend method to letter type
      const methodToLetterType = {
        'factual_inaccuracy': 'factual',
        'meth_factual_inaccuracy': 'factual',
        'debt_validation': 'debt_validation',
        'meth_debt_validation': 'debt_validation',
        'metro2_technical': 'metro2',
        'meth_metro2_technical': 'metro2',
        'unauthorized_inquiry': 'inquiry',
        'meth_unauthorized_inquiry': 'inquiry',
        'identity_theft': 'identity_theft',
        'meth_identity_theft': 'identity_theft',
        'goodwill_adjustment': 'goodwill',
        'meth_goodwill_adjustment': 'goodwill',
        // DATA BREACH METHOD
        'data_breach_method': 'data_breach',
        'data_breach': 'data_breach',
        'meth_data_breach': 'data_breach',
      };
      effectiveLetterType = methodToLetterType[aiDecision.recommended_method] || pathType;
    }
    
    const bureaus = normalizeBureausList([...new Set([
      ...selectedAccountsList.flatMap(a => a.bureaus || [a.bureau || 'Equifax']),
      ...selectedInquiriesList.map(i => i.bureau || 'Equifax'),
      ...selectedPIList.flatMap(pi => pi.bureaus || ['TransUnion', 'Experian', 'Equifax'])
    ])]);
    
    // Get delivery format from params or default to combined (self-repair always combined)
    const deliveryFormatParam = resolvedDeliveryFormat;
    
    // Build the AI Decision payload to pass through
    const decisionPayload = aiDecision ? {
      recommended_method: aiDecision.recommended_method,
      allowed_methods: aiDecision.allowed_methods,
      blocked_methods: aiDecision.blocked_methods,
      strategy_recommendation: aiDecision.strategy_recommendation,
      signals: aiDecision.signals,
      warnings: aiDecision.warnings,
      target_bureaus: aiDecision.target_bureaus,
      review_required: aiDecision.review_required,
      review_reason: aiDecision.review_reason,
    } : (selectedMethod === 'data_breach' ? {
      // Create AI decision payload for data_breach from smart routing
      recommended_method: 'data_breach_method',
      allowed_methods: ['data_breach_method'],
      blocked_methods: [],
      strategy_recommendation: {
        title: 'Data Breach Method',
        key: 'data_breach_method',
        reason: 'Inquiries/Personal Info default to Data Breach Method'
      },
      signals: [],
      warnings: [],
      target_bureaus: bureaus,
      review_required: false,
      review_reason: null,
    } : null);
    
    // Navigate to preview page with all the context INCLUDING AI decision
    navigate(`/dispute-wizard/${effectiveLetterType}/preview`, {
      state: {
        selectedItems: [
          ...selectedAccountsList.map(acc => ({
            type: 'account',
            id: acc.id,
            creditor: acc.creditorName || acc.creditor_name || acc.account_name || acc.name,
            account_number: acc.accountNumber || acc.account_number || '',
            bureau_data: acc.bureau_data || {},
            bureau_comparison: acc.bureau_comparison || {},
            payment_history_grid: acc.payment_history_grid || {},
            balance: acc.balance || acc.currentBalance || '$0.00',
            account_type: acc.accountType || acc.account_type || 'Revolving',
            account_status: acc.accountStatus || acc.payment_status || 'Collection/Charge-off',
            payment_status: acc.paymentStatus || acc.payment_status || '',
            bureau: normalizeBureau(acc.bureau || acc.bureaus?.[0] || 'Equifax'),
            // FIX: Use actual bureaus from account - do NOT fall back to all bureaus
            bureaus: normalizeBureausList(acc.bureaus_reported || acc.bureaus || (acc.bureau ? [acc.bureau] : [])),
            bureaus_reported: acc.bureaus_reported || acc.bureaus || (acc.bureau ? [acc.bureau] : []),
            reason: disputeContext || 'Inaccurate information',
            // Include per-item method assignment
            methodAssignment: methodResolution?.item_assignments?.[acc.id] || null,
          })),
          ...selectedInquiriesList.map(inq => ({
            type: 'inquiry',
            id: inq.id,
            creditor: inq.creditor || inq.company_name || inq.inquiry_name || 'Unknown',
            date: inq.date || inq.inquiry_date || '',
            bureau: normalizeBureau(inq.bureau || 'Equifax'),
            // FIX: Inquiries are single-bureau
            bureaus: inq.bureau ? [normalizeBureau(inq.bureau)] : [],
            bureaus_reported: inq.bureau ? [normalizeBureau(inq.bureau)] : [],
            reason: 'Unauthorized inquiry',
            // Include per-item method assignment
            methodAssignment: methodResolution?.item_assignments?.[inq.id] || null,
          })),
          // Include personal info items with their method assignments
          // Exclude items that are protected as anchors
          ...(() => {
            const protectedIds = piAnchorConfirmations.map(conf => conf.correct_item_id);
            console.log('[PI ANCHOR] handleGenerateLetters - protected IDs:', protectedIds);
            console.log('[PI ANCHOR] handleGenerateLetters - selectedPIList count:', selectedPIList.length);
            const filteredPI = selectedPIList.filter(pi => !protectedIds.includes(pi.id));
            console.log('[PI ANCHOR] handleGenerateLetters - filtered PI count:', filteredPI.length);
            return filteredPI.map(pi => ({
              type: 'personal_info',
              id: pi.id,
              field: pi.field,
              fieldLabel: pi.fieldLabel,
              values: pi.values,
              // FIX: Use actual bureaus from PI item - do NOT fall back to all bureaus
              bureaus: normalizeBureausList(pi.bureaus || []),
              bureaus_reported: pi.bureaus || [],
              reason: 'Inaccurate personal information',
              methodAssignment: methodResolution?.item_assignments?.[pi.id] || null,
            }));
          })()
        ],
        bureaus: bureaus.length > 0 ? bureaus : ['Equifax'],
        deliveryFormat: deliveryFormatParam,
        letterType: effectiveLetterType,
        // Pass the full AI Decision Engine result
        aiDecision: decisionPayload,
        // Pass method resolution for mixed-method handling
        methodResolution: methodResolution,
        // Keep URL-based type as fallback reference
        urlBasedType: pathType,
        violationType: searchParams.get('violation') || 'payment_history_chargeoff',
        furnisher: selectedAccountsList[0]?.creditorName || selectedAccountsList[0]?.creditor_name || selectedAccountsList[0]?.account_name || '',
        roundId: roundData?.round_id,
        contextNotes: disputeContext,
        // PI Anchor Protection data
        piAnchorConfirmations: piAnchorConfirmations,
        protectedPIAnchors: piAnchorConfirmations.map(conf => ({
          category: conf.category,
          item_id: conf.correct_item_id,
          value: conf.correct_value,
          has_supporting_docs: conf.has_supporting_docs,
        })),
      }
    });
    
    // SESSION RECOVERY: Save state to sessionStorage as backup for page refresh
    try {
      const sessionKey = `letterPreview_${effectiveLetterType}_recent`;
      sessionStorage.setItem(sessionKey, JSON.stringify({
        timestamp: Date.now(),
        letterType: effectiveLetterType,
        bureaus: bureaus.length > 0 ? bureaus : ['Equifax'],
        itemCount: selectedAccountsList.length + selectedInquiriesList.length + selectedPIList.length,
        hasMethodResolution: !!methodResolution,
      }));
      console.log('[SESSION RECOVERY] Saved navigation state to:', sessionKey);
    } catch (e) {
      console.warn('[SESSION RECOVERY] Failed to save state:', e);
    }
  };

  // Download letter
  const handleDownload = (packageData) => {
    if (!letterContent?.content) {
      toast.error('Letter content not available');
      return;
    }
    
    const blob = new Blob([letterContent.content], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dispute_letter_${packageData?.target_bureau || 'bureau'}_${roundData?.round_id?.slice(0, 8)}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Letter downloaded');
  };

  // Navigation
  const goNext = async () => {
    if (currentStep === 0 && proceedingItemsCount === 0) {
      toast.error('Please select at least one item to dispute');
      return;
    }
    
    // When leaving step 0 (item selection), check PI anchor requirements first
    if (currentStep === 0) {
      // Check if PI items are selected and if anchor confirmation is needed
      const selectedPIList = personalInfoItems.filter(pi => proceedingSelectedItems.personalInfo.includes(pi.id));
      
      if (selectedPIList.length > 0) {
        // Group selected PI by anchor category
        const selectedByCategory = {};
        selectedPIList.forEach(item => {
          const category = categorizeItem(item);
          if (!selectedByCategory[category]) {
            selectedByCategory[category] = [];
          }
          selectedByCategory[category].push(item);
        });
        
        // Check which anchor categories need confirmation
        const categoriesNeedingConfirmation = ANCHOR_CATEGORIES.filter(cat => 
          selectedByCategory[cat]?.length > 0
        );
        
        // Check if we have all needed confirmations
        const missingConfirmations = categoriesNeedingConfirmation.filter(cat => {
          const hasConfirmation = piAnchorConfirmations.some(
            conf => conf.category === cat && conf.correct_item_id
          );
          return !hasConfirmation;
        });
        
        if (missingConfirmations.length > 0) {
          // Show PI anchor modal
          setShowPIAnchorModal(true);
          return;
        }
      }
      
      // PI validation passed, proceed with AI Decision Engine
      setDecisionLoading(true);
      try {
        // Build selected items for decision engine
        const selectedAccountData = negativeAccounts
          .filter(a => proceedingSelectedItems.accounts.includes(a.id))
          .map(tradeline => ({
            type: 'account',
            tradeline,
            bureauScope: ['TU', 'EX', 'EQ'].filter(b => 
              tradeline[b.toLowerCase()] || tradeline[`${b.toLowerCase()}_reported`]
            ),
          }));
        
        const selectedInquiryData = hardInquiries
          .filter(i => proceedingSelectedItems.inquiries.includes(i.id))
          .map(inquiry => ({
            type: 'inquiry',
            inquiry,
          }));
        
        const allSelectedItems = [...selectedAccountData, ...selectedInquiryData];
        
        // Call the AI Decision Engine
        adminOpsService.setAuthHeaders(getAuthHeaders());
        const decision = await adminOpsService.evaluateDecision({
          report_id: reportData?.report_id,
          selected_items: allSelectedItems,
          user_context: { user_id: user?.id },
          package_context: { dispute_type: disputeType }
        });
        
        setAiDecision(decision);
        
        // Show any critical warnings
        const criticalWarnings = (decision.warnings || []).filter(w => w.severity === 'critical');
        if (criticalWarnings.length > 0) {
          toast.warning(`${criticalWarnings.length} critical warning(s) detected. Please review before proceeding.`, {
            duration: 5000
          });
        }
        
        // Show recommendation
        if (decision.recommended_method) {
          toast.success(`AI recommends: ${decision.strategy_recommendation?.title || decision.recommended_method}`, {
            duration: 3000
          });
        }
        
      } catch (error) {
        console.error('Decision engine error:', error);
        // Don't block the flow - just log the error
        toast.info('Proceeding without AI recommendations');
      } finally {
        setDecisionLoading(false);
      }
      
      setCurrentStep(prev => prev + 1);
      return;
    }
    
    if (currentStep === 2) {
      handleCreateRound();
    } else if (currentStep === 3) {
      handleGenerateLetters();
    } else if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };
  
  // Handle PI Anchor confirmation from modal
  const handlePIAnchorConfirm = (confirmations) => {
    console.log('[PI ANCHOR] handlePIAnchorConfirm called with:', JSON.stringify(confirmations));
    
    // Store confirmations immediately for use
    const storedConfirmations = confirmations;
    
    // Update state
    setPIAnchorConfirmations(confirmations);
    setShowPIAnchorModal(false);
    
    // Show success message
    const categoryLabels = confirmations.map(c => CATEGORY_INFO[c.category]?.label || c.category);
    toast.success(`Protected anchors confirmed: ${categoryLabels.join(', ')}`, {
      description: 'Your correct personal information will be preserved',
      duration: 3000,
    });
    
    // CRITICAL: Auto-proceed using the confirmations we just received (not from state)
    // Use a callback-based approach to ensure state is updated before proceeding
    setTimeout(() => {
      console.log('[PI ANCHOR] Auto-proceeding after confirmation');
      // Force re-render to pick up new state, then call goNext
      goNextAfterAnchorConfirm(storedConfirmations);
    }, 150);
  };
  
  // Special goNext that uses passed confirmations instead of state (to avoid stale closure)
  const goNextAfterAnchorConfirm = async (confirmedAnchors) => {
    console.log('[PI ANCHOR] goNextAfterAnchorConfirm with:', JSON.stringify(confirmedAnchors));
    
    // Skip PI validation since we just confirmed
    // Proceed directly with AI Decision Engine
    setDecisionLoading(true);
    try {
      // Build selected items for decision engine
      const selectedAccountData = negativeAccounts
        .filter(a => proceedingSelectedItems.accounts.includes(a.id))
        .map(tradeline => ({
          type: 'account',
          tradeline,
          bureauScope: ['TU', 'EX', 'EQ'].filter(b => 
            tradeline[b.toLowerCase()] || tradeline[`${b.toLowerCase()}_reported`]
          ),
        }));
      
      const selectedInquiryData = hardInquiries
        .filter(i => proceedingSelectedItems.inquiries.includes(i.id))
        .map(inquiry => ({
          type: 'inquiry',
          inquiry,
        }));
      
      const allSelectedItems = [...selectedAccountData, ...selectedInquiryData];
      
      // Call the AI Decision Engine
      adminOpsService.setAuthHeaders(getAuthHeaders());
      const decision = await adminOpsService.evaluateDecision({
        report_id: reportData?.report_id,
        selected_items: allSelectedItems,
        user_context: { user_id: user?.id },
        package_context: { dispute_type: disputeType }
      });
      
      setAiDecision(decision);
      
      // Show any critical warnings
      const criticalWarnings = (decision.warnings || []).filter(w => w.severity === 'critical');
      if (criticalWarnings.length > 0) {
        toast.warning(`${criticalWarnings.length} critical warning(s) detected. Please review before proceeding.`, {
          duration: 5000
        });
      }
      
      // Show recommendation
      if (decision.recommended_method) {
        toast.success(`AI recommends: ${decision.strategy_recommendation?.title || decision.recommended_method}`, {
          duration: 3000
        });
      }
      
    } catch (error) {
      console.error('Decision engine error:', error);
      // Don't block the flow - just log the error
      toast.info('Proceeding without AI recommendations');
    } finally {
      setDecisionLoading(false);
    }
    
    setCurrentStep(prev => prev + 1);
  };

  const goBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    } else {
      navigate('/dispute-wizard');
    }
  };

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return renderSelectItems();
      case 1:
        return renderAddContext();
      case 2:
        return renderReview();
      case 3:
        return renderGenerating();
      case 4:
        return renderComplete();
      default:
        return null;
    }
  };

  // Step 0: Select Items
  const renderSelectItems = () => (
    <div className="space-y-6" data-testid="step-select-items">
      {/* Source Report Selection - MUST be first for clarity */}
      <ReportSelector 
        showCompact={true}
        onReportSelected={(reportId) => {
          console.log('[ITEM SELECTION] Report selected:', reportId);
          // Clear any cached method resolutions when report changes
          setMethodResolution(null);
        }}
        onReportLocked={(reportId) => {
          console.log('[ITEM SELECTION] Report locked:', reportId);
        }}
        className="mb-4"
      />
      
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-stone-900 dark:text-[#EAF7F0]">
            Select Items to Dispute
          </h2>
          <p className="text-stone-600 dark:text-[#C7DDD0] text-sm mt-1">
            Choose the accounts and inquiries you want to include in your {disputeType === 'metro2' ? 'Metro 2' : 'Factual'} dispute.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={allEligibleSelected ? clearAll : selectAll}
            data-testid="select-all-toggle"
          >
            {allEligibleSelected ? 'Deselect All' : 'Select All'}
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
        <Input
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search accounts and inquiries..."
          className="pl-10"
        />
      </div>

      {/* Negative Accounts */}
      <div>
        <h3 className="font-semibold text-stone-900 dark:text-[#EAF7F0] mb-3 flex items-center gap-2">
          <CreditCard size={18} className="text-red-500" />
          Negative Accounts ({filteredEligibleAccounts.length})
        </h3>
        {filteredEligibleAccounts.length > 0 ? (
          <div className="grid gap-3">
            {filteredEligibleAccounts.map(account => (
              <AccountItemCard
                key={account.id}
                item={account}
                type="account"
                isSelected={selectedItems.accounts.includes(account.id)}
                onToggle={(id) => toggleItem('account', id)}
                methodAssignment={methodResolution?.item_assignments?.[account.id]}
              />
            ))}
          </div>
        ) : (
          <p className="text-stone-500 dark:text-[#93AE9F] text-sm py-4 text-center">
            No negative accounts found in your report.
          </p>
        )}
      </div>

      {/* Hard Inquiries */}
      <div>
        <h3 className="font-semibold text-stone-900 dark:text-[#EAF7F0] mb-3 flex items-center gap-2">
          <Search size={18} className="text-amber-500" />
          Hard Inquiries ({filteredEligibleInquiries.length})
        </h3>
        {filteredEligibleInquiries.length > 0 ? (
          <div className="grid gap-3">
            {filteredEligibleInquiries.map(inquiry => (
              <AccountItemCard
                key={inquiry.id}
                item={inquiry}
                type="inquiry"
                isSelected={selectedItems.inquiries.includes(inquiry.id)}
                onToggle={(id) => toggleItem('inquiry', id)}
                methodAssignment={methodResolution?.item_assignments?.[inquiry.id]}
              />
            ))}
          </div>
        ) : (
          <p className="text-stone-500 dark:text-[#93AE9F] text-sm py-4 text-center">
            No hard inquiries found in your report.
          </p>
        )}
      </div>

      <ExcludedItemsSection excludedItems={excludedDisplayedItems} />

      {/* Personal Information Section */}
      {eligiblePersonalInfo.length > 0 && (
        <div className="bg-white dark:bg-[rgba(10,38,28,0.65)] rounded-xl border border-stone-200 dark:border-[rgba(142,218,182,0.15)] p-4" data-testid="personal-info-section">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-stone-900 dark:text-[#EAF7F0] flex items-center gap-2">
              <User size={16} className="text-emerald-600" />
              Personal Information ({eligiblePersonalInfo.length})
            </h4>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                hasInitializedSelectionRef.current = true;
                const allPiSelected = eligiblePersonalInfo.every((pi) => selectedItems.personalInfo.includes(pi.id));
                if (allPiSelected) {
                  setSelectedItems((prev) => ({ ...prev, personalInfo: [] }));
                } else {
                  setSelectedItems((prev) => ({
                    ...prev,
                    personalInfo: eligiblePersonalInfo.map((pi) => pi.id),
                  }));
                }
              }}
              className="text-stone-600 dark:text-[#C7DDD0] hover:text-stone-900 dark:hover:text-[#EAF7F0]"
            >
              {eligiblePersonalInfo.every((pi) => selectedItems.personalInfo.includes(pi.id)) && eligiblePersonalInfo.length > 0
                ? 'Deselect All'
                : 'Select All'}
            </Button>
          </div>
          
          <div className="space-y-2">
            {personalInfoItems
              .filter(piItem => 
                !searchTerm || 
                piItem.fieldLabel?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                Object.values(piItem.values || {}).some(v => 
                  v && (Array.isArray(v) 
                    ? v.some(val => String(val).toLowerCase().includes(searchTerm.toLowerCase()))
                    : String(v).toLowerCase().includes(searchTerm.toLowerCase())
                  )
                )
              )
              .map((piItem) => {
                const isSelected = selectedItems.personalInfo.includes(piItem.id);
                const itemCategory = categorizeItem(piItem);
                const isAnchorCategory = ANCHOR_CATEGORIES.includes(itemCategory);
                const isProtectedAnchor = piAnchorConfirmations.some(
                  conf => conf.correct_item_id === piItem.id
                );
                const categoryInfo = CATEGORY_INFO[itemCategory];
                
                return (
                  <div 
                    key={piItem.id}
                    className={`flex items-start gap-3 p-3 rounded-lg border transition-all cursor-pointer ${
                      isProtectedAnchor
                        ? 'border-emerald-400 bg-emerald-50 ring-1 ring-emerald-200'
                        : isSelected 
                          ? 'border-emerald-300 bg-emerald-50' 
                          : 'border-stone-200 bg-white hover:border-stone-300'
                    }`}
                    onClick={() => {
                      // Prevent deselecting protected anchors
                      if (isProtectedAnchor) {
                        toast.info('This is your protected anchor - it cannot be disputed', {
                          description: 'Change your anchor selection using the Continue button flow',
                          duration: 3000,
                        });
                        return;
                      }
                      
                      // Show employer warning when DESELECTING an employer item
                      if (isSelected && isEmployerItem(piItem) && hasEmployerData(piItem)) {
                        setEmployerItemPendingRemoval(piItem);
                        setShowEmployerWarning(true);
                        return;
                      }
                      
                      setSelectedItems(prev => ({
                        ...prev,
                        personalInfo: isSelected
                          ? prev.personalInfo.filter(id => id !== piItem.id)
                          : [...prev.personalInfo, piItem.id]
                      }));
                      hasInitializedSelectionRef.current = true;
                    }}
                    data-testid={`personal-info-item-${piItem.id}`}
                  >
                    <Checkbox 
                      checked={isSelected || isProtectedAnchor}
                      disabled={isProtectedAnchor}
                      className={`mt-1 ${isProtectedAnchor ? 'opacity-50' : ''}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-medium text-stone-900 dark:text-[#EAF7F0]">{piItem.fieldLabel}</span>
                        
                        {/* Protected Anchor Badge */}
                        {isProtectedAnchor && (
                          <Badge 
                            variant="outline" 
                            className="bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border-emerald-300 dark:border-emerald-700 text-xs"
                          >
                            <Shield size={10} className="mr-1" />
                            Protected Anchor
                          </Badge>
                        )}
                        
                        {/* Employer Recommendation Badge */}
                        {!isProtectedAnchor && isEmployerItem(piItem) && hasEmployerData(piItem) && (
                          <Badge 
                            variant="outline" 
                            className="bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border-amber-300 dark:border-amber-700 text-xs"
                            data-testid="employer-recommended-badge"
                          >
                            <AlertTriangle size={10} className="mr-1" />
                            Recommended to Dispute
                          </Badge>
                        )}
                        
                        {/* Anchor Category Indicator (for non-protected items) */}
                        {!isProtectedAnchor && isAnchorCategory && isSelected && (
                          <Badge 
                            variant="outline" 
                            className={`text-xs ${categoryInfo?.bgColor || 'bg-amber-50 dark:bg-amber-900/30'} ${categoryInfo?.color || 'text-amber-700 dark:text-amber-300'} ${categoryInfo?.borderColor || 'border-amber-200 dark:border-amber-700'}`}
                          >
                            <AlertTriangle size={10} className="mr-1" />
                            Requires Anchor
                          </Badge>
                        )}
                        
                        {/* Personal Info badge (when not anchor-related and not employer) */}
                        {!isProtectedAnchor && !isAnchorCategory && !isEmployerItem(piItem) && (
                          <Badge 
                            variant="outline" 
                            className="bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-700 text-xs"
                          >
                            Personal Info
                          </Badge>
                        )}
                        
                        {/* Show assigned method badge when selected */}
                        {isSelected && !isProtectedAnchor && methodResolution?.item_assignments?.[piItem.id] && (
                          <Badge 
                            variant="outline" 
                            className={`text-xs ${
                              methodResolution.item_assignments[piItem.id].assigned_method === 'data_breach_method' 
                                ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border-purple-300 dark:border-purple-700'
                                : methodResolution.item_assignments[piItem.id].assigned_method === 'personal_info_accuracy'
                                ? 'bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 border-teal-300 dark:border-teal-700'
                                : methodResolution.item_assignments[piItem.id].assigned_method === 'factual_inaccuracy'
                                ? 'bg-stone-100 dark:bg-stone-800/50 text-stone-600 dark:text-stone-300 border-stone-300 dark:border-stone-600'
                                : 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border-emerald-300 dark:border-emerald-700'
                            }`}
                          >
                            {methodResolution.item_assignments[piItem.id].method_badge}
                          </Badge>
                        )}
                      </div>
                      
                      {/* Bureau-specific values */}
                      <div className="grid grid-cols-3 gap-2 mt-2">
                        {['TransUnion', 'Experian', 'Equifax'].map(bureau => {
                          const val = piItem.values?.[bureau];
                          const hasValue = val && (Array.isArray(val) ? val.length > 0 : true);
                          return (
                            <div key={bureau} className="text-xs">
                              <div className={`font-medium mb-0.5 ${BUREAU_COLORS[bureau]?.split(' ')[1] || 'text-stone-600 dark:text-[#C7DDD0]'}`}>
                                {bureau}
                              </div>
                              {hasValue ? (
                                <div className="text-stone-600 dark:text-[#C7DDD0] truncate">
                                  {Array.isArray(val) 
                                    ? val.slice(0, 2).join('; ') + (val.length > 2 ? '...' : '')
                                    : String(val).substring(0, 50) + (String(val).length > 50 ? '...' : '')
                                  }
                                </div>
                              ) : (
                                <div className="text-stone-400 dark:text-[#93AE9F] italic">—</div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      
                      {/* Employer Education Copy */}
                      {isEmployerItem(piItem) && hasEmployerData(piItem) && (
                        <div className="mt-2 p-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/50 rounded-md" data-testid="employer-education-copy">
                          <div className="flex items-start gap-2">
                            <Info size={12} className="text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                            <p className="text-xs text-amber-700 dark:text-amber-300 leading-relaxed">
                              Employer info may affect file matching and verification. Consider disputing for a cleaner personal-info foundation.
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* Selection Summary */}
      {proceedingItemsCount > 0 && (
        <div className="bg-emerald-50 dark:bg-[rgba(10,38,28,0.75)] border border-emerald-200 dark:border-[rgba(52,211,153,0.20)] rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-semibold text-emerald-900 dark:text-[#EAF7F0]">
                {proceedingItemsCount} item{proceedingItemsCount !== 1 ? 's' : ''} selected
              </h4>
              {Object.keys(bureauCounts).length > 0 && (
                <div className="flex gap-2 mt-1 flex-wrap">
                  {Object.entries(bureauCounts).map(([bureau, count]) => (
                    <Badge key={bureau} variant="outline" className={BUREAU_COLORS[bureau]}>
                      {bureau}: {count}
                    </Badge>
                  ))}
                </div>
              )}
              {/* Show letters count separately if needed */}
              {Object.keys(bureauCounts).length > 1 && (
                <p className="text-xs text-emerald-700 dark:text-[#7FD7A8] mt-1">
                  {Object.keys(bureauCounts).length} bureaus → {Object.keys(bureauCounts).length} letters
                </p>
              )}
            </div>
            <Button 
              onClick={goNext} 
              className="bg-emerald-600 hover:bg-emerald-700"
              disabled={decisionLoading || proceedingItemsCount === 0}
            >
              {decisionLoading ? (
                <>
                  <Loader2 size={16} className="mr-1 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  Continue
                  <ChevronRight size={16} className="ml-1" />
                </>
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );

  // Step 1: Add Context
  const renderAddContext = () => (
    <div className="space-y-6" data-testid="step-add-context">
      <div>
        <h2 className="text-xl font-bold text-stone-900 dark:text-[#EAF7F0]">Add Dispute Context</h2>
        <p className="text-stone-600 dark:text-[#C7DDD0] text-sm mt-1">
          Optional: Add any additional context that should be included in your dispute letters.
        </p>
      </div>

      {showDeliveryFormatChoice && (
        <DeliveryFormatSelector
          selectedFormat={deliveryFormat}
          onFormatChange={setDeliveryFormat}
          recommendedFormat="combined"
        />
      )}
    </div>
  );

  // Step 2: Review
  const renderReview = () => {
    // Bureau name normalization - ensures consistent naming and deduplication
    const normalizeBureauName = (name) => {
      if (!name) return '';
      const value = name.toString().trim().toLowerCase();
      const mapping = {
        'equifax': 'Equifax',
        'experian': 'Experian',
        'transunion': 'TransUnion',
        'trans union': 'TransUnion',
        'tu': 'TransUnion',
        'eq': 'Equifax',
        'ex': 'Experian',
      };
      return mapping[value] || name.trim();
    };
    
    // Build package split display from methodResolution (canonical methods only)
    const isMixedMethods = methodResolution?.summary?.is_mixed || false;
    const itemAssignments = methodResolution?.item_assignments || {};
    const warnings = methodResolution?.warnings || [];
    const excludedItems = excludedDisplayedItems;
    const blockedItems = methodResolution?.blocked_items || [];
    const methodsPending = methodResolutionLoading || (!methodResolution && proceedingItemsCount > 0);
    
    // Group items by canonical method for display — never by stale prompt titles
    const methodPackages = {};
    const proceedingIdSet = new Set([
      ...proceedingSelectedItems.accounts.map(String),
      ...proceedingSelectedItems.inquiries.map(String),
      ...proceedingSelectedItems.personalInfo.map(String),
    ]);

    const accountLookup = (itemId) =>
      accountById.get(String(itemId)) || negativeAccounts.find((i) => String(i.id) === String(itemId));
    const inquiryLookup = (itemId) =>
      inquiryById.get(String(itemId)) || hardInquiries.find((i) => String(i.id) === String(itemId));
    const piLookup = (itemId) =>
      personalInfoById.get(String(itemId)) || personalInfoItems.find((i) => String(i.id) === String(itemId));

    const proceedingRawItems = [
      ...proceedingSelectedItems.accounts.map((id) => {
        const account = accountLookup(id) || { id };
        return {
          ...account,
          id: String(id),
          ...detectCollectionSignals(account),
        };
      }),
      ...proceedingSelectedItems.inquiries.map((id) => ({ ...(inquiryLookup(id) || { id }), id: String(id) })),
      ...proceedingSelectedItems.personalInfo.map((id) => ({ ...(piLookup(id) || { id }), id: String(id) })),
    ];

    const mergedItems = methodsPending
      ? []
      : mergeAssignmentsByStableId(proceedingRawItems, itemAssignments);

    groupPackagesByCanonicalMethod(mergedItems).forEach((pkg) => {
      methodPackages[pkg.method_key] = {
        method_key: pkg.method_key,
        method_name: pkg.method_name,
        method_badge: pkg.method_key,
        items: pkg.items.map((item) => getStableDisputeItemId(item)),
        bureaus: new Set(),
        _rawItems: pkg.items,
      };
      pkg.items.forEach((item) => {
        const itemBureaus = item?.bureaus_reported || item?.bureaus;
        if (itemBureaus && Array.isArray(itemBureaus)) {
          itemBureaus.forEach((b) => {
            const normalized = normalizeBureauName(b);
            if (normalized) methodPackages[pkg.method_key].bureaus.add(normalized);
          });
        } else if (item?.bureau) {
          const normalized = normalizeBureauName(item.bureau);
          if (normalized) methodPackages[pkg.method_key].bureaus.add(normalized);
        }
      });
    });
    
    // Enrich packages with locked prompt metadata from generationPlanPreview
    // Display labels stay canonical — prompt titles never override package family.
    const packages = Object.values(methodPackages).map(pkg => {
      // Find matching preview package by method key
      const previewPkg = generationPlanPreview?.packages?.find(p => 
        normalizeMethodKey(p.method) === pkg.method_key
        || normalizeMethodKey(p.resolved_method) === pkg.method_key
      );
      
      // If we have locked prompt metadata, use it as source of truth
      if (previewPkg?.matched_prompt) {
        const mp = previewPkg.matched_prompt;
        return {
          ...pkg,
          display_name: getCanonicalPackageLabel(pkg.method_key),
          display_family: mp.family || mp.prompt_family || previewPkg.resolved_family || pkg.method_badge,
          display_method: pkg.method_key,
          display_round: mp.round || null,
          is_prompt_managed: true,
          locked_prompt: mp,
        };
      }
      
      // Fallback to canonical package labels
      return {
        ...pkg,
        display_name: getCanonicalPackageLabel(pkg.method_key),
        display_family: pkg.method_badge,
        display_method: pkg.method_key,
        display_round: null,
        is_prompt_managed: false,
        locked_prompt: null,
      };
    });
    
    // Prefer canonical expected package count from preview-generation-plan.
    // Final letter count equals preview packages.length (never method×bureau).
    const calculateGenerationPlanTotals = () => {
      const overallTotalItems = proceedingIdSet.size;
      const previewPackages = generationPlanPreview?.packages || [];
      const totalPackages = getFinalLetterCount(previewPackages)
        || generationPlanPreview?.expected_package_count
        || previewPackages.length
        || packages.length;
      const totalLetters = totalPackages;

      const packageTotals = {};
      const bureauBreakdowns = {};

      packages.forEach((pkg) => {
        const bureauCounts = {};
        Array.from(pkg.bureaus || []).forEach((bureau) => {
          bureauCounts[bureau] = (bureauCounts[bureau] || 0) + 1;
          bureauBreakdowns[bureau] = (bureauBreakdowns[bureau] || 0) + 1;
        });
        packageTotals[pkg.method_key] = {
          totalItems: pkg.items.length,
          bureauCount: (pkg.bureaus || new Set()).size,
          letterCount: (pkg.bureaus || new Set()).size,
          bureauCounts,
        };
      });
      
      if (DEBUG_DISPUTE_SELECTION) {
        console.log('[GEN PLAN TOTALS] Calculated from proceeding items:', {
          overallTotalItems,
          totalPackages,
          totalLetters,
          packageTotals,
          bureauBreakdowns,
        });
      }
      
      return {
        overallTotalItems,
        totalPackages,
        totalLetters,
        packageTotals,
        bureauBreakdowns,
      };
    };
    
    const genPlanTotals = calculateGenerationPlanTotals();
    const totalLetters = genPlanTotals.totalLetters;
    // Find display names for excluded/blocked items
    const getItemDisplayName = (itemId, excludedItem = null) => {
      if (excludedItem?.display_label) return excludedItem.display_label;

      let item = negativeAccounts.find(i => i.id === itemId);
      if (item) return item.creditor_name || item.account_name || itemId;
      
      item = hardInquiries.find(i => i.id === itemId);
      if (item) {
        const creditor = item.company_name || item.creditor_name || item.inquiry_name || item.creditor || 'Unknown Inquiry';
        const date = item.inquiry_date || item.date;
        const bureau = item.bureau || item.bureau_code;
        if (date && bureau) return `${creditor} · ${bureau} · ${date}`;
        if (date) return `${creditor} — ${date}`;
        return creditor;
      }
      
      item = personalInfoItems.find(i => i.id === itemId);
      if (item) return item.fieldLabel || item.field || itemId;

      if (excludedItem?.creditor) {
        const date = excludedItem.inquiry_date;
        const bureau = excludedItem.bureau;
        if (date && bureau) return `${excludedItem.creditor} · ${bureau} · ${date}`;
        if (date) return `${excludedItem.creditor} — ${date}`;
        return excludedItem.creditor;
      }
      
      if (typeof itemId === 'string' && /^inq[_-]/i.test(itemId)) {
        return 'Hard Inquiry';
      }

      return itemId;
    };

    // Canonical final letter rows from preview-generation-plan packages.
    const finalLetterPackages = buildFinalLetterPackagesFromPreview(
      generationPlanPreview?.packages || [],
      { getItemDisplayName },
    );
    
    return (
    <div className="space-y-4 sm:space-y-6 min-w-0 max-w-full overflow-x-hidden" data-testid="step-review">
      <div>
        <h2 className="text-xl font-bold text-stone-900 dark:text-[#EAF7F0]">Review Before Generation</h2>
        <p className="text-stone-600 dark:text-[#C7DDD0] text-sm mt-1">
          Confirm your selections before we generate your dispute letters.
        </p>
      </div>
      
      {/* SOURCE REPORT INFO - Show locked report for transparency */}
      <ReportSelector 
        showCompact={true}
        className="mb-4"
      />

      {/* CRITICAL WARNINGS - PI Over-selection or other critical issues */}
      {warnings.filter(w => w.severity === 'critical').map((warning, idx) => (
        <div 
          key={idx}
          className="bg-red-50 dark:bg-[rgba(239,68,68,0.12)] border-2 border-red-300 dark:border-[rgba(239,68,68,0.30)] rounded-xl p-5"
          data-testid="critical-warning"
        >
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-[rgba(239,68,68,0.20)] flex items-center justify-center flex-shrink-0">
              <Ban size={20} className="text-red-600 dark:text-red-400" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-red-900 dark:text-red-200 text-lg">Action Required</h3>
              <p className="text-red-800 dark:text-red-300 mt-1">{warning.message}</p>
              {warning.action && (
                <p className="text-sm text-red-700 dark:text-red-400 mt-2 font-medium">
                  {warning.action}
                </p>
              )}
              {warning.affected_items?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {warning.affected_items.map((itemId) => (
                    <Badge 
                      key={itemId}
                      variant="outline" 
                      className="bg-red-100 text-red-700 border-red-300"
                    >
                      {getItemDisplayName(itemId)}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      ))}

      {/* AI Decision Summary */}
      {/* AI Analysis & Recommendation - Uses resolved_strategy from generation plan when available */}
      {(generationPlanPreview?.resolved_strategy || aiDecision) && (
        <div className="bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-[rgba(16,185,129,0.12)] dark:to-[rgba(20,184,166,0.08)] border border-emerald-200 dark:border-[rgba(16,185,129,0.20)] rounded-xl p-3 sm:p-5 min-w-0" data-testid="ai-decision-summary">
          <div className="flex flex-wrap items-center gap-2 mb-3 min-w-0">
            <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-[rgba(16,185,129,0.15)] flex items-center justify-center shrink-0">
              <Scale size={18} className="text-emerald-600 dark:text-emerald-400" />
            </div>
            <h3 className="font-bold text-emerald-900 dark:text-emerald-200 break-words min-w-0 flex-1">
              {generationPlanPreview?.resolved_strategy ? 'Resolved Strategy' : 'AI Analysis & Recommendation'}
            </h3>
            {/* Show warning if strategy source differs */}
            {generationPlanPreview?.resolved_strategy?.is_multi_package && (
              <Badge className="bg-purple-100 dark:bg-[rgba(147,51,234,0.15)] text-purple-800 dark:text-purple-300 border-purple-300 dark:border-[rgba(147,51,234,0.25)] ml-2">
                Mixed Strategy
              </Badge>
            )}
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <p className="text-xs text-emerald-700 dark:text-emerald-400 uppercase tracking-wider">
                {generationPlanPreview?.resolved_strategy ? 'Resolved Strategy' : 'Recommended Method'}
              </p>
              <p className="font-semibold text-emerald-900 dark:text-emerald-200 break-words">
                {generationPlanPreview?.resolved_strategy?.label ||
                 generationPlanPreview?.strategy_recommendation?.title ||
                 aiDecision?.strategy_recommendation?.title || 
                 aiDecision?.recommended_method || 
                 'Standard Dispute'}
              </p>
            </div>
            <div>
              <p className="text-xs text-emerald-700 dark:text-emerald-400 uppercase tracking-wider">Target Bureaus</p>
              <p className="font-semibold text-emerald-900 dark:text-emerald-200 break-words">
                {[...new Set(generationPlanPreview?.resolved_strategy?.target_bureaus || 
                 aiDecision?.target_bureaus || 
                 ['All Bureaus'])].join(', ')}
              </p>
            </div>
          </div>
          
          {/* Strategy Rationale - from resolved_strategy */}
          {(generationPlanPreview?.resolved_strategy?.rationale || aiDecision?.strategy_recommendation?.reason) && (
            <div className="mb-4">
              <p className="text-xs text-emerald-700 dark:text-emerald-400 uppercase tracking-wider">Strategy Rationale</p>
              <p className="text-sm text-emerald-800 dark:text-emerald-300 mt-1">
                {generationPlanPreview?.resolved_strategy?.rationale ||
                 aiDecision?.strategy_recommendation?.reason}
              </p>
            </div>
          )}
          
          {/* Item Breakdown for multi-package strategies */}
          {generationPlanPreview?.resolved_strategy?.item_breakdown && (
            generationPlanPreview.resolved_strategy.item_breakdown.structural_count > 0 ||
            generationPlanPreview.resolved_strategy.item_breakdown.recovery_count > 0 ||
            generationPlanPreview.resolved_strategy.item_breakdown.isolated_count > 0 ||
            generationPlanPreview.resolved_strategy.item_breakdown.data_breach_count > 0
          ) && (
            <div className="mb-4 p-3 bg-white/50 dark:bg-[rgba(10,38,28,0.50)] rounded-lg border border-emerald-100 dark:border-[rgba(142,218,182,0.15)]">
              <p className="text-xs text-emerald-700 dark:text-emerald-300 uppercase tracking-wider mb-2">Package Breakdown</p>
              <div className="flex flex-wrap gap-2">
                {generationPlanPreview.resolved_strategy.item_breakdown.structural_count > 0 && (
                  <Badge className="bg-blue-100 text-blue-800 border-blue-200">
                    Structural: {generationPlanPreview.resolved_strategy.item_breakdown.structural_count}
                  </Badge>
                )}
                {generationPlanPreview.resolved_strategy.item_breakdown.recovery_count > 0 && (
                  <Badge className="bg-orange-100 text-orange-800 border-orange-200">
                    Recovery: {generationPlanPreview.resolved_strategy.item_breakdown.recovery_count}
                  </Badge>
                )}
                {generationPlanPreview.resolved_strategy.item_breakdown.isolated_count > 0 && (
                  <Badge className="bg-green-100 text-green-800 border-green-200">
                    Isolated: {generationPlanPreview.resolved_strategy.item_breakdown.isolated_count}
                  </Badge>
                )}
                {generationPlanPreview.resolved_strategy.item_breakdown.data_breach_count > 0 && (
                  <Badge className="bg-red-100 text-red-800 border-red-200">
                    Data Breach: {generationPlanPreview.resolved_strategy.item_breakdown.data_breach_count}
                  </Badge>
                )}
              </div>
            </div>
          )}
          
          {/* Validation Issues - show to user when items have problems */}
          {generationPlanPreview?.validation?.errors?.length > 0 && (
            <div className="mt-3 pt-3 border-t border-red-200">
              <p className="text-xs text-red-700 uppercase tracking-wider mb-2">Validation Issues</p>
              {generationPlanPreview.validation.errors.map((error, idx) => (
                <div 
                  key={idx} 
                  className="flex items-start gap-2 text-sm p-2 rounded-lg mb-1 bg-red-100 text-red-800"
                >
                  <AlertCircle size={16} className="mt-0.5 shrink-0" />
                  <span>{error.message}</span>
                </div>
              ))}
            </div>
          )}
          
          {/* Legacy AI Decision Warnings (fallback if no generation plan yet) */}
          {!generationPlanPreview?.resolved_strategy && aiDecision?.warnings?.length > 0 && (
            <div className="mt-3 pt-3 border-t border-emerald-200">
              <p className="text-xs text-emerald-700 uppercase tracking-wider mb-2">Warnings</p>
              {aiDecision.warnings.map((warning, idx) => (
                <div 
                  key={idx} 
                  className={`flex items-start gap-2 text-sm p-2 rounded-lg mb-1 ${
                    warning.severity === 'critical' ? 'bg-red-100 text-red-800' :
                    warning.severity === 'warning' ? 'bg-amber-100 text-amber-800' :
                    warning.severity === 'caution' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-blue-100 text-blue-800'
                  }`}
                >
                  <AlertCircle size={16} className="mt-0.5 shrink-0" />
                  <span>{warning.message}</span>
                </div>
              ))}
            </div>
          )}
          
          {!generationPlanPreview?.resolved_strategy && aiDecision?.review_required && (
            <div className="mt-3 pt-3 border-t border-emerald-200">
              <div className="flex items-center gap-2 text-amber-800 bg-amber-100 p-2 rounded-lg">
                <AlertCircle size={16} />
                <span className="text-sm font-medium">Manual review recommended: {aiDecision.review_reason}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* PHASE A: GENERATION PLAN PREVIEW - Shows matched prompts BEFORE generation */}
      {/* Role-based visibility: Admin sees technical details, Self-repair users see simplified summary */}
      {(disputePlanSummaryStatus !== 'idle' || generationPlanPreview || generationPlanLoading || disputePlanSummaryError) && (
        <div 
          className={`rounded-xl p-4 sm:p-5 min-w-0 max-w-full overflow-hidden min-h-[160px] ${
            canViewPromptDebug 
              ? 'bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-[rgba(147,51,234,0.12)] dark:to-[rgba(99,102,241,0.08)] border border-purple-200 dark:border-[rgba(147,51,234,0.20)]' 
              : 'bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-[rgba(16,185,129,0.12)] dark:to-[rgba(20,184,166,0.08)] border border-emerald-200 dark:border-[rgba(16,185,129,0.20)]'
          }`} 
          data-testid="generation-plan-preview"
          data-summary-status={disputePlanSummaryStatus}
        >
          <div className="flex flex-wrap items-center gap-2 mb-4 min-w-0">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              canViewPromptDebug ? 'bg-purple-100 dark:bg-[rgba(147,51,234,0.15)]' : 'bg-emerald-100 dark:bg-[rgba(16,185,129,0.15)]'
            }`}>
              {canViewPromptDebug ? (
                <Sparkles size={18} className="text-purple-600 dark:text-purple-400" />
              ) : (
                <FileText size={18} className="text-emerald-600 dark:text-emerald-400" />
              )}
            </div>
            <h3 className={`font-bold break-words ${canViewPromptDebug ? 'text-purple-900 dark:text-purple-200' : 'text-emerald-950 dark:text-[#EAF7F0]'}`}>
              {canViewPromptDebug ? 'Generation Plan Preview' : 'Dispute Plan Summary'}
            </h3>
            {(generationPlanLoading || disputePlanSummaryStatus === 'loading') && (
              <div className="ml-auto flex items-center gap-2">
                <span className={`text-xs ${canViewPromptDebug ? 'text-purple-600' : 'text-emerald-700'}`}>
                  Preparing dispute plan...
                </span>
                <Loader2 className={`h-4 w-4 animate-spin ${canViewPromptDebug ? 'text-purple-500' : 'text-emerald-500'}`} />
              </div>
            )}
            {disputePlanSummaryStatus === 'success'
              && (generationPlanPreview?.packages?.length || 0) > 3
              && !(generationPlanLoading || disputePlanSummaryStatus === 'loading') && (
              <Button
                variant="ghost"
                size="sm"
                className="ml-auto text-xs"
                onClick={() => setDisputePlanSummaryCollapsed((prev) => !prev)}
                data-testid="dispute-plan-summary-toggle"
              >
                {disputePlanSummaryCollapsed ? 'Expand' : 'Collapse'}
              </Button>
            )}
          </div>

          {(disputePlanSummaryStatus === 'error' || disputePlanSummaryError) && (
            <div
              className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
              data-testid="dispute-plan-summary-error"
            >
              <p>{disputePlanSummaryError || 'Unable to prepare the dispute plan summary. Your selected items are still preserved.'}</p>
              <Button
                variant="outline"
                size="sm"
                className="mt-2"
                data-testid="dispute-plan-summary-retry"
                onClick={() => {
                  lastPreviewSignatureRef.current = '';
                  inFlightPreviewSignatureRef.current = '';
                  setDisputePlanSummaryError(null);
                  setDisputePlanSummaryStatus('loading');
                  setGenerationPlanLoading(true);
                  setSummaryRetryNonce((n) => n + 1);
                }}
              >
                Retry Summary
              </Button>
            </div>
          )}
          
          {generationPlanPreview && !disputePlanSummaryCollapsed && (
            <>
              {/* ADMIN/STAFF VIEW - Full Technical Details */}
              {canViewPromptDebug ? (
                <>
                  {/* Generation Mode Summary */}
                  <div className="flex gap-3 mb-4">
                    {generationPlanPreview.generation_summary?.all_prompt_managed && (
                      <Badge className="bg-purple-600 hover:bg-purple-700 text-white">
                        <CheckCircle size={12} className="mr-1" />
                        All Prompt Managed
                      </Badge>
                    )}
                    {generationPlanPreview.generation_summary?.all_deterministic && (
                      <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300">
                        <Info size={12} className="mr-1" />
                        Deterministic Fallback
                      </Badge>
                    )}
                    {!generationPlanPreview.generation_summary?.all_prompt_managed && !generationPlanPreview.generation_summary?.all_deterministic && (
                      <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-300">
                        <Info size={12} className="mr-1" />
                        Mixed: {generationPlanPreview.generation_summary?.prompt_managed_count} Prompt, {generationPlanPreview.generation_summary?.deterministic_count} Deterministic
                      </Badge>
                    )}
                  </div>
                  
                  {/* Per-Package Prompt Metadata - FULL TECHNICAL VIEW */}
                  <div className="space-y-3">
                    {generationPlanPreview.packages?.map((pkg, idx) => (
                      <div 
                        key={idx}
                        className={`p-4 rounded-lg border ${
                          pkg.compatibility_status === 'invalid'
                            ? 'bg-red-50 dark:bg-[rgba(239,68,68,0.12)] border-red-200 dark:border-[rgba(239,68,68,0.20)]'
                            : pkg.compatibility_status === 'corrected'
                            ? 'bg-amber-50 dark:bg-[rgba(245,158,11,0.12)] border-amber-200 dark:border-[rgba(245,158,11,0.20)]'
                            : pkg.generation_mode === 'prompt_managed' 
                            ? 'bg-white dark:bg-[rgba(10,38,28,0.65)] border-purple-200 dark:border-[rgba(147,51,234,0.20)]' 
                            : 'bg-amber-50 dark:bg-[rgba(245,158,11,0.12)] border-amber-200 dark:border-[rgba(245,158,11,0.20)]'
                        }`}
                        data-testid={`package-prompt-metadata-${pkg.bureau}`}
                      >
                        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between mb-2 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 min-w-0">
                            <Badge variant="outline" className="bg-stone-100 text-stone-700 border-stone-300 dark:bg-[rgba(84,106,93,0.25)] dark:text-[#C7DDD0] dark:border-[rgba(142,218,182,0.20)]">
                              {pkg.bureau}
                            </Badge>
                            <span className="text-sm font-medium text-stone-700 dark:text-[#C7DDD0] break-words">
                              {pkg.item_count} item{pkg.item_count !== 1 ? 's' : ''}
                            </span>
                          </div>
                          <div className="flex flex-wrap items-center gap-2 w-full md:w-auto md:justify-end">
                            {/* View Evidence Button */}
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedEvidenceForView({
                                  bureau: pkg.bureau,
                                  method: pkg.selected_method || pkg.resolved_method,
                                  family: pkg.selected_family || pkg.resolved_family,
                                  matched_prompt: pkg.matched_prompt,
                                  evidence_views: pkg.evidence_views || [],
                                });
                                setViewEvidenceModalOpen(true);
                              }}
                              className="flex items-center gap-1 px-2 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md transition-colors text-xs"
                              title="View underlying evidence data"
                              data-testid={`view-evidence-btn-${pkg.bureau}`}
                            >
                              <Database size={12} />
                              <span>View Evidence</span>
                            </button>
                            {/* Lock Status Badge */}
                            <Badge 
                              className={
                                pkg.lock_status === 'blocked'
                                  ? 'bg-red-600 text-white'
                                  : pkg.lock_status === 'locked'
                                  ? 'bg-emerald-600 text-white'
                                  : 'bg-amber-500 text-white'
                              }
                            >
                              {pkg.lock_status === 'blocked' ? 'Blocked' : pkg.lock_status === 'locked' ? 'Locked for Generation' : 'Unlocked'}
                            </Badge>
                            <Badge 
                              className={pkg.generation_mode === 'prompt_managed' 
                                ? 'bg-purple-600 text-white' 
                                : 'bg-amber-500 text-white'
                              }
                            >
                              {pkg.generation_mode === 'prompt_managed' ? 'Prompt Managed' : 'Deterministic'}
                            </Badge>
                          </div>
                        </div>
                        
                        {/* Method/Family/Prompt Display - Using backend values */}
                        <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                          <div>
                            <span className="text-stone-500">Method:</span>
                            <span className={`ml-2 font-medium ${pkg.compatibility_status === 'invalid' ? 'text-red-700' : 'text-stone-800'}`}>
                              {pkg.selected_method || pkg.resolved_method}
                            </span>
                          </div>
                          <div>
                            <span className="text-stone-500">Family:</span>
                            <span className={`ml-2 font-medium ${pkg.compatibility_status === 'invalid' ? 'text-red-700' : 'text-stone-800'}`}>
                              {pkg.selected_family || pkg.matched_prompt?.prompt_family || pkg.resolved_family}
                            </span>
                          </div>
                          {pkg.round && (
                            <div>
                              <span className="text-stone-500">Round:</span>
                              <span className="ml-2 font-medium text-stone-800">Round {pkg.round}</span>
                            </div>
                          )}
                          {pkg.confidence !== undefined && (
                            <div>
                              <span className="text-stone-500">Confidence:</span>
                              <span className={`ml-2 font-medium ${pkg.confidence === 0 ? 'text-red-700' : 'text-emerald-700'}`}>
                                {(pkg.confidence * 100).toFixed(0)}%
                              </span>
                            </div>
                          )}
                        </div>
                        
                        {/* Validation Errors */}
                        {pkg.validation_errors?.length > 0 && (
                          <div className="mt-3 p-3 bg-red-50 dark:bg-[rgba(239,68,68,0.12)] rounded-lg border border-red-200 dark:border-[rgba(239,68,68,0.20)]">
                            <div className="text-xs text-red-700 dark:text-red-300 uppercase tracking-wider mb-1">Validation Errors</div>
                            <ul className="text-sm text-red-800 dark:text-red-200 list-disc pl-4">
                              {pkg.validation_errors.map((err, errIdx) => (
                                <li key={errIdx}>{err}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        {/* Validation Warnings */}
                        {pkg.validation_warnings?.length > 0 && (
                          <div className="mt-3 p-3 bg-amber-50 dark:bg-[rgba(245,158,11,0.12)] rounded-lg border border-amber-200 dark:border-[rgba(245,158,11,0.20)]">
                            <div className="text-xs text-amber-700 dark:text-amber-300 uppercase tracking-wider mb-1">Warnings</div>
                            <ul className="text-sm text-amber-800 dark:text-amber-200 list-disc pl-4">
                              {pkg.validation_warnings.map((warn, warnIdx) => (
                                <li key={warnIdx}>{warn}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        {pkg.matched_prompt ? (
                          <div className="mt-3 p-3 bg-purple-50 rounded-lg border border-purple-100">
                            <div className="flex items-center justify-between mb-1">
                              <div className="text-xs text-purple-700 uppercase tracking-wider">Matched Prompt</div>
                              {pkg.matched_prompt.is_locked && (
                                <Badge className="bg-purple-200 text-purple-800 text-xs">
                                  <Shield size={10} className="mr-1" />
                                  Locked for Generation
                                </Badge>
                              )}
                            </div>
                            <div className="font-medium text-purple-900 text-sm">
                              {pkg.matched_prompt.prompt_title}
                            </div>
                            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                              <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded">
                                ID: {pkg.matched_prompt.prompt_id?.slice(0, 16) || 'N/A'}...
                              </span>
                              {pkg.matched_prompt.prompt_version && (
                                <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded">
                                  v{pkg.matched_prompt.prompt_version}
                                </span>
                              )}
                              {pkg.matched_prompt.confidence && (
                                <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded">
                                  Confidence: {(pkg.matched_prompt.confidence * 100).toFixed(0)}%
                                </span>
                              )}
                              {/* View Prompt Button */}
                              <button
                                type="button"
                                onClick={() => {
                                  setSelectedPromptForView({
                                    ...pkg.matched_prompt,
                                    bureau: pkg.bureau,
                                    method: pkg.resolved_method,
                                    family: pkg.matched_prompt?.prompt_family || pkg.resolved_family,
                                  });
                                  setViewPromptModalOpen(true);
                                }}
                                className="ml-auto flex items-center gap-1 px-2 py-1 bg-purple-600 hover:bg-purple-700 text-white rounded-md transition-colors"
                                title="View full prompt content"
                                data-testid={`view-prompt-btn-${pkg.bureau}`}
                              >
                                <Eye size={12} />
                                <span>View Prompt</span>
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="mt-3 p-3 bg-amber-50 dark:bg-[rgba(245,158,11,0.12)] rounded-lg border border-amber-200 dark:border-[rgba(245,158,11,0.20)]">
                            <div className="text-xs text-amber-700 dark:text-amber-300 uppercase tracking-wider mb-1">Fallback Reason</div>
                            <p className="text-sm text-amber-800 dark:text-amber-200">
                              {pkg.fallback_reason || 'No matching prompt found in Prompt Management'}
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                /* SELF-REPAIR USER VIEW - Simplified Dispute Plan Summary */
                <>
                  {/* Client-friendly description */}
                  <p className="text-sm text-stone-600 dark:text-[#C7DDD0] mb-4">
                    Your dispute plan has been prepared based on the items you selected. Review the summary below before generating your letters.
                  </p>
                  
                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 mb-4">
                    {/* Letters Prepared */}
                    <div className="bg-white dark:bg-[rgba(10,38,28,0.65)] rounded-lg border border-emerald-200 dark:border-[rgba(142,218,182,0.15)] p-3 min-w-0">
                      <div className="text-xs text-stone-500 dark:text-[#93AE9F] uppercase tracking-wider mb-1">Letters Prepared</div>
                      <div className="text-lg font-bold text-emerald-700 dark:text-emerald-300">
                        {generationPlanPreview.packages?.length || 0}
                      </div>
                      <div className="text-xs text-stone-500 dark:text-[#93AE9F]">bureau-specific letters</div>
                    </div>
                    
                    {/* Bureaus Included */}
                    <div className="bg-white dark:bg-[rgba(10,38,28,0.65)] rounded-lg border border-emerald-200 dark:border-[rgba(142,218,182,0.15)] p-3 min-w-0">
                      <div className="text-xs text-stone-500 dark:text-[#93AE9F] uppercase tracking-wider mb-1">Bureaus</div>
                      <div className="text-sm font-medium text-emerald-700 dark:text-emerald-300 break-words">
                        {[...new Set(generationPlanPreview.packages?.map(p => p.bureau) || [])].join(', ')}
                      </div>
                    </div>
                    
                    {/* Total Items */}
                    <div className="bg-white dark:bg-[rgba(10,38,28,0.65)] rounded-lg border border-emerald-200 dark:border-[rgba(142,218,182,0.15)] p-3 min-w-0">
                      <div className="text-xs text-stone-500 dark:text-[#93AE9F] uppercase tracking-wider mb-1">Items Included</div>
                      <div className="text-lg font-bold text-emerald-700 dark:text-emerald-300">
                        {proceedingItemsCount}
                      </div>
                      <div className="text-xs text-stone-500 dark:text-[#93AE9F]">selected items</div>
                    </div>
                    
                    {/* Plan Type */}
                    <div className="bg-white dark:bg-[rgba(10,38,28,0.65)] rounded-lg border border-emerald-200 dark:border-[rgba(142,218,182,0.15)] p-3 min-w-0">
                      <div className="text-xs text-stone-500 dark:text-[#93AE9F] uppercase tracking-wider mb-1">Plan Type</div>
                      <div className="text-sm font-medium text-emerald-700 dark:text-emerald-300">
                        {generationPlanPreview.packages?.length > 3 ? 'Multi-part dispute' : 'Standard dispute'}
                      </div>
                    </div>
                  </div>
                  
                  {/* Simplified Package List */}
                  <div className="space-y-2">
                    {generationPlanPreview.packages?.map((pkg, idx) => (
                      <div 
                        key={idx}
                        className="p-3 rounded-lg bg-white dark:bg-[rgba(10,38,28,0.50)] border border-emerald-100 dark:border-[rgba(142,218,182,0.12)] w-full max-w-full min-w-0 overflow-hidden"
                        data-testid={`package-summary-${pkg.bureau}`}
                      >
                        {/* Mobile: stacked rows — no overlap */}
                        <div className="md:hidden space-y-2 min-w-0">
                          <div className="flex items-center justify-between gap-2 min-w-0">
                            <Badge variant="outline" className="bg-emerald-50 dark:bg-[rgba(16,185,129,0.15)] text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-[rgba(16,185,129,0.25)] shrink-0">
                              {pkg.bureau}
                            </Badge>
                            <Badge className="bg-emerald-100 dark:bg-[rgba(16,185,129,0.15)] text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-[rgba(16,185,129,0.25)] shrink-0 whitespace-nowrap">
                              <CheckCircle size={10} className="mr-1" />
                              Ready
                            </Badge>
                          </div>
                          <p className="text-sm text-stone-700 dark:text-[#C7DDD0] break-words">
                            {pkg.item_count} selected item{pkg.item_count !== 1 ? 's' : ''}
                          </p>
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedEvidenceForView({
                                bureau: pkg.bureau,
                                method: pkg.resolved_method,
                                family: pkg.resolved_family,
                                matched_prompt: pkg.matched_prompt,
                                evidence_views: pkg.evidence_views || [],
                              });
                              setViewEvidenceModalOpen(true);
                            }}
                            className="w-full flex items-center justify-center gap-1.5 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md transition-colors text-xs"
                            title="Review selected items"
                            data-testid={`review-items-btn-${pkg.bureau}`}
                          >
                            <Eye size={12} />
                            <span>Review Items</span>
                          </button>
                        </div>

                        {/* md+: horizontal row */}
                        <div className="hidden md:flex md:items-center md:justify-between gap-3 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 min-w-0 flex-1">
                            <Badge variant="outline" className="bg-emerald-50 dark:bg-[rgba(16,185,129,0.15)] text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-[rgba(16,185,129,0.25)] shrink-0">
                              {pkg.bureau}
                            </Badge>
                            <span className="text-sm text-stone-700 dark:text-[#C7DDD0] min-w-0 break-words">
                              {pkg.item_count} selected item{pkg.item_count !== 1 ? 's' : ''}
                            </span>
                          </div>
                          <div className="flex flex-wrap items-center gap-2 shrink-0">
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedEvidenceForView({
                                  bureau: pkg.bureau,
                                  method: pkg.resolved_method,
                                  family: pkg.resolved_family,
                                  matched_prompt: pkg.matched_prompt,
                                  evidence_views: pkg.evidence_views || [],
                                });
                                setViewEvidenceModalOpen(true);
                              }}
                              className="flex items-center justify-center gap-1 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md transition-colors text-xs whitespace-nowrap"
                              title="Review selected items"
                            >
                              <Eye size={12} />
                              <span>Review Items</span>
                            </button>
                            <Badge className="bg-emerald-100 dark:bg-[rgba(16,185,129,0.15)] text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-[rgba(16,185,129,0.25)] shrink-0 whitespace-nowrap">
                              <CheckCircle size={10} className="mr-1" />
                              Ready
                            </Badge>
                          </div>
                        </div>
                        <div className="mt-2 text-xs text-stone-500 dark:text-[#93AE9F]">
                          Letter will be prepared for this bureau
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Review Reminder */}
                  <div className="mt-4 p-3 bg-emerald-50 dark:bg-[rgba(16,185,129,0.12)] rounded-lg border border-emerald-200 dark:border-[rgba(16,185,129,0.20)]">
                    <div className="flex items-start gap-2">
                      <Info size={16} className="text-emerald-600 dark:text-emerald-400 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-emerald-800 dark:text-emerald-200">
                        Please confirm all selected items and supporting details before generating your letters.
                      </p>
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      )}

      <ExcludedItemsSection excludedItems={excludedItems} />


      {/* ============================================ */}
      {/* FINAL LETTER PACKAGES - canonical preview packages */}
      {/* ============================================ */}
      {(finalLetterPackages.length > 0 || methodsPending || proceedingItemsCount > 0) && (
        <div className="bg-emerald-50 dark:bg-[rgba(10,38,28,0.65)] border border-emerald-200 dark:border-[rgba(142,218,182,0.15)] rounded-xl overflow-hidden w-full max-w-full min-w-0" data-testid="final-letter-packages-section">
          <div className="bg-emerald-100 dark:bg-[rgba(8,32,24,0.65)] px-4 py-3 border-b border-emerald-200 dark:border-[rgba(142,218,182,0.12)]">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between min-w-0">
              <div className="flex flex-wrap items-center gap-2 min-w-0 flex-1">
                <CheckCircle size={18} className="text-emerald-600 dark:text-emerald-400 shrink-0" />
                <h3 className="font-semibold text-emerald-950 dark:text-[#EAF7F0]">Final Letter Packages</h3>
                <Badge variant="outline" className="bg-emerald-200 dark:bg-[rgba(16,185,129,0.15)] text-emerald-900 dark:text-emerald-300 border-emerald-300 dark:border-[rgba(16,185,129,0.25)] text-xs shrink-0" data-testid="final-letter-count">
                  {genPlanTotals.totalLetters} letter{genPlanTotals.totalLetters !== 1 ? 's' : ''}
                </Badge>
                <Badge variant="outline" className="bg-white dark:bg-[rgba(10,38,28,0.50)] text-emerald-800 dark:text-emerald-300 border-emerald-300 dark:border-[rgba(16,185,129,0.25)] text-xs shrink-0">
                  {genPlanTotals.overallTotalItems || proceedingItemsCount} items
                </Badge>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="text-emerald-800 dark:text-emerald-300 hover:text-emerald-950 dark:hover:text-[#EAF7F0] hover:bg-emerald-200 dark:hover:bg-[rgba(16,185,129,0.12)] shrink-0 self-start sm:self-auto"
                onClick={() => setShowProceedingItemsExpanded(prev => !prev)}
              >
                {showProceedingItemsExpanded ? (
                  <>
                    <ChevronUp size={14} className="mr-1" />
                    Collapse
                  </>
                ) : (
                  <>
                    <ChevronDown size={14} className="mr-1" />
                    View Details
                  </>
                )}
              </Button>
            </div>
            <p className="text-sm text-emerald-800 dark:text-[#C7DDD0] mt-1">
              {methodsPending
                ? 'Finalizing dispute methods...'
                : 'One letter package per bureau-scoped package from the generation plan.'}
            </p>
          </div>

          {showProceedingItemsExpanded && (
            <div className="divide-y divide-emerald-100 dark:divide-[rgba(142,218,182,0.08)]" data-testid="final-letter-packages-list">
              {methodsPending && (
                <div className="p-4 text-sm text-emerald-800 dark:text-emerald-200 flex items-center gap-2" data-testid="finalizing-dispute-methods">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Finalizing dispute methods...
                </div>
              )}
              {!methodsPending && finalLetterPackages.map((pkg, pkgIdx) => {
                const METHOD_COLORS_FINAL = {
                  data_breach: 'bg-purple-100 text-purple-700 border-purple-300 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-700',
                  isolated: 'bg-emerald-100 text-emerald-700 border-emerald-300 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-700',
                  structural: 'bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700',
                  recovery: 'bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700',
                  structural_recovery_combo: 'bg-indigo-100 text-indigo-700 border-indigo-300 dark:bg-indigo-900/30 dark:text-indigo-300 dark:border-indigo-700',
                  structural_recovery: 'bg-indigo-100 text-indigo-700 border-indigo-300 dark:bg-indigo-900/30 dark:text-indigo-300 dark:border-indigo-700',
                };
                const badgeClass = METHOD_COLORS_FINAL[pkg.package_family]
                  || 'bg-stone-100 text-stone-700 border-stone-300 dark:bg-stone-800/50 dark:text-stone-300 dark:border-stone-600';
                const sectionEntries = Object.entries(pkg.section_groups || {}).filter(([, ids]) => Array.isArray(ids) && ids.length > 0);

                return (
                  <div key={pkg.key || pkg.package_id || pkgIdx} className="p-4 min-w-0" data-testid={`final-letter-package-${pkg.bureau}`}>
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between mb-3 pb-2 border-b border-emerald-100 dark:border-[rgba(142,218,182,0.12)] min-w-0">
                      <div className="flex flex-wrap items-center gap-2 min-w-0 flex-1">
                        <div className="w-6 h-6 rounded-md bg-emerald-200 dark:bg-[rgba(16,185,129,0.20)] text-emerald-800 dark:text-emerald-300 flex items-center justify-center text-xs font-bold shrink-0">
                          {String.fromCharCode(65 + pkgIdx)}
                        </div>
                        <span className="font-semibold text-emerald-950 dark:text-[#EAF7F0] break-words min-w-0" data-testid={`final-package-title-${pkg.bureau}`}>
                          {pkg.title}
                        </span>
                        <Badge variant="outline" className={`${badgeClass} text-xs shrink-0`}>
                          {pkg.display_label}
                        </Badge>
                        {pkg.bureau && (
                          <Badge variant="outline" className={`text-xs shrink-0 ${BUREAU_COLORS[pkg.bureau] || 'bg-stone-100 text-stone-600'}`}>
                            {pkg.bureau}
                          </Badge>
                        )}
                      </div>
                      <span className="text-xs text-emerald-700 dark:text-emerald-300 shrink-0">
                        1 letter · {pkg.item_count} item{pkg.item_count !== 1 ? 's' : ''}
                      </span>
                    </div>

                    {sectionEntries.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {sectionEntries.map(([section, ids]) => (
                          <Badge key={section} variant="outline" className="text-xs bg-white dark:bg-[rgba(10,38,28,0.50)] text-emerald-800 dark:text-emerald-300 border-emerald-200 dark:border-[rgba(16,185,129,0.25)]">
                            {section.replace(/_/g, ' ')}: {ids.length}
                          </Badge>
                        ))}
                      </div>
                    )}

                    <div className="space-y-2">
                      {(pkg.item_ids || []).map((itemId, itemIdx) => (
                        <div key={`${itemId}-${itemIdx}`} className="flex items-start gap-2 p-2 bg-white dark:bg-[rgba(10,38,28,0.50)] rounded-lg border border-emerald-100 dark:border-[rgba(142,218,182,0.12)]">
                          <div className="w-5 h-5 rounded-full bg-emerald-100 dark:bg-[rgba(16,185,129,0.15)] flex items-center justify-center flex-shrink-0 mt-0.5">
                            <Check size={12} className="text-emerald-600 dark:text-emerald-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className="font-medium text-stone-900 dark:text-[#EAF7F0] text-sm truncate">
                              {pkg.item_names?.[itemIdx] || getItemDisplayName(itemId)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {!showProceedingItemsExpanded && finalLetterPackages.length > 0 && (
            <div className="p-4 text-sm text-emerald-800 dark:text-emerald-300" data-testid="final-letter-packages-collapsed">
              <div className="flex flex-wrap gap-2">
                {finalLetterPackages.map((pkg) => (
                  <Badge key={pkg.key || pkg.package_id} variant="outline" className="bg-white dark:bg-[rgba(10,38,28,0.50)] text-emerald-700 dark:text-emerald-300 border-emerald-300 dark:border-[rgba(16,185,129,0.25)]">
                    {pkg.title}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Optional: selected item strategy groups (not final letter count) */}
      {!methodsPending && packages.length > 0 && (
        <details className="bg-stone-50 dark:bg-[rgba(8,32,24,0.45)] border border-stone-200 dark:border-[rgba(142,218,182,0.12)] rounded-xl overflow-hidden w-full max-w-full min-w-0" data-testid="selected-item-strategies">
          <summary className="cursor-pointer px-4 py-3 text-sm font-medium text-stone-700 dark:text-[#C7DDD0] select-none">
            Selected item strategies
          </summary>
          <div className="px-4 pb-4 flex flex-wrap gap-2">
            {packages.map((pkg, idx) => (
              <Badge key={idx} variant="outline" className="bg-white dark:bg-[rgba(10,38,28,0.50)] text-stone-700 dark:text-[#C7DDD0] border-stone-300 dark:border-[rgba(142,218,182,0.20)]">
                {(pkg.display_name || pkg.method_name)}: {pkg.items.length} items
              </Badge>
            ))}
          </div>
        </details>
      )}

      {/* COMPACT PI ANCHOR CONFIRMATION - Small status summary */}
      {piAnchorConfirmations.length > 0 && (
        <div className="bg-emerald-50 dark:bg-[rgba(16,185,129,0.12)] border border-emerald-200 dark:border-[rgba(16,185,129,0.20)] rounded-xl p-3 sm:p-4 min-w-0" data-testid="pi-anchor-summary">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between min-w-0">
            <div className="flex items-start gap-3 min-w-0 flex-1">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 dark:bg-[rgba(16,185,129,0.15)] flex items-center justify-center shrink-0">
                <Shield size={20} className="text-emerald-600 dark:text-emerald-400" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold text-emerald-900 dark:text-emerald-200">Identity Protection Active</span>
                  <Badge variant="outline" className="bg-emerald-100 dark:bg-[rgba(16,185,129,0.15)] text-emerald-700 dark:text-emerald-300 border-emerald-300 dark:border-[rgba(16,185,129,0.25)] text-xs shrink-0">
                    {piAnchorConfirmations.length} record{piAnchorConfirmations.length !== 1 ? 's' : ''} preserved
                  </Badge>
                </div>
                <p className="text-sm text-emerald-700 dark:text-emerald-300 mt-0.5 break-words">
                  {piAnchorConfirmations.map(c => CATEGORY_INFO[c.category]?.label || c.category).join(' • ')}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="text-emerald-700 dark:text-emerald-300 hover:text-emerald-900 dark:hover:text-emerald-100 hover:bg-emerald-100 dark:hover:bg-[rgba(16,185,129,0.12)] self-start sm:self-auto shrink-0"
              onClick={() => setShowPIAnchorModal(true)}
            >
              <PenLine size={14} className="mr-1" />
              Edit
            </Button>
          </div>
        </div>
      )}


      {/* PACKAGE SPLIT SUMMARY — Final Letter Packages from preview */}
      {finalLetterPackages.length > 0 && (
        <div className="bg-white dark:bg-[rgba(10,38,28,0.65)] border border-stone-200 dark:border-[rgba(142,218,182,0.15)] rounded-xl overflow-hidden w-full max-w-full min-w-0" data-testid="package-split-summary">
          <div className="bg-stone-50 dark:bg-[rgba(8,32,24,0.65)] px-3 sm:px-4 py-3 border-b border-stone-200 dark:border-[rgba(142,218,182,0.12)]">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between min-w-0">
              <div className="flex items-center gap-2 min-w-0">
                <Package size={18} className="text-stone-600 dark:text-[#93AE9F] shrink-0" />
                <h3 className="font-semibold text-stone-900 dark:text-[#EAF7F0]">Generation Plan</h3>
              </div>
              <div className="flex flex-col gap-0.5 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-3 sm:gap-y-1 text-sm text-stone-600 dark:text-[#C7DDD0] min-w-0">
                <span>{genPlanTotals.overallTotalItems} items proceeding</span>
                <span className="hidden sm:inline">•</span>
                <span className="font-semibold">{genPlanTotals.totalPackages} package{genPlanTotals.totalPackages !== 1 ? 's' : ''}</span>
                <span className="hidden sm:inline">•</span>
                <span className="font-semibold text-emerald-700 dark:text-emerald-300" data-testid="generation-plan-letter-count">
                  {genPlanTotals.totalLetters} letter{genPlanTotals.totalLetters !== 1 ? 's' : ''}
                </span>
              </div>
            </div>
          </div>

          <div className="divide-y divide-stone-100 dark:divide-[rgba(142,218,182,0.08)]">
            {finalLetterPackages.map((pkg, idx) => {
              const METHOD_COLORS = {
                data_breach: 'bg-purple-100 dark:bg-[rgba(147,51,234,0.20)] text-purple-700 dark:text-purple-300 border-purple-300 dark:border-[rgba(147,51,234,0.30)]',
                isolated: 'bg-emerald-100 dark:bg-[rgba(16,185,129,0.20)] text-emerald-700 dark:text-emerald-300 border-emerald-300 dark:border-[rgba(16,185,129,0.30)]',
                structural: 'bg-amber-100 dark:bg-[rgba(245,158,11,0.20)] text-amber-700 dark:text-amber-300 border-amber-300 dark:border-[rgba(245,158,11,0.30)]',
                recovery: 'bg-blue-100 dark:bg-[rgba(59,130,246,0.20)] text-blue-700 dark:text-blue-300 border-blue-300 dark:border-[rgba(59,130,246,0.30)]',
                structural_recovery: 'bg-indigo-100 dark:bg-[rgba(99,102,241,0.20)] text-indigo-700 dark:text-indigo-300 border-indigo-300 dark:border-[rgba(99,102,241,0.30)]',
                structural_recovery_combo: 'bg-indigo-100 dark:bg-[rgba(99,102,241,0.20)] text-indigo-700 dark:text-indigo-300 border-indigo-300 dark:border-[rgba(99,102,241,0.30)]',
              };
              const badgeClass = METHOD_COLORS[pkg.package_family]
                || 'bg-stone-100 dark:bg-[rgba(84,106,93,0.25)] text-stone-700 dark:text-[#C7DDD0] border-stone-300 dark:border-[rgba(142,218,182,0.20)]';

              return (
                <div key={pkg.key || pkg.package_id || idx} className="p-3 sm:p-4 min-w-0" data-testid={`generation-plan-package-${pkg.bureau}`}>
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center font-semibold shrink-0 bg-stone-100 dark:bg-[rgba(84,106,93,0.25)] text-stone-600 dark:text-[#C7DDD0]">
                      {String.fromCharCode(65 + idx)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-start sm:gap-x-2 sm:gap-y-1">
                        <span className="font-semibold text-stone-900 dark:text-[#EAF7F0] break-words leading-snug">{pkg.title}</span>
                        <div className="flex flex-wrap items-center gap-1.5">
                          <Badge variant="outline" className={badgeClass}>
                            {pkg.display_label}
                          </Badge>
                          {pkg.bureau && (
                            <Badge variant="outline" className={`${BUREAU_COLORS[pkg.bureau] || BUREAU_COLORS[pkg.bureau?.toLowerCase()]} text-xs shrink-0`}>
                              {pkg.bureau}
                            </Badge>
                          )}
                          <Badge variant="outline" className="bg-emerald-100 dark:bg-[rgba(16,185,129,0.20)] text-emerald-700 dark:text-emerald-300 border-emerald-300 dark:border-[rgba(16,185,129,0.30)] text-xs shrink-0">
                            <CheckCircle size={10} className="mr-1" />
                            1 letter
                          </Badge>
                        </div>
                      </div>
                      <p className="text-sm text-stone-600 dark:text-[#C7DDD0] mt-1.5 break-words">
                        {pkg.item_count} item{pkg.item_count !== 1 ? 's' : ''} • {pkg.package_family.replace(/_/g, ' ')}
                      </p>
                      {Object.keys(pkg.section_groups || {}).length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {Object.entries(pkg.section_groups).map(([section, ids]) => (
                            Array.isArray(ids) && ids.length > 0 ? (
                              <Badge key={section} variant="outline" className="text-xs bg-stone-50 dark:bg-[rgba(84,106,93,0.20)] text-stone-600 dark:text-[#C7DDD0] border-stone-200 dark:border-[rgba(142,218,182,0.15)]">
                                {section}: {ids.length}
                              </Badge>
                            ) : null
                          ))}
                        </div>
                      )}
                      {(pkg.item_names || []).length > 0 && (
                        <div className="mt-2 text-xs text-stone-500 dark:text-[#93AE9F] leading-relaxed break-words">
                          <ul className="sm:hidden space-y-1 list-none pl-0">
                            {pkg.item_names.slice(0, 3).map((name, i) => (
                              <li key={`${pkg.package_id}-name-${i}`} className="break-words">{name}</li>
                            ))}
                            {pkg.item_names.length > 3 && (
                              <li className="text-stone-400 dark:text-[#7A9488]">+{pkg.item_names.length - 3} more</li>
                            )}
                          </ul>
                          <p className="hidden sm:block break-words">
                            {pkg.item_names.slice(0, 3).join(', ')}
                            {pkg.item_names.length > 3 && ` +${pkg.item_names.length - 3} more`}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* REROUTED/BLOCKED ITEMS NOTICE */}
      {blockedItems.length > 0 && (
        <div className="bg-amber-50 dark:bg-[rgba(245,158,11,0.12)] border border-amber-200 dark:border-[rgba(245,158,11,0.20)] rounded-xl overflow-hidden" data-testid="blocked-items-notice">
          <div className="bg-amber-100 dark:bg-[rgba(245,158,11,0.18)] px-4 py-3 border-b border-amber-200 dark:border-[rgba(245,158,11,0.20)]">
            <div className="flex items-center gap-2">
              <AlertTriangle size={18} className="text-amber-600 dark:text-amber-400" />
              <h3 className="font-semibold text-amber-900 dark:text-amber-200">Rerouted Items ({blockedItems.length})</h3>
            </div>
            <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
              These items were blocked from their default method and rerouted to an alternative. They will still be included in your dispute.
            </p>
          </div>
          <div className="divide-y divide-amber-100 dark:divide-[rgba(245,158,11,0.12)]">
            {blockedItems.map((item, idx) => (
              <div key={idx} className="p-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-amber-100 dark:bg-[rgba(245,158,11,0.15)] flex items-center justify-center flex-shrink-0">
                    <ArrowRight size={16} className="text-amber-600 dark:text-amber-400" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-amber-900 dark:text-amber-200">{getItemDisplayName(item.item_id)}</span>
                      <Badge variant="outline" className="bg-red-100 dark:bg-[rgba(239,68,68,0.15)] text-red-700 dark:text-red-300 border-red-300 dark:border-[rgba(239,68,68,0.25)] line-through text-xs">
                        {item.blocked_method_name}
                      </Badge>
                      <ArrowRight size={14} className="text-amber-600 dark:text-amber-400" />
                      <Badge variant="outline" className="bg-emerald-100 dark:bg-[rgba(16,185,129,0.15)] text-emerald-700 dark:text-emerald-300 border-emerald-300 dark:border-[rgba(16,185,129,0.25)] text-xs">
                        {item.alternative_method_name}
                      </Badge>
                    </div>
                    <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">{item.reason}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* NON-CRITICAL WARNINGS */}
      {warnings.filter(w => w.severity !== 'critical').length > 0 && (
        <div className="bg-amber-50 dark:bg-[rgba(245,158,11,0.12)] border border-amber-200 dark:border-[rgba(245,158,11,0.20)] rounded-xl p-3 sm:p-4 min-w-0" data-testid="warnings-notice">
          <div className="flex items-start gap-3 min-w-0">
            <AlertCircle size={20} className="text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-amber-900 dark:text-amber-200">Notices</h4>
              {warnings.filter(w => w.severity !== 'critical').map((warning, idx) => (
                <p key={idx} className="text-sm text-amber-700 dark:text-amber-300 mt-1 break-words leading-relaxed">{warning.message}</p>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* GOVERNANCE NOTICE */}
      {methodResolution?.governance && (
        <div className="bg-stone-50 dark:bg-[rgba(10,38,28,0.50)] border border-stone-200 dark:border-[rgba(142,218,182,0.15)] rounded-xl p-3 sm:p-4 min-w-0">
          <div className="flex items-start gap-3 min-w-0">
            <Shield size={20} className="text-stone-500 dark:text-[#93AE9F] mt-0.5 shrink-0" />
            <div className="min-w-0 flex-1">
              <h4 className="font-semibold text-stone-700 dark:text-[#C7DDD0]">Admin Control Active</h4>
              <p className="text-sm text-stone-600 dark:text-[#93AE9F] mt-1 break-words leading-relaxed">
                Package grouping is controlled by Admin Console rules. User selection is the pool; 
                admin logic decides final grouping, blocked items, and method assignments.
              </p>
              <div className="flex gap-2 mt-2 flex-wrap">
                {methodResolution.governance.flat_output_enforced && (
                  <Badge variant="outline" className="bg-stone-100 dark:bg-[rgba(84,106,93,0.25)] text-stone-600 dark:text-[#C7DDD0] border-stone-300 dark:border-[rgba(142,218,182,0.15)] text-xs">
                    Flat Output Enforced
                  </Badge>
                )}
                {methodResolution.governance.mismatch_prevention && (
                  <Badge variant="outline" className="bg-stone-100 dark:bg-[rgba(84,106,93,0.25)] text-stone-600 dark:text-[#C7DDD0] border-stone-300 dark:border-[rgba(142,218,182,0.15)] text-xs">
                    Mismatch Prevention
                  </Badge>
                )}
                {methodResolution.governance.pi_protection_active && (
                  <Badge variant="outline" className="bg-purple-100 dark:bg-[rgba(147,51,234,0.15)] text-purple-600 dark:text-purple-300 border-purple-300 dark:border-[rgba(147,51,234,0.25)] text-xs">
                    PI Protection Active
                  </Badge>
                )}
                {methodResolution.governance.legitimate_inquiry_blocking && (
                  <Badge variant="outline" className="bg-blue-100 dark:bg-[rgba(59,130,246,0.15)] text-blue-600 dark:text-blue-300 border-blue-300 dark:border-[rgba(59,130,246,0.25)] text-xs">
                    Inquiry Validation Active
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Fallback: Original summary if no method resolution */}
      {packages.length === 0 && (
      <div className="bg-white dark:bg-[rgba(10,38,28,0.65)] border border-stone-200 dark:border-[rgba(142,218,182,0.15)] rounded-xl divide-y divide-slate-100 dark:divide-[rgba(142,218,182,0.08)]">
        <div className="p-4">
          <h4 className="text-sm font-medium text-stone-500 dark:text-[#93AE9F]">Dispute Type</h4>
          <p className="text-lg font-semibold text-stone-900 dark:text-[#EAF7F0] flex items-center gap-2 mt-1">
            {disputeType === 'metro2' ? <Database size={20} className="text-emerald-600" /> : <FileText size={20} className="text-emerald-600" />}
            {disputeType === 'metro2' ? 'Metro 2 Technical Dispute' : 'Factual Dispute'}
          </p>
        </div>
        
        <div className="p-4">
          <h4 className="text-sm font-medium text-stone-500 dark:text-[#93AE9F]">Items Selected</h4>
          <p className="text-lg font-semibold text-stone-900 dark:text-[#EAF7F0] mt-1">
            {proceedingSelectedItems.accounts.length} accounts, {proceedingSelectedItems.inquiries.length} inquiries
            {proceedingSelectedItems.personalInfo?.length > 0 && `, ${proceedingSelectedItems.personalInfo.length} personal info`}
          </p>
        </div>
        
        <div className="p-4">
          <h4 className="text-sm font-medium text-stone-500 dark:text-[#93AE9F]">Bureaus</h4>
          <div className="flex gap-2 mt-1">
            {Object.entries(bureauCounts).map(([bureau, count]) => (
              <Badge key={bureau} variant="outline" className={BUREAU_COLORS[bureau]}>
                {bureau}: {count} item{count !== 1 ? 's' : ''}
              </Badge>
            ))}
          </div>
        </div>
        
        {showDeliveryFormatChoice && (
          <div className="p-4">
            <h4 className="text-sm font-medium text-stone-500 dark:text-[#93AE9F]">Delivery Format</h4>
            <p className="text-lg font-semibold text-stone-900 dark:text-[#EAF7F0] mt-1">
              {deliveryFormat === 'combined' ? 'Combined Letter Package' : 'Separate Letters'}
            </p>
          </div>
        )}
        
        {disputeContext && (
          <div className="p-4">
            <h4 className="text-sm font-medium text-stone-500 dark:text-[#93AE9F]">Additional Context</h4>
            <p className="text-sm text-stone-700 dark:text-[#C7DDD0] mt-1">{disputeContext}</p>
          </div>
        )}
      </div>
      )}

      <div className="bg-emerald-50 dark:bg-[rgba(16,185,129,0.12)] border border-emerald-200 dark:border-[rgba(16,185,129,0.20)] rounded-xl p-3 sm:p-4 min-w-0">
        <div className="flex items-start gap-3 min-w-0">
          <CheckCircle size={20} className="text-emerald-600 dark:text-emerald-400 mt-0.5 shrink-0" />
          <div className="min-w-0 flex-1">
            <h4 className="font-semibold text-emerald-950 dark:text-[#EAF7F0]">Ready to Generate</h4>
            <p className="text-sm text-emerald-800 dark:text-emerald-300 break-words leading-relaxed">
              Click "Generate Dispute Letters" to create your letters. This will use our dispute engine 
              to analyze your items and generate professional dispute letters.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
  };

  // Step 3: Generating - Modern progress experience with auto-advance
  const renderGenerating = () => {
    // Calculate counts for the progress display
    const selectedAccountCount = proceedingSelectedItems.accounts.length;
    const selectedInquiryCount = proceedingSelectedItems.inquiries.length;
    const selectedPiCount = proceedingSelectedItems.personalInfo.length;
    
    // Determine effective letter type
    const pathType = searchParams.get('type') || (location.pathname.includes('metro2') ? 'metro2' : 'factual');
    let effectiveLetterType = pathType;
    
    if (selectedMethod === 'data_breach') {
      effectiveLetterType = 'data_breach';
    } else if (aiDecision?.recommended_method) {
      const methodToLetterType = {
        'factual_inaccuracy': 'factual',
        'meth_factual_inaccuracy': 'factual',
        'debt_validation': 'debt_validation',
        'meth_debt_validation': 'debt_validation',
        'metro2_technical': 'metro2',
        'meth_metro2_technical': 'metro2',
        'unauthorized_inquiry': 'inquiry',
        'meth_unauthorized_inquiry': 'inquiry',
        'identity_theft': 'identity_theft',
        'meth_identity_theft': 'identity_theft',
        'goodwill_adjustment': 'goodwill',
        'meth_goodwill_adjustment': 'goodwill',
        'data_breach_method': 'data_breach',
        'data_breach': 'data_breach',
        'meth_data_breach': 'data_breach',
        // CRITICAL: Add isolated, structural, recovery methods
        'isolated_method': 'isolated',
        'isolated': 'isolated',
        'meth_isolated': 'isolated',
        'structural_method': 'structural',
        'structural': 'structural',
        'meth_structural': 'structural',
        'recovery_method': 'recovery',
        'recovery': 'recovery',
        'meth_recovery': 'recovery',
        'control_file_method': 'control_file',
        'control_file': 'control_file',
      };
      effectiveLetterType = methodToLetterType[aiDecision.recommended_method] || pathType;
    }
    
    // Build the generation request for real backend progress
    const buildGenerationRequest = () => {
      // CRITICAL: Use PI anchor confirmations for consumer identity when available
      // This ensures the letter uses the PROTECTED anchor values, not potentially inaccurate report data
      
      // Get address anchor confirmation if exists
      const addressAnchor = piAnchorConfirmations.find(conf => conf.category === 'address');
      const nameAnchor = piAnchorConfirmations.find(conf => conf.category === 'legal_name');
      const dobAnchor = piAnchorConfirmations.find(conf => conf.category === 'dob');
      
      // Use anchor values when available, fall back to report data
      let consumerName = nameAnchor?.correct_value || reportData?.personal_info?.name || user?.name || '';
      let consumerAddress = '';
      let cityStateZip = '';
      
      // Address handling with anchor priority - USE STRUCTURED FIELDS
      if (addressAnchor) {
        // PRIORITY 1: Use normalized_address if available (has all structured fields)
        if (addressAnchor.normalized_address) {
          const norm = addressAnchor.normalized_address;
          // Combine street + unit for consumerAddress
          consumerAddress = norm.street_address || '';
          if (norm.unit) {
            consumerAddress += ' ' + norm.unit;
          }
          cityStateZip = `${norm.city || ''}, ${norm.state || ''} ${norm.zip || ''}`.trim();
        }
        // PRIORITY 2: Use manual_entry_values structured fields
        else if (addressAnchor.manual_entry_values) {
          const manual = addressAnchor.manual_entry_values;
          consumerAddress = manual.street || '';
          if (manual.unit) {
            consumerAddress += ' ' + manual.unit;
          }
          cityStateZip = `${manual.city || ''}, ${manual.state || ''} ${manual.zip || ''}`.trim();
        }
        // PRIORITY 3: Fall back to correct_value string parsing
        else if (addressAnchor.correct_value) {
          const addressValue = addressAnchor.correct_value;
          consumerAddress = addressValue;
          // Try to extract CSZ from full address (last part after comma)
          const parts = addressValue.split(',');
          if (parts.length > 2) {
            consumerAddress = parts.slice(0, -2).join(',').trim();
            cityStateZip = parts.slice(-2).join(',').trim();
          } else if (parts.length === 2) {
            consumerAddress = parts[0].trim();
            cityStateZip = parts[1].trim();
          }
        }
      } else {
        // Fall back to report data or personalInfoItems
        // Try reportData first
        let addr = reportData?.personal_info?.current_address || 
          (reportData?.addresses?.[0]?.full_address) || '';
        let csz = reportData?.personal_info?.city_state_zip || 
          reportData?.addresses?.[0]?.city_state_zip || '';
        
        // If still empty, try to get from personalInfoItems
        if (!addr || !csz) {
          const currentAddressItem = personalInfoItems.find(pi => pi.field === 'current_address');
          if (currentAddressItem?.values) {
            // Get the first non-null value from any bureau
            const bureauValues = currentAddressItem.values;
            const firstValue = bureauValues.TransUnion || bureauValues.Experian || bureauValues.Equifax || '';
            if (firstValue && !addr) {
              // Parse address: typically "123 MAIN ST CITY, ST 12345"
              const addressMatch = firstValue.match(/^(.+?)\s+([A-Z]+),?\s*([A-Z]{2})\s+(\d{5}(-\d{4})?)$/i);
              if (addressMatch) {
                addr = addressMatch[1];
                csz = `${addressMatch[2]}, ${addressMatch[3]} ${addressMatch[4]}`;
              } else {
                // Can't parse - use full string as address
                addr = firstValue;
              }
            }
          }
        }
        
        consumerAddress = addr;
        cityStateZip = csz;
      }
      
      // Get DOB from anchor or report
      const consumerDob = dobAnchor?.correct_value || reportData?.personal_info?.dob || '';
      
      if (DEBUG_DISPUTE_SELECTION) {
        console.log('[GENERATION] Using consumer info:', {
          name: consumerName,
          address: consumerAddress,
          cityStateZip: cityStateZip,
          dob: consumerDob,
          addressFromAnchor: !!addressAnchor?.correct_value,
          nameFromAnchor: !!nameAnchor?.correct_value,
          dobFromAnchor: !!dobAnchor?.correct_value
        });
      }
      
      // Build dispute items from selected items
      const disputeItems = [
        ...negativeAccounts
          .filter(a => proceedingSelectedItems.accounts.includes(a.id))
          .map(acc => ({
            // Core identification
            item_id: acc.id,
            item_type: 'account',
            creditor_name: acc.creditorName || acc.creditor_name || acc.account_name || acc.name,
            account_number: acc.accountNumber || acc.account_number || acc.account_number_masked || '',
            bureau_data: acc.bureau_data || {},
            payment_history_grid: acc.payment_history_grid || {},
            payment_history: acc.payment_history || {},
            payment_history_summary: acc.payment_history_summary || {},
            // Account classification - CRITICAL for proper letter sections
            account_type: acc.accountType || acc.account_type || 'Revolving',
            account_type_detail: acc.account_type_detail || acc.accountTypeDetail || '',
            account_status: acc.accountStatus || acc.account_status || acc.payment_status || '',
            payment_status: acc.paymentStatus || acc.payment_status || '',
            // Financial details - REQUIRED for letter content
            balance: acc.balance || acc.currentBalance || acc.current_balance || '',
            past_due: acc.past_due || acc.pastDue || acc.amount_past_due || acc.amountPastDue || '',
            high_credit: acc.high_credit || acc.highCredit || acc.credit_limit || '',
            credit_limit: acc.credit_limit || acc.creditLimit || '',
            // Original creditor for collections
            original_creditor: acc.original_creditor || acc.originalCreditor || '',
            // Dates - important for dispute timeline
            date_opened: acc.date_opened || acc.dateOpened || acc.open_date || '',
            date_reported: acc.date_reported || acc.dateReported || '',
            date_of_first_delinquency: acc.date_of_first_delinquency || acc.dofd || '',
            last_payment_date: acc.last_payment_date || acc.lastPaymentDate || '',
            // Status flags for section bucketing
            is_chargeoff: acc.is_chargeoff || 
              (acc.payment_status || '').toLowerCase().includes('chargeoff') ||
              (acc.payment_status || '').toLowerCase().includes('charge-off') ||
              (acc.account_status || '').toLowerCase().includes('chargeoff'),
            is_collection: acc.is_collection ||
              (acc.account_type_detail || '').toLowerCase().includes('collection') ||
              (acc.payment_status || '').toLowerCase().includes('collection'),
            // Late payment details
            late_payment_count: acc.late_payment_count || acc.latePaymentCount || 0,
            detected_late_months: acc.detected_late_months || acc.late_months || [],
            // Bureau-specific data
            bureaus: normalizeBureausList(acc.bureaus_reported || acc.bureaus || [acc.bureau || 'Equifax']),
            bureau_comparison: acc.bureau_comparison || {},
            // Dispute context
            dispute_reason: disputeContext || 'Inaccurate information',
            method_assignment: methodResolution?.item_assignments?.[acc.id] || null,
            // Include raw tradeline data for comprehensive letter generation
            raw_tradeline: acc,
          })),
        ...hardInquiries
          .filter(i => proceedingSelectedItems.inquiries.includes(i.id))
          .map(inq => ({
            item_id: inq.id,
            item_type: 'inquiry',
            creditor_name: inq.creditor_name || inq.creditor || inq.company_name || inq.inquiry_name || 'Unknown',
            inquiry_date: inq.date || inq.inquiry_date || '',
            inquiry_type: inq.inquiry_type || inq.type || 'Hard Inquiry',
            bureau: normalizeBureau(inq.bureau || 'Equifax'),
            bureaus: [normalizeBureau(inq.bureau || 'Equifax')],
            dispute_reason: 'Unauthorized inquiry - I did not authorize this credit check',
            method_assignment: methodResolution?.item_assignments?.[inq.id] || null,
          })),
        ...personalInfoItems
          .filter(pi => proceedingSelectedItems.personalInfo.includes(pi.id))
          .map(pi => ({
            item_id: pi.id,
            item_type: 'personal_info',
            field_type: pi.field,
            field_label: pi.fieldLabel,
            values: pi.values || {
              TransUnion: pi.tu_value || pi.transunion || '',
              Experian: pi.ex_value || pi.experian || '',
              Equifax: pi.eq_value || pi.equifax || '',
            },
            bureau_values: pi.values || {
              TransUnion: pi.tu_value || pi.transunion || '',
              Experian: pi.ex_value || pi.experian || '',
              Equifax: pi.eq_value || pi.equifax || '',
            },
            tu_value: pi.values?.TransUnion || pi.values?.transunion || pi.tu_value || pi.transunion,
            ex_value: pi.values?.Experian || pi.values?.experian || pi.ex_value || pi.experian,
            eq_value: pi.values?.Equifax || pi.values?.equifax || pi.eq_value || pi.equifax,
            reported_value: '',
            disputed_value: '',
            correct_value: '',
            bureaus: normalizeBureausList(pi.bureaus || ['TransUnion', 'Experian', 'Equifax']),
            dispute_reason: 'Inaccurate personal information - does not belong to me or is incorrect',
            method_assignment: methodResolution?.item_assignments?.[pi.id] || null,
          })),
      ];
      
      // Build decision output from AI decision
      const decisionOutput = aiDecision ? {
        recommended_method: aiDecision.recommended_method,
        allowed_methods: aiDecision.allowed_methods,
        blocked_methods: aiDecision.blocked_methods,
        strategy_recommendation: aiDecision.strategy_recommendation,
        signals: aiDecision.signals,
        warnings: aiDecision.warnings,
        target_bureaus: aiDecision.target_bureaus,
      } : null;
      
      // Get bureaus from selected items
      const targetBureaus = normalizeBureausList([
        ...new Set([
          ...disputeItems.flatMap(item => item.bureaus || [item.bureau]).filter(Boolean)
        ])
      ]);
      
      // CRITICAL: Filter out protected PI anchors BEFORE sending to backend
      const protectedAnchorIds = piAnchorConfirmations.map(conf => conf.correct_item_id);
      let filteredDisputeItems = disputeItems.filter(item => {
        if (item.item_type === 'personal_info') {
          const isProtected = protectedAnchorIds.includes(item.item_id);
          if (DEBUG_DISPUTE_SELECTION && isProtected) {
            console.log('[PI ANCHOR] Excluding protected anchor from generation:', item.item_id, item.field_label);
          }
          return !isProtected;
        }
        return true;
      });

      // Excluded IDs are authoritative: do not re-add assignment-only items.
      
      if (DEBUG_DISPUTE_SELECTION) {
        console.log('[PI ANCHOR] buildGenerationRequest - confirmations:', JSON.stringify(piAnchorConfirmations));
        console.log('[PI ANCHOR] buildGenerationRequest - protected IDs:', protectedAnchorIds);
        console.log('[PI ANCHOR] buildGenerationRequest - original PI count:', disputeItems.filter(i => i.item_type === 'personal_info').length);
        console.log('[PI ANCHOR] buildGenerationRequest - filtered PI count:', filteredDisputeItems.filter(i => i.item_type === 'personal_info').length);
      }

      const dataBreachSelectedCount = filteredDisputeItems.filter(item => {
        const assignment = methodResolution?.item_assignments?.[item.item_id];
        const method = assignment?.assigned_method || item.method_assignment?.assigned_method || '';
        return String(method).includes('data_breach');
      }).length;

      const rebuildMethodGroupsFromAssignments = () => {
        const rawItems = filteredDisputeItems.map((item) => {
          const assignment = methodResolution?.item_assignments?.[item.item_id]
            || item.method_assignment
            || null;
          const account = accountById.get(String(item.item_id));
          const collectionSignals = account ? detectCollectionSignals(account) : {};
          const normalized = normalizeDisputeMethod(
            {
              ...item,
              ...account,
              is_collection: item.is_collection ?? collectionSignals.is_collection,
              original_creditor:
                item.original_creditor
                || collectionSignals.original_creditor
                || account?.original_creditor
                || account?.originalCreditor,
            },
            assignment
          );
          return {
            ...item,
            id: String(item.item_id),
            disputeFamily: normalized.disputeFamily,
            dispute_family: normalized.disputeFamily,
            method: normalized.method,
            resolved_method: normalized.method,
            isCollection: normalized.isCollection,
            is_collection: normalized.isCollection,
            assignment,
          };
        });
        return toMethodResolutionGroups(groupPackagesByCanonicalMethod(rawItems));
      };

      const resolvedMethodGroups = rebuildMethodGroupsFromAssignments();
      const structuralCheck = assertNoCollectionsInStructural(resolvedMethodGroups);
      if (!structuralCheck.ok && process.env.NODE_ENV !== 'production') {
        console.debug('[PACKAGE GROUPING] Moved collection offenders out of structural during rebuild', {
          offenderCount: structuralCheck.offenders.length,
        });
      }

      // Re-apply canonical method onto each dispute item for the payload
      const disputeItemsForGeneration = filteredDisputeItems.map((item) => {
        const assignment = methodResolution?.item_assignments?.[item.item_id]
          || item.method_assignment
          || null;
        const account = accountById.get(String(item.item_id));
        const collectionSignals = account ? detectCollectionSignals(account) : {};
        const normalized = normalizeDisputeMethod(
          {
            ...item,
            ...account,
            is_collection: item.is_collection ?? collectionSignals.is_collection,
            original_creditor:
              item.original_creditor
              || collectionSignals.original_creditor
              || account?.original_creditor
              || account?.originalCreditor,
          },
          assignment
        );
        return {
          ...item,
          dispute_family: normalized.disputeFamily,
          method: normalized.method,
          resolved_method: normalized.method,
          is_collection: normalized.isCollection,
          method_assignment: {
            ...(assignment || {}),
            assigned_method: normalized.method,
            dispute_family: normalized.disputeFamily,
          },
        };
      });

      const packageGroupingPreview = resolvedMethodGroups.map(group => ({
        method_key: group.method_key,
        item_ids: (group.items || []).map(entry => entry?.item_id || entry?.id || entry),
      }));

      const selectedItemIdsForGeneration = disputeItemsForGeneration.map((item) => String(item.item_id));
      const uiProceedingCount = proceedingItemsCount;
      const selectedIdsCount = selectedItemIdsForGeneration.length;
      const disputeItemsCount = disputeItemsForGeneration.length;
      if (uiProceedingCount !== selectedIdsCount || uiProceedingCount !== disputeItemsCount) {
        console.error('[GEN REQUEST BLOCKED] count mismatch', {
          uiProceedingCount,
          selectedIdsCount,
          disputeItemsCount,
        });
        throw new Error('Generation request count mismatch before POST');
      }
      if (DEBUG_DISPUTE_SELECTION) {
        console.debug('[GEN REQUEST] selected_item_ids', selectedItemIdsForGeneration);
        console.debug('[GEN REQUEST] method_resolution', {
          is_mixed: methodResolution?.summary?.is_mixed,
          method_groups: resolvedMethodGroups.map(g => g.method_key),
          assignment_count: Object.keys(methodResolution?.item_assignments || {}).length,
        });
        console.debug('[GEN REQUEST] package_grouping', packageGroupingPreview);
        console.debug('[GEN REQUEST] data_breach selected count', dataBreachSelectedCount);
      }

      const isProceedingSelected = (itemId, selectedIds) => selectedIds.includes(itemId);

      if (DEBUG_DISPUTE_SELECTION) {
        const methodGroupCounts = resolvedMethodGroups.map((group) => ({
          method: group.method_key,
          count: (group.items || []).length,
        }));
        console.debug('[GEN REQUEST FINAL CONSISTENCY]', {
          uiProceedingCount,
          selectedItemIdsCount: selectedIdsCount,
          disputeItemsCount,
          accountsCount: proceedingSelectedItems.accounts.length,
          inquiriesCount: proceedingSelectedItems.inquiries.length,
          personalInfoCount: proceedingSelectedItems.personalInfo.length,
          methodGroupCounts,
          packageGroupCounts: packageGroupingPreview.map((group) => ({ method: group.method_key, count: group.item_ids.length })),
          expectedTotals: {
            total_items: disputeItemsCount,
            total_packages: resolvedMethodGroups.length,
          },
        });
      }

      const generationPayload = {
        selected_item_ids: selectedItemIdsForGeneration,
        consumer_name: consumerName,
        consumer_address: consumerAddress,
        consumer_city_state_zip: cityStateZip,
        consumer_ssn_last4: reportData?.personal_info?.ssn_last4 || '',
        consumer_dob: consumerDob,
        bureaus: targetBureaus.length > 0 ? targetBureaus : ['Equifax', 'Experian', 'TransUnion'],
        dispute_items: disputeItemsForGeneration,
        letter_type: (methodResolution?.summary?.is_mixed || methodResolution?.is_mixed)
          ? 'mixed'
          : effectiveLetterType,
        decision_output: decisionOutput,
        method_resolution: methodResolution ? {
          is_mixed: methodResolution.summary?.is_mixed || false,
          primary_method: methodResolution.summary?.primary_method,
          unique_method_count: methodResolution.summary?.unique_method_count || 1,
          method_groups: resolvedMethodGroups,
          item_assignments: Object.fromEntries(
            disputeItemsForGeneration.map((item) => [
              String(item.item_id),
              {
                ...(methodResolution.item_assignments?.[item.item_id] || {}),
                item_id: item.item_id,
                assigned_method: item.method,
                dispute_family: item.dispute_family,
                is_collection: item.is_collection,
              },
            ])
          ),
        } : {
          is_mixed: resolvedMethodGroups.length > 1,
          primary_method: resolvedMethodGroups[0]?.method_key || null,
          unique_method_count: resolvedMethodGroups.length,
          method_groups: resolvedMethodGroups,
          item_assignments: Object.fromEntries(
            disputeItemsForGeneration.map((item) => [
              String(item.item_id),
              {
                item_id: item.item_id,
                assigned_method: item.method,
                dispute_family: item.dispute_family,
                is_collection: item.is_collection,
              },
            ])
          ),
        },
        excluded_items: [],
        blocked_items: [],
        round_id: roundData?.round_id,
        tenant_id: user?.tenant_id || null,
        // PI Anchor Protection data - send to backend for validation
        pi_anchor_confirmations: piAnchorConfirmations.map(conf => ({
          category: conf.category,
          anchor_source: conf.anchor_source || 'listed',  // 'listed' or 'manual'
          correct_item_id: conf.correct_item_id,
          correct_value: conf.correct_value,
          manual_entry_values: conf.manual_entry_values || null,
          normalized_address: conf.normalized_address || null,
          has_supporting_docs: conf.has_supporting_docs,
        })),
        // Protected anchor values for letter identity - include STRUCTURED address fields
        protected_consumer_info: {
          name: consumerName,
          address: consumerAddress,
          city_state_zip: cityStateZip,
          dob: consumerDob,
          address_from_anchor: !!addressAnchor,
          name_from_anchor: !!nameAnchor?.correct_value,
          dob_from_anchor: !!dobAnchor?.correct_value,
          // CRITICAL: Include structured address fields for Step 7 prefill
          structured_address: (() => {
            if (!addressAnchor) return null;
            
            // Try to get structured fields from multiple sources
            const na = addressAnchor.normalized_address || {};
            const mv = addressAnchor.manual_entry_values || {};
            
            if (process.env.NODE_ENV !== 'production') {
              console.debug('[PROTECTED_CONSUMER_INFO] Building structured_address', {
                hasNormalizedAddress: Boolean(na),
                hasManualEntry: Boolean(mv),
                hasCorrectValue: Boolean(addressAnchor.correct_value),
              });
            }
            
            return {
              street_address: na.street_address || mv.street || '',
              unit: na.unit || mv.unit || '',
              city: na.city || mv.city || '',
              state: na.state || mv.state || '',
              zip: na.zip || mv.zip || '',
              source: 'identity_anchor',
            };
          })(),
        },
        // CRITICAL: Pass expected totals from Generation Plan preview
        // Arrays are the source of truth — no parallel count fields.
        expected_totals: buildDurableExpectedTotals({
          packages: generationPlanPreview?.packages || [],
          methodGroups: resolvedMethodGroups,
        }),
        // CRITICAL: Pass selected PI reference data for assigned + checkbox-selected items
        full_pi_data: personalInfoItems
          .filter(pi => isProceedingSelected(pi.id, proceedingSelectedItems.personalInfo))
          .map(pi => ({
          id: pi.id,
          field: pi.field || pi.fieldLabel || pi.fieldName,
          field_label: pi.fieldLabel || pi.field || pi.fieldName,
          field_type: pi.field || pi.fieldLabel,
          fieldLabel: pi.fieldLabel || pi.field,
          // Pass the entire values dict for bureau-specific values
          values: pi.values || {
            TransUnion: pi.tu_value || pi.transunion,
            Experian: pi.ex_value || pi.experian,
            Equifax: pi.eq_value || pi.equifax,
          },
          // Also pass individual values for backwards compatibility
          tu_value: pi.values?.TransUnion || pi.values?.transunion || pi.tu_value || pi.transunion,
          ex_value: pi.values?.Experian || pi.values?.experian || pi.ex_value || pi.experian,
          eq_value: pi.values?.Equifax || pi.values?.equifax || pi.eq_value || pi.equifax,
          protected_value: pi.protected_value || pi.correct_value,
          correct_value: pi.correct_value || pi.protected_value,
          bureaus: pi.bureaus,
          status: pi.status,
        })),
        // CRITICAL: Pass full tradeline data for assigned + checkbox-selected accounts
        full_account_data: negativeAccounts
          .filter(a => isProceedingSelected(a.id, proceedingSelectedItems.accounts))
          .map(acc => ({
            id: acc.id,
            item_id: acc.id,
            creditor_name: acc.creditorName || acc.creditor_name || acc.account_name || acc.name,
            account_number: acc.accountNumber || acc.account_number || acc.account_number_masked || '',
            bureau_data: acc.bureau_data || {},
            bureau_comparison: acc.bureau_comparison || {},
            payment_history_grid: acc.payment_history_grid || {},
            bureaus_reported: acc.bureaus_reported || acc.bureaus || [],
            raw_tradeline: acc,
          })),
        // CRITICAL: Pass full inquiry data for assigned + checkbox-selected inquiries
        full_inquiry_data: hardInquiries
          .filter(i => isProceedingSelected(i.id, proceedingSelectedItems.inquiries))
          .map(inq => ({
            id: inq.id,
            inquiry_id: inq.inquiry_id || inq.id,
            // CRITICAL: creditor_name is the primary field for inquiry creditor in most reports
            company_name: inq.creditor_name || inq.company_name || inq.inquiry_name || inq.creditor || '',
            creditor_name: inq.creditor_name || inq.company_name || inq.inquiry_name || inq.creditor || '',
            inquiry_name: inq.inquiry_name || inq.creditor_name || inq.company_name || '',
            inquiry_date: inq.inquiry_date || inq.date || '',
            date: inq.date || inq.inquiry_date || '',
            inquiry_type: inq.inquiry_type || inq.type || 'Hard Inquiry',
            bureau: inq.bureau || '',
            bureau_code: inq.bureau_code || inq.bureau || '',
            business_type: inq.business_type || inq.type_of_business || inq.type || '',
          })),
        force_regenerate: true,
        regeneration_nonce: regenerationNonce,
      };

      if (DEBUG_DISPUTE_SELECTION) {
        console.debug('[GEN REQUEST] final POST body', {
          dispute_item_count: generationPayload.dispute_items.length,
          method_groups: generationPayload.method_resolution?.method_groups?.map(g => g.method_key),
          expected_totals: generationPayload.expected_totals,
          bureaus: generationPayload.bureaus,
        });
      }

      if (process.env.NODE_ENV !== 'production') {
        console.debug('[DURABLE GENERATE REQUEST]', {
          roundId: generationPayload.round_id,
          itemCount: generationPayload.dispute_items?.length || 0,
          packageCount: generationPayload.method_resolution?.method_groups?.length || 0,
          methods: (generationPayload.method_resolution?.method_groups || []).map((g) => g.method_key),
          hasConsumerInfo: Boolean(generationPayload.consumer_name && generationPayload.consumer_address),
          hasStructuredAddress: Boolean(generationPayload.protected_consumer_info?.structured_address),
        });
      }

      return generationPayload;
    };
    
    // Handle generation result from SSE
    const handleGenerationResult = (result) => {
      console.log('[ITEM SELECTION] Received generation result:', result);
      setGeneratedLetters(result?.packages || []);
    };
    
    // Handle auto-advance when generation completes
    const handleGenerationComplete = async (result) => {
      setGenerationError(null);
      if (result?.generation_debug) {
        console.log('[GEN DEBUG]', {
          run_id: result.run_id,
          commit: result.generation_debug.commit,
          final_plan_methods: result.generation_debug.final_plan_methods,
          queued_methods: result.generation_debug.queued_methods,
          generated_methods: result.generation_debug.generated_methods,
          total_letters: result.generation_debug.total_letters ?? result.total_letters,
          letters_by_method: result.generation_debug.letters_by_method,
          blocked_methods: result.generation_debug.blocked_methods,
        });
      }
      logGenerationResultAudit(result, 'GENERATION COMPLETE');

      let resolvedResult = normalizeGenerationResult(result, { logDrops: true });
      const resultSource = result?.session_id ? 'sse_inline' : 'generation_complete';

      if (!isFinalPackageReady(resolvedResult)) {
        const apiUrl = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/api$/, '');
        const sessionId = result?.session_id || sessionStorage.getItem(`generation_session_${roundData?.round_id}`);
        const runId =
          result?.run_id ||
          (sessionId ? sessionStorage.getItem(`generation_run_${sessionId}`) : null);

        resolvedResult = await pollUntilLettersAvailable({
          apiUrl,
          sessionId,
          useDurable: true,
          roundId: roundData?.round_id,
          runId,
          forceRegenerate: true,
          getAuthHeaders,
        });
        if (resolvedResult) {
          resolvedResult = normalizeGenerationResult(resolvedResult, { logDrops: true });
          logGenerationResultAudit(resolvedResult, 'GENERATION COMPLETE (polled)');
          logStep5ResultCounts(resolvedResult, resolvedResult._result_source || 'durable_result', {
            run_id: runId,
            roundId: roundData?.round_id,
          });
        }
      } else {
        logStep5ResultCounts(resolvedResult, resultSource, {
          run_id: result?.run_id,
          roundId: roundData?.round_id,
        });
      }

      if (!resolvedResult || !isFinalPackageReady(resolvedResult)) {
        logGenerationResultAudit(resolvedResult, 'GENERATION COMPLETE (failed)');
        setGenerationError(packageReconciliationFailureMessage(resolvedResult));
        return;
      }

      // Do not gate navigation on stale preview totalLetters vs generated count.
      // Backend is_complete / missing_package_ids / duplicate_package_ids are authoritative.

      console.log('[GENERATION COMPLETE] Navigating to preview with result');

      // Determine route — mixed packages use generic preview so all methods render
      let targetRoute = effectiveLetterType;
      const methodsInResult = Object.keys(lettersByMethodFromResult(resolvedResult));
      const isMixedResult =
        methodResolution?.summary?.is_mixed ||
        methodsInResult.length > 1 ||
        (methodResolution?.summary?.unique_method_count || 0) > 1;

      const resultMethod =
        resolvedResult.packages?.[0]?.letter?.method_used ||
        resolvedResult.letters?.[0]?.method_used ||
        resolvedResult.packages?.[0]?.method_used ||
        aiDecision?.recommended_method;

      const methodToRoute = {
        isolated_method: 'isolated',
        isolated: 'isolated',
        meth_isolated: 'isolated',
        structural_method: 'structural',
        structural: 'structural',
        meth_structural: 'structural',
        recovery_method: 'recovery',
        recovery: 'recovery',
        meth_recovery: 'recovery',
        data_breach_method: 'data_breach',
        data_breach: 'data_breach',
        factual_inaccuracy: 'factual',
        factual: 'factual',
        meth_factual_inaccuracy: 'factual',
      };

      if (isMixedResult) {
        targetRoute = 'preview';
        console.log('[GENERATION COMPLETE] Mixed result — using generic preview route', methodsInResult);
      } else if (resultMethod && methodToRoute[resultMethod]) {
        targetRoute = methodToRoute[resultMethod];
        console.log('[GENERATION COMPLETE] Mapped method to route:', resultMethod, '->', targetRoute);
      }

      const sessionKey = `dispute_wizard_${resolvedResult.session_id || Date.now()}`;
      try {
        sessionStorage.setItem(
          sessionKey,
          JSON.stringify({
            preGeneratedResult: resolvedResult,
            roundId: roundData?.round_id,
            letterType: isMixedResult ? 'mixed' : targetRoute,
            timestamp: Date.now(),
            run_id: resolvedResult.run_id,
          })
        );
        console.log('[GENERATION COMPLETE] Saved to sessionStorage:', sessionKey);
      } catch (e) {
        console.warn('[GENERATION COMPLETE] Failed to save to sessionStorage:', e);
      }

      const identityAnchorData = buildIdentityAnchorDataForCompletion({
        resolvedResult,
        result,
        reportData,
        user,
        piAnchorConfirmations,
      });

      console.log('[GENERATION COMPLETE] Built identityAnchorData for Step 7:', identityAnchorData);

      const previewPath =
        targetRoute === 'preview'
          ? '/dispute-wizard/preview'
          : `/dispute-wizard/${targetRoute}/preview`;

      trackWizardStep({
        metro2Service,
        roundId: roundData?.round_id,
        token,
        currentStep: 'preview',
        lastCompletedStep: 'select_items',
        wizardStatus: 'letters_generated',
        navigationState: {
          roundId: roundData?.round_id,
          letterType: isMixedResult ? 'mixed' : targetRoute,
          preGeneratedResult: resolvedResult,
        },
      });

      navigate(previewPath, {
        state: {
          preGeneratedResult: resolvedResult,
          roundId: roundData?.round_id,
          letterType: isMixedResult ? 'mixed' : targetRoute,
          sessionKey,
          fromGeneration: true,
          identityAnchor: identityAnchorData,
          consumerInfo: resolvedResult.consumer_info,
          methodResolution,
          isMixed: isMixedResult,
        },
        replace: true,
      });
    };

    buildGenerationRequestFnRef.current = buildGenerationRequest;

    let generationPayload = step3GenerationPayloadRef.current;
    if (methodResolution && roundData?.round_id) {
      generationPayload = buildGenerationRequest();
      step3GenerationPayloadRef.current = generationPayload;
    }

    if (
      generationPayload &&
      roundData?.round_id &&
      generatePayloadLoggedForRoundRef.current !== roundData.round_id
    ) {
      generatePayloadLoggedForRoundRef.current = roundData.round_id;
      const disputeItems = generationPayload.dispute_items || [];
      console.log('[GENERATE CLICK PAYLOAD]', {
        selectedItems,
        effectiveSelectedItems,
        generationPlanFromReview: reviewPlanSnapshotRef.current,
        methodResolution: generationPayload.method_resolution,
        itemAssignments: generationPayload.method_resolution?.item_assignments || {},
        dispute_items_count: disputeItems.length,
        full_inquiry_data_count: generationPayload.full_inquiry_data?.length || 0,
        full_pi_data_count: generationPayload.full_pi_data?.length || 0,
        expected_total_letters_from_review:
          reviewPlanSnapshotRef.current?.totalLetters ??
          generationPayload.expected_totals?.expected_package_ids?.length ??
          resolvedGenerationSummary.totalLetters,
        expected_totals: generationPayload.expected_totals,
        method_groups: generationPayload.method_resolution?.method_groups?.map((g) => g.method_key),
      });
    }

    return (
      <div className="space-y-4">
        {generationError && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4 flex items-start gap-3">
            <AlertCircle className="text-red-600 shrink-0 mt-0.5" size={20} />
            <div className="flex-1">
              <p className="font-medium text-red-800">{generationError}</p>
              <Button
                className="mt-3 bg-emerald-600 hover:bg-emerald-700"
                size="sm"
                onClick={() => {
                  setGenerationError(null);
                  step3GenerationPayloadRef.current = null;
                  setRegenerationNonce((n) => n + 1);
                  try {
                    Object.keys(sessionStorage)
                      .filter((key) => key.startsWith('generation_'))
                      .forEach((key) => sessionStorage.removeItem(key));
                  } catch (_) {
                    // ignore storage errors
                  }
                }}
              >
                Retry Generate Letters
              </Button>
            </div>
          </div>
        )}
        <div className="w-full max-w-full min-w-0 overflow-x-hidden">
        <LetterGenerationProgress
        key={`gen-${roundData?.round_id || 'pending'}-${regenerationNonce}`}
        selectedAccountCount={selectedAccountCount}
        selectedInquiryCount={selectedInquiryCount}
        selectedPiCount={selectedPiCount}
        totalItems={resolvedGenerationSummary.totalItems}
        totalPackages={resolvedGenerationSummary.totalPackages}
        totalLetters={resolvedGenerationSummary.totalLetters}
        roundId={roundData?.round_id || ''}
        isGenerating={loading || currentStep === 3}
        onComplete={handleGenerationComplete}
        onGenerationResult={handleGenerationResult}
        generationRequest={generationPayload}
        getAuthHeaders={getAuthHeaders}
        hideWizardStepper={true}
        onBackToReview={() => {
          setGenerationError(null);
          setCurrentStep(2);
        }}
        onRetryGeneration={() => {
          setGenerationError(null);
          step3GenerationPayloadRef.current = null;
          setRegenerationNonce((n) => n + 1);
        }}
      />
      </div>
      </div>
    );
  };

  // Step 4: Complete
  const renderComplete = () => (
    <div className="space-y-6" data-testid="step-complete">
      <div className="text-center">
        <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle size={32} className="text-emerald-600" />
        </div>
        <h2 className="text-xl font-bold text-stone-900">Dispute Letters Generated!</h2>
        <p className="text-stone-600 mt-2">
          Your dispute letters are ready. Download them or view the preview below.
        </p>
      </div>

      {/* Generated Letters */}
      <div className="space-y-4">
        {generatedLetters.map((pkg, index) => (
          <div key={pkg.package_id || index} className="bg-white dark:bg-[rgba(10,38,28,0.65)] border border-stone-200 dark:border-[rgba(142,218,182,0.15)] rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 dark:bg-[rgba(16,185,129,0.15)] rounded-lg flex items-center justify-center">
                  <FileText size={20} className="text-emerald-600 dark:text-emerald-400" />
                </div>
                <div>
                  <h4 className="font-semibold text-stone-900 dark:text-[#EAF7F0]">
                    {pkg.target_bureau || `Bureau ${index + 1}`}
                  </h4>
                  <p className="text-sm text-stone-600 dark:text-[#C7DDD0]">
                    {pkg.findings_count || 0} item{(pkg.findings_count || 0) !== 1 ? 's' : ''} disputed
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setLetterContent(pkg)}>
                  <Eye size={16} className="mr-1" />
                  Preview
                </Button>
                <Button size="sm" onClick={() => handleDownload(pkg)} className="bg-emerald-600 hover:bg-emerald-700">
                  <Download size={16} className="mr-1" />
                  Download
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Letter Preview */}
      {letterContent?.content && (
        <div className="bg-white dark:bg-[rgba(10,38,28,0.65)] border border-stone-200 dark:border-[rgba(142,218,182,0.15)] rounded-xl overflow-hidden">
          <div className="bg-stone-50 dark:bg-[rgba(8,32,24,0.65)] px-4 py-3 border-b border-stone-200 dark:border-[rgba(142,218,182,0.12)]">
            <h4 className="font-semibold text-stone-900 dark:text-[#EAF7F0]">Letter Preview</h4>
          </div>
          <div 
            className="p-6 prose prose-sm dark:prose-invert max-w-none"
            dangerouslySetInnerHTML={{ __html: letterContent.content }}
          />
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 min-w-0">
        <Button variant="outline" onClick={() => navigate('/disputes')} className="w-full sm:flex-1">
          View All Disputes
        </Button>
        <Button onClick={() => navigate('/dispute-wizard')} className="w-full sm:flex-1 bg-emerald-600 hover:bg-emerald-700">
          Start Another Dispute
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-stone-50 dark:bg-transparent overflow-x-hidden" data-testid="item-selection-page">
      <div className="max-w-4xl mx-auto px-3 sm:px-6 py-6 sm:py-8 w-full min-w-0 pb-8">
        {/* Header */}
        <div className="mb-6">
          <Button 
            variant="ghost" 
            onClick={goBack}
            className="mb-4 text-stone-600 dark:text-[#C7DDD0]"
          >
            <ChevronLeft size={16} className="mr-1" />
            {currentStep === 0 ? 'Back to Wizard' : 'Back'}
          </Button>
          
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
              disputeType === 'metro2' 
                ? 'bg-gradient-to-br from-emerald-500 to-emerald-600' 
                : 'bg-gradient-to-br from-emerald-500 to-emerald-600'
            }`}>
              {disputeType === 'metro2' ? <Database size={20} className="text-white" /> : <FileText size={20} className="text-white" />}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-stone-900 dark:text-[#EAF7F0]">
                {disputeType === 'metro2' ? 'Metro 2 Dispute' : 'Factual Dispute'}
              </h1>
              <p className="text-sm text-stone-600 dark:text-[#C7DDD0]">{mode === 'rounds' ? 'Round-based' : 'Anytime'} dispute flow</p>
            </div>
          </div>
        </div>

        {/* Step Indicator */}
        <StepIndicator currentStep={currentStep} steps={steps} />

        {/* MIXED METHOD BANNER - shown when selection requires multiple methods */}
        {currentStep === 0 && methodResolution?.summary?.is_mixed && (
          <MixedMethodBanner methodResolution={methodResolution} />
        )}

        {/* SMART ROUTING: Strategy Recommendation Banner (uniform method only) */}
        {currentStep === 0 && (smartRoutingData || methodResolution) && (
          <StrategyRecommendationBanner
            classification={classificationResult}
            recommendedStrategy={recommendedStrategy}
            classificationReason={classificationReason}
            warnings={smartRoutingData?.warnings}
            selectedMethod={selectedMethod}
            methodResolution={methodResolution}
            itemCount={
              (preloadedItems?.accounts?.length || 0) + 
              (preloadedItems?.inquiries?.length || 0) +
              (preloadedItems?.publicRecords?.length || 0) +
              (preloadedItems?.personalInfo?.length || 0)
            }
            onChangeStrategy={null} // Could add manual override later
          />
        )}

        {/* Content — lighter padding on Review/Generate so cards have room on mobile */}
        <div className={`bg-white dark:bg-[rgba(10,38,28,0.65)] rounded-2xl border border-stone-200 dark:border-[rgba(142,218,182,0.15)] shadow-sm dark:shadow-[0_12px_32px_rgba(0,0,0,0.35)] min-w-0 overflow-x-hidden ${
          currentStep >= 3 ? 'p-2 sm:p-4 md:p-6' : currentStep === 2 ? 'p-3 sm:p-5 md:p-6' : 'p-4 sm:p-6'
        }`}>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 size={32} className="animate-spin text-emerald-500" />
            </div>
          ) : (
            renderStepContent()
          )}
        </div>

        {/* Footer Navigation - Hidden during generation step (step 3) since it auto-advances */}
        {currentStep > 0 && currentStep < 3 && !loading && !decisionLoading && (
          <div className="mt-4 sm:mt-6 flex flex-col sm:flex-row sm:justify-between gap-3 min-w-0 pb-2">
            <Button variant="outline" onClick={goBack} className="w-full sm:w-auto order-2 sm:order-1">
              <ChevronLeft size={16} className="mr-1" />
              Back
            </Button>
            <Button onClick={goNext} className="w-full sm:w-auto order-1 sm:order-2 bg-emerald-600 hover:bg-emerald-700">
              {currentStep === 2 ? (
                <>
                  <span className="sm:hidden">Generate Letters</span>
                  <span className="hidden sm:inline">Generate Dispute Letters</span>
                </>
              ) : (
                'Continue'
              )}
              <ChevronRight size={16} className="ml-1" />
            </Button>
          </div>
        )}
      </div>
      
      {/* PI Anchor Selection Modal */}
      <PIAnchorSelectionModal
        open={showPIAnchorModal}
        onClose={() => setShowPIAnchorModal(false)}
        selectedPIItems={personalInfoItems.filter(pi => proceedingSelectedItems.personalInfo.includes(pi.id))}
        allPIItems={personalInfoItems}
        onConfirm={handlePIAnchorConfirm}
        existingConfirmations={piAnchorConfirmations}
        savedAddresses={[
          // Only include user's profile address (from Settings) - NOT imported report addresses
          ...(userProfileAddress && userProfileAddress.street_address ? [userProfileAddress] : []),
        ]}
        clientProfileAddress={userProfileAddress || null}
        correctAddress={piAnchorConfirmations.find(c => c.category === 'address')?.correct_value ? {
          street_address: piAnchorConfirmations.find(c => c.category === 'address')?.correct_value || '',
          ...piAnchorConfirmations.find(c => c.category === 'address')?.manual_entry_values,
          source: 'previously_confirmed',
        } : null}
      />
      
      {/* View Prompt Modal - Shows full prompt content before generation */}
      <Dialog open={viewPromptModalOpen} onOpenChange={setViewPromptModalOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-purple-600" />
              <span>Locked Prompt for Generation</span>
            </DialogTitle>
            <DialogDescription>
              This is the exact prompt that will be used to generate the letter for {selectedPromptForView?.bureau || 'this bureau'}.
            </DialogDescription>
          </DialogHeader>
          
          {selectedPromptForView && (
            <div className="flex-1 overflow-y-auto space-y-4">
              {/* Prompt Header Info */}
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="space-y-1">
                    <h4 className="font-semibold text-purple-900">
                      {selectedPromptForView.prompt_title}
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      <Badge className="bg-purple-100 text-purple-700 border-purple-300">
                        ID: {selectedPromptForView.prompt_id}
                      </Badge>
                      {selectedPromptForView.prompt_version && (
                        <Badge className="bg-purple-100 text-purple-700 border-purple-300">
                          v{selectedPromptForView.prompt_version}
                        </Badge>
                      )}
                      <Badge className="bg-purple-600 text-white">
                        <Shield size={12} className="mr-1" />
                        Locked for Generation
                      </Badge>
                    </div>
                  </div>
                  {selectedPromptForView.confidence && (
                    <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300">
                      {(selectedPromptForView.confidence * 100).toFixed(0)}% Confidence
                    </Badge>
                  )}
                </div>
                
                {/* Metadata */}
                <div className="mt-3 grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-purple-600">Bureau:</span>
                    <span className="ml-2 font-medium text-purple-900">{selectedPromptForView.bureau}</span>
                  </div>
                  <div>
                    <span className="text-purple-600">Method:</span>
                    <span className="ml-2 font-medium text-purple-900">{selectedPromptForView.method}</span>
                  </div>
                  <div>
                    <span className="text-purple-600">Family:</span>
                    <span className="ml-2 font-medium text-purple-900">{selectedPromptForView.family}</span>
                  </div>
                </div>
                
                {selectedPromptForView.locked_at && (
                  <div className="mt-2 text-xs text-purple-600">
                    Locked at: {new Date(selectedPromptForView.locked_at).toLocaleString()}
                  </div>
                )}
              </div>
              
              {/* Full Prompt Content */}
              <div className="border dark:border-[rgba(142,218,182,0.15)] rounded-lg overflow-hidden">
                <div className="bg-stone-100 dark:bg-[rgba(8,32,24,0.65)] px-4 py-2 border-b dark:border-[rgba(142,218,182,0.12)] flex items-center gap-2">
                  <FileText size={16} className="text-stone-600 dark:text-[#93AE9F]" />
                  <span className="font-medium text-stone-700 dark:text-[#C7DDD0]">Full Prompt Content</span>
                </div>
                <div className="p-4 bg-white dark:bg-[rgba(10,38,28,0.50)]">
                  {selectedPromptForView.prompt_content ? (
                    <pre className="whitespace-pre-wrap text-sm text-stone-800 dark:text-[#C7DDD0] font-mono leading-relaxed">
                      {selectedPromptForView.prompt_content}
                    </pre>
                  ) : (
                    <div className="text-center py-8 text-stone-500 dark:text-[#93AE9F]">
                      <AlertCircle className="h-8 w-8 mx-auto mb-2 text-amber-500" />
                      <p>Prompt content not available.</p>
                      <p className="text-sm mt-1">The prompt will still be used for generation.</p>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Footer Note */}
              <div className="bg-emerald-50 dark:bg-[rgba(16,185,129,0.12)] border border-emerald-200 dark:border-[rgba(16,185,129,0.20)] rounded-lg p-3 flex items-start gap-2">
                <CheckCircle size={18} className="text-emerald-600 dark:text-emerald-400 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-emerald-800 dark:text-emerald-200">
                  <strong>This is the exact prompt locked for generation.</strong> When you click "Generate Dispute Letters", this prompt will be used to create the letter for {selectedPromptForView.bureau}. No changes can be made to the prompt during generation.
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setViewPromptModalOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Evidence Modal - Shows underlying comparison/evidence data for packages */}
      <Dialog open={viewEvidenceModalOpen} onOpenChange={setViewEvidenceModalOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Database size={20} className="text-emerald-600" />
              Package Evidence View
            </DialogTitle>
            <DialogDescription>
              Underlying comparison and evidence data for {selectedEvidenceForView?.bureau || 'this bureau'} package.
            </DialogDescription>
          </DialogHeader>
          
          {selectedEvidenceForView && (
            <div className="flex-1 overflow-y-auto space-y-4 pr-2">
              {/* Package Header - CRITICAL: Use frozen prompt metadata as source of truth when available */}
              <div className="bg-stone-50 dark:bg-[rgba(10,38,28,0.50)] rounded-lg p-4 border border-stone-200 dark:border-[rgba(142,218,182,0.15)]">
                <div className="flex items-center gap-4 flex-wrap">
                  <div className="flex items-center gap-2">
                    <span className="text-stone-500 dark:text-[#93AE9F] text-sm">Bureau:</span>
                    <Badge variant="outline" className="bg-white dark:bg-[rgba(10,38,28,0.65)] text-stone-800 dark:text-[#EAF7F0] font-semibold">
                      {selectedEvidenceForView.bureau}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-stone-500 dark:text-[#93AE9F] text-sm">Method:</span>
                    <Badge variant="outline" className="bg-emerald-100 dark:bg-[rgba(16,185,129,0.15)] text-emerald-800 dark:text-emerald-300 font-semibold">
                      {/* Use frozen prompt method if available, otherwise fallback to package method */}
                      {(selectedEvidenceForView.matched_prompt?.method || selectedEvidenceForView.method)?.replace(/_/g, ' ').toUpperCase()}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-stone-500 text-sm">Family:</span>
                    <Badge variant="outline" className="bg-blue-100 dark:bg-[rgba(59,130,246,0.15)] text-blue-800 dark:text-blue-300 font-semibold">
                      {/* Use frozen prompt family if available, otherwise fallback to package family */}
                      {(selectedEvidenceForView.matched_prompt?.family || selectedEvidenceForView.family)?.replace(/_/g, ' ').toUpperCase()}
                    </Badge>
                  </div>
                  {/* Show round from frozen prompt if available */}
                  {selectedEvidenceForView.matched_prompt?.round && (
                    <div className="flex items-center gap-2">
                      <span className="text-stone-500 dark:text-[#93AE9F] text-sm">Round:</span>
                      <Badge variant="outline" className="bg-purple-100 dark:bg-[rgba(147,51,234,0.15)] text-purple-800 dark:text-purple-300 font-semibold">
                        {selectedEvidenceForView.matched_prompt.round}
                      </Badge>
                    </div>
                  )}
                </div>
                
                {/* Matched Prompt Summary */}
                {selectedEvidenceForView.matched_prompt && (
                  <div className="mt-3 pt-3 border-t border-stone-200 dark:border-[rgba(142,218,182,0.12)]">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Shield size={14} className="text-purple-600 dark:text-purple-400" />
                        <span className="text-sm font-medium text-purple-800 dark:text-purple-300">Locked Prompt:</span>
                        <span className="text-sm text-purple-700 dark:text-purple-400">{selectedEvidenceForView.matched_prompt.prompt_title}</span>
                      </div>
                      <Badge className="bg-purple-100 dark:bg-[rgba(147,51,234,0.15)] text-purple-700 dark:text-purple-300 text-xs">
                        {selectedEvidenceForView.matched_prompt.prompt_id?.slice(0, 12)}...
                      </Badge>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Evidence Items */}
              {selectedEvidenceForView.evidence_views?.length > 0 ? (
                <div className="space-y-4">
                  {selectedEvidenceForView.evidence_views.map((evidence, idx) => (
                    <div key={idx} className="border dark:border-[rgba(142,218,182,0.15)] rounded-lg overflow-hidden">
                      {/* Account Evidence */}
                      {evidence.type === 'account' && (
                        <>
                          <div className="bg-blue-50 dark:bg-[rgba(59,130,246,0.12)] px-4 py-3 border-b border-blue-200 dark:border-[rgba(59,130,246,0.20)]">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <CreditCard size={16} className="text-blue-600 dark:text-blue-400" />
                                <span className="font-semibold text-blue-900 dark:text-blue-200">{evidence.creditor_name}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="bg-blue-100 dark:bg-[rgba(59,130,246,0.15)] text-blue-700 dark:text-blue-300 text-xs">
                                  {evidence.masked_account_number}
                                </Badge>
                              </div>
                            </div>
                          </div>
                          
                          <div className="p-4 space-y-4">
                            {/* Basic Info Grid */}
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 text-sm">
                              <div className="bg-stone-50 dark:bg-[rgba(10,38,28,0.50)] rounded p-2">
                                <div className="text-stone-500 dark:text-[#93AE9F] text-xs">Account Type</div>
                                <div className="font-medium text-stone-800 dark:text-[#EAF7F0]">{evidence.account_type || 'N/A'}</div>
                              </div>
                              <div className="bg-stone-50 dark:bg-[rgba(10,38,28,0.50)] rounded p-2">
                                <div className="text-stone-500 dark:text-[#93AE9F] text-xs">Status</div>
                                <div className={`font-medium ${isDerogatoryEvidenceValue(evidence.account_status) ? 'text-red-700 dark:text-red-400' : 'text-stone-800 dark:text-[#EAF7F0]'}`}>
                                  {evidence.account_status || 'N/A'}
                                </div>
                              </div>
                              <div className="bg-stone-50 dark:bg-[rgba(10,38,28,0.50)] rounded p-2">
                                <div className="text-stone-500 dark:text-[#93AE9F] text-xs">Payment Status</div>
                                <div className={`font-medium ${isDerogatoryEvidenceValue(resolveEvidencePaymentStatus(evidence)) ? 'text-red-700 dark:text-red-400' : 'text-stone-800 dark:text-[#EAF7F0]'}`}>
                                  {resolveEvidencePaymentStatus(evidence) || 'N/A'}
                                </div>
                              </div>
                              <div className="bg-stone-50 dark:bg-[rgba(10,38,28,0.50)] rounded p-2">
                                <div className="text-stone-500 dark:text-[#93AE9F] text-xs">Balance</div>
                                <div className="font-medium text-stone-800 dark:text-[#EAF7F0]">{typeof evidence.balance === 'number' ? `$${evidence.balance.toLocaleString()}` : (evidence.balance || 'N/A')}</div>
                              </div>
                              <div className="bg-stone-50 dark:bg-[rgba(10,38,28,0.50)] rounded p-2">
                                <div className="text-stone-500 dark:text-[#93AE9F] text-xs">Past Due</div>
                                <div className="font-medium text-red-700 dark:text-red-400">{evidence.past_due || 'N/A'}</div>
                              </div>
                            </div>
                            
                            {/* Bureau Comparison Table */}
                            {bureauComparisonTableVisible(evidence.bureau_comparison) && (
                              <div>
                                <h5 className="text-sm font-semibold text-stone-700 dark:text-[#C7DDD0] mb-2 flex items-center gap-2">
                                  <Scale size={14} />
                                  Bureau Comparison Table
                                </h5>
                                <div className="overflow-x-auto">
                                  <table className="w-full text-sm border-collapse">
                                    <thead>
                                      <tr className="bg-stone-100 dark:bg-[rgba(8,32,24,0.65)]">
                                        <th className="text-left px-3 py-2 border border-stone-200 dark:border-[rgba(142,218,182,0.12)] font-semibold text-stone-700 dark:text-[#C7DDD0]">Field</th>
                                        <th className="text-left px-3 py-2 border border-stone-200 dark:border-[rgba(142,218,182,0.12)] font-semibold text-emerald-700 dark:text-emerald-400">TransUnion</th>
                                        <th className="text-left px-3 py-2 border border-stone-200 dark:border-[rgba(142,218,182,0.12)] font-semibold text-amber-700 dark:text-amber-400">Experian</th>
                                        <th className="text-left px-3 py-2 border border-stone-200 dark:border-[rgba(142,218,182,0.12)] font-semibold text-red-700 dark:text-red-400">Equifax</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {buildOrderedBureauComparisonEntries(evidence.bureau_comparison).map(([field, values]) => {
                                        const isDifferent = evidence.differences_detected?.includes(field.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()))
                                          || evidence.differences_detected?.includes(formatBureauComparisonFieldLabel(field));
                                        const renderCell = (cellValue) => {
                                          const display = cellValue || '-';
                                          const derogatory = isDerogatoryEvidenceValue(display);
                                          return (
                                            <span className={derogatory ? 'text-red-700 dark:text-red-400 font-medium' : ''}>
                                              {display}
                                            </span>
                                          );
                                        };
                                        return (
                                          <tr key={field} className={isDifferent ? 'bg-amber-50 dark:bg-[rgba(245,158,11,0.10)]' : ''}>
                                            <td className="px-3 py-2 border border-stone-200 dark:border-[rgba(142,218,182,0.12)] font-medium text-stone-700 dark:text-[#C7DDD0]">
                                              {formatBureauComparisonFieldLabel(field)}
                                              {isDifferent && <AlertTriangle size={12} className="inline ml-1 text-amber-500" />}
                                            </td>
                                            <td className="px-3 py-2 border border-stone-200 dark:border-[rgba(142,218,182,0.12)] text-stone-600 dark:text-[#C7DDD0]">{renderCell(values?.TransUnion)}</td>
                                            <td className="px-3 py-2 border border-stone-200 dark:border-[rgba(142,218,182,0.12)] text-stone-600 dark:text-[#C7DDD0]">{renderCell(values?.Experian)}</td>
                                            <td className="px-3 py-2 border border-stone-200 dark:border-[rgba(142,218,182,0.12)] text-stone-600 dark:text-[#C7DDD0]">{renderCell(values?.Equifax)}</td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}
                            
                            {/* Differences Detected */}
                            {evidence.differences_detected?.length > 0 && (
                              <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
                                <div className="flex items-center gap-2 mb-2">
                                  <AlertTriangle size={14} className="text-amber-600" />
                                  <span className="text-sm font-semibold text-amber-800">Differences Detected</span>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                  {evidence.differences_detected.map((diff, i) => (
                                    <Badge key={i} variant="outline" className="bg-amber-100 text-amber-700 border-amber-300 text-xs">
                                      {diff}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {/* Late Months Detection */}
                            {evidence.distinct_late_months_count > 0 && (
                              <div className="bg-red-50 rounded-lg p-3 border border-red-200">
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center gap-2">
                                    <Calendar size={14} className="text-red-600" />
                                    <span className="text-sm font-semibold text-red-800">Late Payment History</span>
                                  </div>
                                  <Badge className="bg-red-100 text-red-700">
                                    {evidence.distinct_late_months_count} Unique Late Month{evidence.distinct_late_months_count !== 1 ? 's' : ''}
                                  </Badge>
                                </div>
                                {evidence.detected_late_months?.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-2">
                                    {evidence.detected_late_months.map((month, i) => (
                                      <span key={i} className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs font-mono">
                                        {month}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                            
                            {/* Classification Reason - Shows underlying source classification */}
                            {evidence.classification && (
                              <div className="bg-stone-100 rounded-lg p-3 border border-stone-200">
                                <div className="flex items-center gap-2 mb-1">
                                  <Info size={14} className="text-stone-500" />
                                  <span className="text-xs font-medium text-stone-500 uppercase tracking-wide">Source Classification (for reference only)</span>
                                </div>
                                <div className="flex items-center gap-2 mb-2">
                                  <Badge variant="outline" className={`text-xs border-dashed ${
                                    evidence.classification === 'isolated' ? 'border-emerald-400 text-emerald-600' :
                                    evidence.classification === 'structural' ? 'border-amber-400 text-amber-600' :
                                    evidence.classification === 'recovery' ? 'border-red-400 text-red-600' :
                                    'border-purple-400 text-purple-600'
                                  }`}>
                                    {evidence.classification?.toUpperCase() || 'N/A'}
                                  </Badge>
                                  <span className="text-xs text-stone-400">|</span>
                                  <span className="text-xs text-stone-500">{evidence.classification_reason || 'No reason provided'}</span>
                                </div>
                                <p className="text-xs text-stone-400 italic">
                                  Note: The locked package method ({selectedEvidenceForView?.method?.replace(/_/g, ' ')}) is the active generation method, not this source classification.
                                </p>
                              </div>
                            )}
                          </div>
                        </>
                      )}
                      
                      {/* Inquiry Evidence */}
                      {evidence.type === 'inquiry' && (
                        <>
                          <div className="bg-purple-50 px-4 py-3 border-b border-purple-200">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Search size={16} className="text-purple-600" />
                                <span className="font-semibold text-purple-900">{evidence.normalized_creditor_name}</span>
                              </div>
                              <Badge variant="outline" className="bg-purple-100 text-purple-700 text-xs">
                                {evidence.bureau}
                              </Badge>
                            </div>
                          </div>
                          
                          <div className="p-4 space-y-4">
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                              <div className="bg-stone-50 dark:bg-[rgba(10,38,28,0.50)] rounded p-2">
                                <div className="text-stone-500 dark:text-[#93AE9F] text-xs">Inquiry Date</div>
                                <div className="font-medium text-stone-800 dark:text-[#EAF7F0]">{evidence.inquiry_date || 'N/A'}</div>
                              </div>
                              <div className="bg-stone-50 dark:bg-[rgba(10,38,28,0.50)] rounded p-2">
                                <div className="text-stone-500 dark:text-[#93AE9F] text-xs">Bureau</div>
                                <div className="font-medium text-stone-800 dark:text-[#EAF7F0]">{evidence.bureau || 'N/A'}</div>
                              </div>
                              <div className="bg-stone-50 dark:bg-[rgba(10,38,28,0.50)] rounded p-2">
                                <div className="text-stone-500 dark:text-[#93AE9F] text-xs">Type of Business</div>
                                <div className="font-medium text-stone-800 dark:text-[#EAF7F0]">{evidence.type_of_business || 'N/A'}</div>
                              </div>
                            </div>
                            
                            {evidence.raw_creditor_name && evidence.raw_creditor_name !== evidence.normalized_creditor_name && (
                              <div className="bg-amber-50 dark:bg-[rgba(245,158,11,0.12)] rounded-lg p-3 border border-amber-200 dark:border-[rgba(245,158,11,0.20)]">
                                <div className="text-xs text-amber-700 dark:text-amber-300">Source Report Creditor Name</div>
                                <div className="font-medium text-amber-800 dark:text-amber-200">{evidence.raw_creditor_name}</div>
                                {evidence.normalized_creditor_name && (
                                  <div className="mt-1">
                                    <div className="text-xs text-amber-600 dark:text-amber-400">Normalized Creditor Name</div>
                                    <div className="font-medium text-amber-700 dark:text-amber-300">{evidence.normalized_creditor_name}</div>
                                  </div>
                                )}
                              </div>
                            )}
                            
                            <div className={`rounded-lg p-3 border ${
                              evidence.matching_account_found 
                                ? evidence.match_confidence === 'strong' 
                                  ? 'bg-red-50 dark:bg-[rgba(239,68,68,0.12)] border-red-200 dark:border-[rgba(239,68,68,0.20)]'
                                  : 'bg-amber-50 dark:bg-[rgba(245,158,11,0.12)] border-amber-200 dark:border-[rgba(245,158,11,0.20)]' 
                                : 'bg-emerald-50 dark:bg-[rgba(16,185,129,0.12)] border-emerald-200 dark:border-[rgba(16,185,129,0.20)]'
                            }`}>
                              <div className="flex items-center gap-2">
                                {evidence.matching_account_found ? (
                                  evidence.match_confidence === 'strong' ? (
                                    <>
                                      <Ban size={14} className="text-red-600 dark:text-red-400" />
                                      <span className="text-sm font-semibold text-red-800 dark:text-red-300">Likely Connected to Opened Account</span>
                                    </>
                                  ) : (
                                    <>
                                      <AlertTriangle size={14} className="text-amber-600 dark:text-amber-400" />
                                      <span className="text-sm font-semibold text-amber-800 dark:text-amber-300">Possible Matching Tradeline Found</span>
                                    </>
                                  )
                                ) : (
                                  <>
                                    <CheckCircle size={14} className="text-emerald-600 dark:text-emerald-400" />
                                    <span className="text-sm font-semibold text-emerald-800 dark:text-emerald-300">No Matching Tradeline Found</span>
                                  </>
                                )}
                              </div>
                              {evidence.matched_account_name && (
                                <div className="mt-2 text-sm text-gray-700 dark:text-[#C7DDD0]">
                                  <span className="font-medium">Matched Account:</span> {evidence.matched_account_name}
                                </div>
                              )}
                            </div>
                            
                            <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
                              <div className="flex items-center gap-2 mb-1">
                                <Shield size={14} className="text-purple-600" />
                                <span className="text-sm font-semibold text-purple-800">Routing Reason</span>
                              </div>
                              <p className="text-sm text-purple-700">{evidence.routing_reason}</p>
                            </div>
                          </div>
                        </>
                      )}
                      
                      {/* Personal Info Evidence */}
                      {evidence.type === 'personal_info' && (
                        <>
                          <div className="bg-teal-50 px-4 py-3 border-b border-teal-200">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <User size={16} className="text-teal-600" />
                                <span className="font-semibold text-teal-900">{evidence.field_label}</span>
                              </div>
                              <Badge variant="outline" className="bg-teal-100 text-teal-700 text-xs">
                                {evidence.pi_category?.replace(/_/g, ' ').toUpperCase() || 'PI'}
                              </Badge>
                            </div>
                          </div>
                          
                          <div className="p-4 space-y-4">
                            {/* Bureau-by-Bureau Values */}
                            <div>
                              <h5 className="text-sm font-semibold text-stone-700 mb-2">Bureau Values</h5>
                              <div className="overflow-x-auto">
                                <table className="w-full text-sm border-collapse">
                                  <thead>
                                    <tr className="bg-stone-100">
                                      <th className="text-left px-3 py-2 border border-stone-200 font-semibold text-emerald-700">TransUnion</th>
                                      <th className="text-left px-3 py-2 border border-stone-200 font-semibold text-amber-700">Experian</th>
                                      <th className="text-left px-3 py-2 border border-stone-200 font-semibold text-red-700">Equifax</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr>
                                      <td className="px-3 py-2 border border-stone-200 text-stone-600">
                                        {evidence.bureau_values?.TransUnion || '-'}
                                      </td>
                                      <td className="px-3 py-2 border border-stone-200 text-stone-600">
                                        {evidence.bureau_values?.Experian || '-'}
                                      </td>
                                      <td className="px-3 py-2 border border-stone-200 text-stone-600">
                                        {evidence.bureau_values?.Equifax || '-'}
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </div>
                            
                            {/* Protected Anchor */}
                            {evidence.protection_triggered && (
                              <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-200">
                                <div className="flex items-center gap-2 mb-2">
                                  <Shield size={14} className="text-emerald-600" />
                                  <span className="text-sm font-semibold text-emerald-800">Protected Anchor Value</span>
                                </div>
                                <div className="bg-white rounded p-2 border border-emerald-200">
                                  <span className="font-mono text-sm text-emerald-800">{evidence.protected_anchor_value || 'Not specified'}</span>
                                </div>
                                {evidence.has_supporting_docs && (
                                  <div className="flex items-center gap-1 mt-2 text-xs text-emerald-700">
                                    <CheckCircle size={12} />
                                    <span>Supporting documents confirmed</span>
                                  </div>
                                )}
                              </div>
                            )}
                            
                            {/* Disputed Values */}
                            {evidence.disputed_values?.length > 0 && (
                              <div className="bg-red-50 rounded-lg p-3 border border-red-200">
                                <div className="flex items-center gap-2 mb-2">
                                  <AlertTriangle size={14} className="text-red-600" />
                                  <span className="text-sm font-semibold text-red-800">Disputed Values</span>
                                </div>
                                <div className="space-y-1">
                                  {evidence.disputed_values.map((dv, i) => (
                                    <div key={i} className="flex items-center gap-2 text-sm">
                                      <Badge variant="outline" className="bg-red-100 text-red-700 text-xs">
                                        {dv.bureau}
                                      </Badge>
                                      <span className="text-red-800 font-mono">{dv.value}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {/* No conflicts message for DOB with year-only or exact matches */}
                            {evidence.pi_category === 'dob' && evidence.disputed_values?.length === 0 && evidence.protected_anchor_value && (
                              <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-200">
                                <div className="flex items-center gap-2 mb-2">
                                  <CheckCircle size={14} className="text-emerald-600" />
                                  <span className="text-sm font-semibold text-emerald-800">All Values Match</span>
                                </div>
                                <p className="text-sm text-emerald-700">
                                  No conflicting DOB values detected. All bureau values match or are compatible with the protected anchor.
                                </p>
                                {evidence.match_status && (
                                  <div className="mt-2 space-y-1">
                                    {Object.entries(evidence.match_status).map(([bureau, status]) => (
                                      <div key={bureau} className="flex items-center gap-2 text-xs">
                                        <span className="text-emerald-700">{bureau}:</span>
                                        <Badge 
                                          variant="outline" 
                                          className={status === 'match' ? 'bg-emerald-100 text-emerald-700' : 
                                                    status === 'year_only_match' ? 'bg-amber-100 text-amber-700' : 
                                                    'bg-gray-100 text-gray-700'}
                                        >
                                          {status === 'match' ? 'Exact Match' : 
                                           status === 'year_only_match' ? 'Year Match' : 
                                           status === 'missing' ? 'No Data' : status}
                                        </Badge>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                            
                            {/* Protection Reason */}
                            <div className="bg-teal-50 rounded-lg p-3 border border-teal-200">
                              <div className="flex items-center gap-2 mb-1">
                                <Info size={14} className="text-teal-600" />
                                <span className="text-sm font-semibold text-teal-800">Protection Reason</span>
                              </div>
                              <p className="text-sm text-teal-700">{evidence.protection_reason}</p>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-stone-500">
                  <Database className="h-10 w-10 mx-auto mb-3 text-stone-400" />
                  <p className="font-medium">No Evidence Data Available</p>
                  <p className="text-sm mt-1">Evidence views will be populated when items are processed.</p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="mt-4 border-t pt-4">
            <Button variant="outline" onClick={() => setViewEvidenceModalOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* ============================================ */}
      {/* EMPLOYER WARNING MODAL                       */}
      {/* ============================================ */}
      <Dialog open={showEmployerWarning} onOpenChange={setShowEmployerWarning}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <AlertTriangle size={20} />
              {EMPLOYER_WARNING_TITLE}
            </DialogTitle>
            <DialogDescription className="text-stone-600 mt-2">
              {EMPLOYER_WARNING_BODY}
            </DialogDescription>
          </DialogHeader>
          
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start gap-2">
              <Info size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-700">
                {employerItemPendingRemoval && (
                  <span className="font-medium">
                    Employer info on your report: {
                      (() => {
                        const vals = employerItemPendingRemoval.values || {};
                        const allVals = [vals.TransUnion, vals.Experian, vals.Equifax]
                          .filter(v => v && v !== '—')
                          .join(', ');
                        return allVals || 'Present on file';
                      })()
                    }
                  </span>
                )}
              </p>
            </div>
          </div>
          
          <DialogFooter className="mt-6 flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => {
                // User confirms keeping employer - deselect it
                if (employerItemPendingRemoval) {
                  setSelectedItems(prev => ({
                    ...prev,
                    personalInfo: prev.personalInfo.filter(id => id !== employerItemPendingRemoval.id)
                  }));
                  // Track as removed for Step 3
                  setEmployerRemovedItems(prev => [...prev, employerItemPendingRemoval]);
                }
                setShowEmployerWarning(false);
                setEmployerItemPendingRemoval(null);
              }}
              className="flex-1"
              data-testid="keep-employer-btn"
            >
              Keep Employer Info
            </Button>
            <Button 
              onClick={() => {
                // User chooses to dispute - keep it selected (do nothing to selection)
                setShowEmployerWarning(false);
                setEmployerItemPendingRemoval(null);
                toast.success('Employer information will be included in dispute', {
                  description: 'Employer data will be disputed as part of your personal information cleanup.',
                });
              }}
              className="flex-1 bg-emerald-600 hover:bg-emerald-700"
              data-testid="dispute-employer-btn"
            >
              Dispute Employer Info
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ItemSelectionPage;
